import React, { useState, useEffect, useContext, useRef } from "react";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { useNavigate, useParams } from "react-router-dom";
import { serverHost } from "variables/serverdata";
import {
  Button, Card, CardHeader, CardBody, FormGroup, Form,
  Input, Container, Row, Col, Alert, Table, Spinner, Modal,
  ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import SimpleHeader from "components/Headers/SimpleHeader.js";
import {
  getUser, getSite, validateBackgroundFile,
  getDeviceCallLogs, subscribeToDeviceEvents, pullDeviceEvents,
  getAkuvoxDisplaySettings, updateAkuvoxDisplaySettings,
} from "./deviceService";
import HusariaSelect from "components/HusariaSelect";
import akuvoxThemeLight from "assets/img/theme/akuvox/light.png";
import akuvoxThemeDark from "assets/img/theme/akuvox/dark.png";
import akuvoxThemeNewYear from "assets/img/theme/akuvox/new-year.png";
import akuvoxThemeValentines from "assets/img/theme//akuvox/valentines-day.png";
import akuvoxThemeEarthDay from "assets/img/theme/akuvox/earth-day.png";
import akuvoxThemeWorkersDay from "assets/img/theme/akuvox/workers-day.png";
import akuvoxThemeHalloween from "assets/img/theme/akuvox/halloween.png";
import akuvoxThemeChristmas from "assets/img/theme/akuvox/christmas.png";
import akuvoxX916ThemeLight from "assets/img/theme/akuvox/X916_homepage_light.png";
import akuvoxX916ThemeDark from "assets/img/theme/akuvox/X916_homepage_dark.png";
import akuvoxX916ThemeNewYear from "assets/img/theme/akuvox/X916_homepage_new_year.png";
import akuvoxX916ThemeValentines from "assets/img/theme/akuvox/X916_homepage_valentines_day.png";
import akuvoxX916ThemeEarthDay from "assets/img/theme/akuvox/X916_homepage_earth_day.png";
import akuvoxX916ThemeWorkersDay from "assets/img/theme/akuvox/X916_homepage_workers_day.png";
import akuvoxX916ThemeHalloween from "assets/img/theme/akuvox/X916_homepage_halloween.png";
import akuvoxX916ThemeChristmas from "assets/img/theme/akuvox/X916_homepage_christmas.png";

const DIRECTORY_IMAGE_COLOR_OPTIONS = [
  { label: "Red", hex: "#ef4444" },
  { label: "Orange", hex: "#f97316" },
  { label: "Yellow", hex: "#facc15" },
  { label: "Green", hex: "#22c55e" },
  { label: "Blue", hex: "#3b82f6" },
  { label: "Indigo", hex: "#6366f1" },
  { label: "Violet", hex: "#a855f7" },
  { label: "White", hex: "#ffffff" },
  { label: "Gray", hex: "#6b7280" },
  { label: "Black", hex: "#000000" },
];

const DIRECTORY_IMAGE_SIZE = 432;
const AKUVOX_S539_MODEL = "Akuvox S539";
const AKUVOX_X916_MODEL = "Akuvox X916";
const AKUVOX_API_MODELS = new Set([
  AKUVOX_S539_MODEL,
  AKUVOX_X916_MODEL,
]);
const CURRENT_2N_DEVICE_MODELS = new Set([
  "2N IP Style",
  "2N IP Verso",
  "2N IP Solo",
]);

const ACTIVITY_DEVICE_MODELS = new Set([
  ...CURRENT_2N_DEVICE_MODELS,
  ...AKUVOX_API_MODELS,
]);

const AKUVOX_THEME_OPTIONS = [
  // Akuvox S539 /api/config/get and /api/config/set both use 1-based values.
  { value: "1", label: "Villa" },
  { value: "2", label: "Building" },
  { value: "3", label: "Multi-factor Authentication" },
  { value: "4", label: "Alphanumeric" },
];

const AKUVOX_X916_THEME_OPTIONS = [
  { value: "2", label: "Intercom" },
  { value: "1", label: "VMS" },
  { value: "5", label: "Directory" },
];

const AKUVOX_X916_DISPLAY_TYPE_OPTIONS = [
  { value: "0", label: "Homepage" },
  { value: "1", label: "Delivery" },
  { value: "2", label: "Temp Key" },
  { value: "3", label: "PIN" },
  { value: "4", label: "Dial" },
  { value: "5", label: "Tenant" },
  { value: "8", label: "Visitor" },
];

// Values follow the order shown by the X916 web interface; the first five
// are also reflected by Key1Type-Key5Type in the supplied config response.
const AKUVOX_X916_KEY_TYPE_OPTIONS = [
  { value: "0", label: "Delivery" },
  { value: "1", label: "Temp Key" },
  { value: "2", label: "PIN" },
  { value: "3", label: "Dial" },
  { value: "4", label: "Tenant" },
  { value: "5", label: "Speed Dial" },
  { value: "7", label: "Visitor" },
  { value: "8", label: "Relay" },
  { value: "6", label: "Null" },
];

const AKUVOX_X916_LANGUAGE_OPTIONS = [
  { value: "-1", label: "NULL" },
  { value: "0", label: "English" },
  { value: "1", label: "Español" },
  { value: "2", label: "Français" },
  { value: "3", label: "简体中文" },
];

const AKUVOX_X916_HOMEPAGE_TEXT_FIELDS = [
  { field: "addressLine", apiField: "address_line", label: "Address Line" },
  { field: "welcomeMessage", apiField: "welcome_message", label: "Welcome Message" },
  { field: "visitorCallButtonLabel", apiField: "visitor_call_button_label", label: "Visitor Call Button Label" },
  { field: "tempPinEnterButtonLabel", apiField: "temp_pin_enter_button_label", label: "Temp PIN Enter Button Label" },
];

const DEFAULT_AKUVOX_X916_DISPLAY_SETTINGS = {
  theme: "2",
  displayType: "0",
  numberOfKeys: "",
  fullScreenDirectory: null,
  keys: Array.from({ length: 6 }, (_, index) => ({
    index: index + 1,
    type: "8",
    label: "",
    value: "",
  })),
  keypadMode: null,
  speedDial: { cloudNumber: null, groupEnabled: null, groupNumber: null },
  groups: [],
  groupsError: "",
  reception: { dialType: null, openRelay: null, httpEnabled: null, httpUrl: null },
  languageSelectorVisible: null,
  languages: [null, null, null, null],
  appearance: { mode: null, residentTheme: null, autoActivation: null },
  addressLine: null,
  welcomeMessage: null,
  visitorCallButtonLabel: null,
  tempPinEnterButtonLabel: null,
};

const AKUVOX_BUILDING_KEY_TYPE_OPTIONS = [
  { value: "0", label: "PIN" },
  { value: "1", label: "Call" },
  { value: "2", label: "Directory" },
  { value: "3", label: "Speed Dial" },
  { value: "4", label: "Null" },
  { value: "5", label: "Relay" },
];

const AKUVOX_VILLA_KEY_TYPE_OPTIONS = [
  { value: "2", label: "Directory" },
  { value: "0", label: "PIN" },
  { value: "1", label: "Call" },
];

const AKUVOX_BUILDING_DEFAULT_PAGE_OPTIONS = [
  { value: "0", label: "Home Page" },
  { value: "1", label: "PIN" },
  { value: "2", label: "Call" },
  { value: "3", label: "Directory" },
];

const AKUVOX_VILLA_DEFAULT_PAGE_OPTIONS = [
  { value: "5", label: "Home Page" },
  { value: "1", label: "Directory" },
  { value: "3", label: "PIN" },
  { value: "0", label: "Call" },
];

const AKUVOX_VILLA_DEFAULT_PAGE_TO_KEY_TYPE = {
  "0": "1", // Call
  "1": "2", // Directory
  "3": "0", // PIN
};

const AKUVOX_RESIDENT_THEME_OPTIONS = [
  {
    value: "0",
    label: "Light",
    previewSrc: akuvoxThemeLight,
  },
  {
    value: "1",
    label: "Dark",
    previewSrc: akuvoxThemeDark,
  },
  {
    value: "2",
    label: "New Year",
    previewSrc: akuvoxThemeNewYear,
  },
  {
    value: "3",
    label: "Valentine's Day",
    previewSrc: akuvoxThemeValentines,
  },
  {
    value: "4",
    label: "Earth Day",
    previewSrc: akuvoxThemeEarthDay,
  },
  {
    value: "5",
    label: "Worker's Day",
    previewSrc: akuvoxThemeWorkersDay,
  },
  {
    value: "6",
    label: "Halloween",
    previewSrc: akuvoxThemeHalloween,
  },
  {
    value: "7",
    label: "Christmas",
    previewSrc: akuvoxThemeChristmas,
  },
];

const AKUVOX_X916_RESIDENT_THEME_OPTIONS = AKUVOX_RESIDENT_THEME_OPTIONS.map((option, index) => ({
  ...option,
  previewSrc: [
    akuvoxX916ThemeLight, akuvoxX916ThemeDark, akuvoxX916ThemeNewYear,
    akuvoxX916ThemeValentines, akuvoxX916ThemeEarthDay, akuvoxX916ThemeWorkersDay,
    akuvoxX916ThemeHalloween, akuvoxX916ThemeChristmas,
  ][index],
}));

const AKUVOX_AUTO_ACTIVATION_OPTIONS = [
  { value: "all", label: "All festivals" },
  { value: "2", label: "New Year (Dec 31 - Jan 1)" },
  { value: "3", label: "Valentine's Day (Feb 14)" },
  { value: "4", label: "Earth Day (Apr 22)" },
  { value: "5", label: "Worker's Day (May 1)" },
  { value: "6", label: "Halloween (Oct 31 - Nov 1)" },
  { value: "7", label: "Christmas (Dec 24 - Dec 27)" },
];

const AKUVOX_AUTO_ACTIVATION_THEME_VALUES =
  AKUVOX_AUTO_ACTIVATION_OPTIONS
    .filter((option) => option.value !== "all")
    .map((option) => option.value);

const AKUVOX_SPEED_DIAL_ACCOUNT_OPTIONS = [
  { value: "0", label: "Auto" },
  { value: "1", label: "Account 1" },
  { value: "2", label: "Account 2" },
];

const AKUVOX_SPEED_DIAL_RELAY_OPTIONS = [
  { value: "0", label: "None" },
  { value: "1", label: "Relay A" },
  { value: "2", label: "Relay B" },
  { value: "3", label: "Relay C" },
];

const AKUVOX_LANGUAGE_OPTIONS = [
  { value: "0", label: "English" },
  { value: "2", label: "Español" },
  { value: "4", label: "Français" },
  { value: "1", label: "简体中文" },
];

const toAkuvoxBool = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") {
    return fallback;
  }

  if (typeof value === "string") {
    return ["1", "true", "yes", "on"].includes(
      value.trim().toLowerCase()
    );
  }

  return Boolean(value);
};

const DEFAULT_AKUVOX_DISPLAY_SETTINGS = {
  // Never present a guessed theme while waiting for the live device read.
  theme: "",
  keypadMode: "0",

  prompts: {
    call: "Please enter the apartment number (e.g.101)",
    pin: "Please enter your PIN",
    directory: "Tap here to search",
  },

  building: {
    defaultPage: "0",
    keys: [
      { index: 1, label: "", type: "0", value: "" },
      { index: 2, label: "", type: "1", value: "" },
      { index: 3, label: "", type: "2", value: "" },
      { index: 4, label: "", type: "3", value: "" },
    ],
  },

  villa: {
    defaultPage: "5",
    keys: [
      { index: 1, label: "", type: "2", visible: true },
      { index: 2, label: "", type: "0", visible: true },
      { index: 3, label: "", type: "1", visible: true },
    ],
  },

  speedDial: {
    groupEnabled: false,
    groupNumber: "",
    dialOutForward: false,
    account: "0",
    openRelay: "0",
    httpEnabled: false,
    httpUrl: "",
  },

  appearance: {
    mode: "0",
    residentTheme: "0",
    autoActivation: [],
  },

  languageSelectorVisible: false,
  deviceLanguage: "0",
  languages: ["0", "2", "4", "1"],

  time: {
    autoTime: true,
    localTime: "",
    timezoneId: "",
    timezoneLabel: "",
    timezoneOptions: [],
    ntpServer1: "pool.ntp.org",
    dateFormat: "0",
    timeFormat: "0",
  },
};

const toDateTimeLocalValue = (value) => {
  const raw = String(value || "").trim();

  if (!raw) return "";

  const isoMatch = raw.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/
  );

  if (isoMatch) {
    const [, year, month, day, hour, minute] = isoMatch;
    return `${year}-${month}-${day}T${hour}:${minute}`;
  }

  // Akuvox commonly returns: YYYYMMDD HH:mm:ss
  const compactMatch = raw.match(
    /^(\d{4})(\d{2})(\d{2})[ T](\d{2}):(\d{2})/
  );

  if (compactMatch) {
    const [, year, month, day, hour, minute] = compactMatch;
    return `${year}-${month}-${day}T${hour}:${minute}`;
  }

  // Format accepted by /api/time/set: YYYY,M,D,H,M
  const commaMatch = raw.match(
    /^(\d{4}),(\d{1,2}),(\d{1,2}),(\d{1,2}),(\d{1,2})/
  );

  if (commaMatch) {
    const [, year, month, day, hour, minute] = commaMatch;
    const pad = (part) => String(part).padStart(2, "0");

    return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`;
  }

  return "";
};

const mergeAkuvoxLocalDateTime = (
  currentValue,
  nextParts = {}
) => {
  const normalized = toDateTimeLocalValue(currentValue);
  const currentDate = normalized.slice(0, 10);
  const currentTime = normalized.slice(11, 16);

  const date = nextParts.date ?? currentDate;
  const time = nextParts.time ?? currentTime;

  if (!date) return "";

  return `${date}T${time || "00:00"}`;
};

const AKUVOX_TIMEZONE_OPTIONS = [
  {
    value: "Vancouver",
    label: "Pacific Time (PST/PDT)",
  },
  {
    value: "New_York",
    label: "Eastern Time (EST/EDT)",
  },
];

const normalizeAkuvoxTimezoneId = (
  rawValue,
  rawLabel = ""
) => {
  const value = String(rawValue || "").trim();
  const label = String(rawLabel || "").trim();
  const searchable = `${value} ${label}`.toLowerCase();

  // Always use Vancouver as the canonical Pacific value.
  if (
    /vancouver|los[_\s/-]?angeles|tijuana|pacific|pst8pdt|\bpst\b|\bpdt\b/.test(
      searchable
    )
  ) {
    return "Vancouver";
  }

  // Always use New_York as the canonical Eastern value.
  if (
    /new[_\s/-]?york|toronto|detroit|montreal|eastern|est5edt|\best\b|\bedt\b/.test(
      searchable
    )
  ) {
    return "New_York";
  }

  /*
   * Some firmware returns a numeric timezone ID such as -1.
   * Use the accompanying GMT offset only for those opaque values.
   */
  if (!value || /^-?\d+$/.test(value)) {
    const offsetMatch = label.match(
      /gmt\s*([+-])\s*0?(\d{1,2})(?::?\d{2})?/i
    );

    if (offsetMatch && offsetMatch[1] === "-") {
      const offsetHours = Number(offsetMatch[2]);

      // PST = GMT-8, PDT = GMT-7
      if (offsetHours === 7 || offsetHours === 8) {
        return "Vancouver";
      }

      // EST = GMT-5, EDT = GMT-4
      if (offsetHours === 4 || offsetHours === 5) {
        return "New_York";
      }
    }
  }

  return "";
};

const isValidHexColor = (hexColor) => {
  return /^#[0-9a-fA-F]{6}$/.test(String(hexColor || "").trim());
};

const createSolidColorDirectoryImageDataUrl = (hexColor) => {
  return new Promise((resolve, reject) => {
    if (!isValidHexColor(hexColor)) {
      reject(new Error("Invalid directory image color"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = DIRECTORY_IMAGE_SIZE;
    canvas.height = DIRECTORY_IMAGE_SIZE;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not create directory color image"));
      return;
    }

    ctx.fillStyle = hexColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    resolve(canvas.toDataURL("image/png"));
  });
};

function DeviceForm() {
  const navigate = useNavigate();
  const { device_uuid } = useParams();
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const editing = Boolean(device_uuid);
  const [directories, setDirectories] = useState([]);
  const [directoryTenants, setDirectoryTenants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState(null);
  const [savedBackgroundPreview, setSavedBackgroundPreview] = useState(null);
  const [backgroundUploading, setBackgroundUploading] = useState(false);
  const [backgroundStatus, setBackgroundStatus] = useState({
    show: false,
    success: false,
    message: "",
  });
  const [backgroundMeta, setBackgroundMeta] = useState({
    width: null,
    height: null,
    validDimensions: false,
  });
  const [backgroundRemoving, setBackgroundRemoving] = useState(false);
  const [extensionOptions, setExtensionOptions] = useState([]);
  const [quickSetupBusy, setQuickSetupBusy] = useState(false);
  const [quickSetupStatus, setQuickSetupStatus] = useState({
    show: false,
    success: false,
    message: "",
  });
  const [rfidBacklightEnabled, setRfidBacklightEnabled] = useState(false);
  const [rfidBacklightLoading, setRfidBacklightLoading] = useState(false);
  const [initialRfidBacklightEnabled, setInitialRfidBacklightEnabled] = useState(false);
  const [showcaseMode, setShowcaseMode] = useState("OFF");
  const [showcaseModeLoading, setShowcaseModeLoading] = useState(false);
  const [initialShowcaseMode, setInitialShowcaseMode] = useState("OFF");
  const [rootFolderDisplay, setRootFolderDisplay] = useState("CARDS_1_COLUMN");
  const [rootFolderDisplayLoading, setRootFolderDisplayLoading] = useState(false);
  const [initialRootFolderDisplay, setInitialRootFolderDisplay] = useState("CARDS_1_COLUMN");

  const defaultStyleDisplaySettings = {
    codeEntryButton: true,
    entryKeypadMode: "NORMAL",
    touchSounds: true,
    flexiBuilderEnabled: false,
  };

  const [styleDisplaySettings, setStyleDisplaySettings] = useState(defaultStyleDisplaySettings);
  const [initialStyleDisplaySettings, setInitialStyleDisplaySettings] = useState(defaultStyleDisplaySettings);
  const [styleDisplaySettingsLoading, setStyleDisplaySettingsLoading] = useState(false);

  const defaultVersoDisplaySettings = {
    phonebookDisplayed: true,
    codeEntryButton: true,
    entryKeypadMode: "NORMAL",
    preferIconsToText: false,
  };

  const [versoDisplaySettings, setVersoDisplaySettings] = useState(defaultVersoDisplaySettings);
  const [initialVersoDisplaySettings, setInitialVersoDisplaySettings] = useState(defaultVersoDisplaySettings);
  const [versoDisplaySettingsLoading, setVersoDisplaySettingsLoading] = useState(false);

  const [akuvoxDisplaySettings, setAkuvoxDisplaySettings] = useState(
    DEFAULT_AKUVOX_DISPLAY_SETTINGS
  );
  const [akuvoxDisplaySettingsLoading, setAkuvoxDisplaySettingsLoading] = useState(false);
  const akuvoxDisplayDirtySectionsRef = useRef(new Set());
  const akuvoxDisplayLoadRequestRef = useRef(0);
  const [akuvoxX916DisplaySettings, setAkuvoxX916DisplaySettings] = useState(
    DEFAULT_AKUVOX_X916_DISPLAY_SETTINGS
  );
  const akuvoxX916LoadedSettings = useRef(null);
  const [akuvoxX916DisplayLoading, setAkuvoxX916DisplayLoading] = useState(false);
  const [akuvoxX916DisplayDirty, setAkuvoxX916DisplayDirty] = useState(false);
  const [akuvoxX916DisplayLoaded, setAkuvoxX916DisplayLoaded] = useState(false);
  const [akuvoxX916DisplayLoadError, setAkuvoxX916DisplayLoadError] = useState("");
  const [akuvoxX916Images, setAkuvoxX916Images] = useState({ background: null, logo: null });
  const [akuvoxX916ImageBusy, setAkuvoxX916ImageBusy] = useState("");
  const [akuvoxX916ImageStatus, setAkuvoxX916ImageStatus] = useState(null);
  const akuvoxX916ImageInputs = useRef({});
  const akuvoxX916ImageReads = useRef({ background: 0, logo: 0 });
  const akuvoxX916ImageRequestActive = useRef(false);
  const akuvoxX916ImageGeneration = useRef(0);


  const [directoryDisplaySettings, setDirectoryDisplaySettings] = useState({});
  const [initialDirectoryDisplaySettings, setInitialDirectoryDisplaySettings] = useState({});
  const isBusy = saving || backgroundUploading || backgroundRemoving || quickSetupBusy || !!akuvoxX916ImageBusy;

  const [callLogs, setCallLogs] = useState([]);
  const [callLogsLoading, setCallLogsLoading] = useState(false);
  const [callLogsError, setCallLogsError] = useState("");

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState("");

  const [callLogsOpen, setCallLogsOpen] = useState(false);
  const [liveEventsOpen, setLiveEventsOpen] = useState(false);
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState(false);
  const [x916DisplaySettingsOpen, setX916DisplaySettingsOpen] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);

  const [eventFilter, setEventFilter] = useState("ALL");
  const [callLogsRange, setCallLogsRange] = useState("7D");
  const [eventsRange, setEventsRange] = useState("1D");
  const [eventsRequestId, setEventsRequestId] = useState(0);

  const MAX_DIRECTORY_IMAGE_BYTES = 93388; // 98304 is 2n max size, but using lower value as safer threshold

  const MAX_QUICK_DIAL_BUTTON_COUNT = 100;
  const MAX_TENANTS_PER_DEVICE_BUTTON = 16;

  const TENANT_PICKER_PAGE_SIZE = 5;

  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [tenantPickerButtonNumber, setTenantPickerButtonNumber] = useState(null);
  const [tenantPickerSearch, setTenantPickerSearch] = useState("");
  const [tenantPickerSelected, setTenantPickerSelected] = useState([]);
  const [tenantPickerPage, setTenantPickerPage] = useState(1);

  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [imageEditorDirUuid, setImageEditorDirUuid] = useState(null);
  const [imageEditorSrc, setImageEditorSrc] = useState(null);
  const [imageEditorFileName, setImageEditorFileName] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1.0);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageEditorError, setImageEditorError] = useState("");
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, panX: 0, panY: 0 });
  const imageEditorRef = useRef(null);
  const [imageEditorResultTooLarge, setImageEditorResultTooLarge] = useState(false);
  const [imageEditorResultBytes, setImageEditorResultBytes] = useState(0);

  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? "#2a2a2a" : "#fff",
      color: isDark ? "#f8f9fa" : "#111",
      borderColor: isDark ? "#3a3a3a" : base.borderColor,
      boxShadow: "none",
      minHeight: "38px",
      ":hover": {
        borderColor: isDark ? "#3a3a3a" : base.borderColor,
      },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "2px 8px",
    }),

    singleValue: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
    }),

    input: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
    }),

    placeholder: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#6c757d",
      opacity: isDark ? 0.7 : 1,
    }),

    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#2a2a2a" : "#fff",
      border: isDark ? "1px solid #3a3a3a" : base.border,
      zIndex: 9999,
    }),

    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? (isDark ? "#3a3a3a" : "#e9ecef")
        : state.isFocused
          ? (isDark ? "#333333" : "#f6f9fc")
          : "transparent",
      color: isDark ? "#f8f9fa" : "#111",
      ":active": {
        backgroundColor: isDark ? "#3a3a3a" : "#e9ecef",
      },
    }),

    multiValue: (base) => ({
      ...base,
      backgroundColor: isDark ? "#3a3a3a" : "#e9ecef",
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: isDark ? "#f8f9fa" : "#111",
      ":hover": {
        backgroundColor: isDark ? "#444444" : "#dee2e6",
        color: isDark ? "#f8f9fa" : "#111",
      },
    }),
  };

  const [deviceData, setDeviceData] = useState({
    device_name: "",
    device_model: "",
    device_ip: "",
    device_mac: "",
    device_firmware: "2.46",
    device_button: [],
    quick_dial_button_count: 1,
    device_button_assignments: {},
    extension_uuid: "",
    site_uuid: "",
    last_checkin: null,
    dir_uuids: [],
  });

  useEffect(() => {
    ++akuvoxX916ImageGeneration.current;
    ++akuvoxX916ImageReads.current.background;
    ++akuvoxX916ImageReads.current.logo;
    setAkuvoxX916Images({ background: null, logo: null });
    setAkuvoxX916ImageStatus(null);
    for (const input of Object.values(akuvoxX916ImageInputs.current)) {
      if (input) input.value = "";
    }
  }, [device_uuid, deviceData.device_model]);

  const [loading, setLoading] = useState(editing);
  const [saveStatus, setSaveStatus] = useState({ show: false, success: false, message: "" });

  const labelStyle = {
    color: isDark ? "#ffffff" : "#000000",
  };

  const sectionHeadingStyle = {
    color: theme === "dark" ? "#ffffff" : "#172b4d",
    fontWeight: "700",
    fontSize: "1.00rem",
    textTransform: "uppercase",
    marginBottom: "1rem",
  };

  const collapsibleHeaderStyle = {
    backgroundColor: isDark ? "#1f2937" : "#ffffff",
    borderBottom: "none",
    paddingTop: "1rem",
    paddingBottom: "1rem",
    cursor: "pointer",
  };

  const akuvoxSettingsCardStyle = {
    backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
    border: isDark
      ? "1px solid #374151"
      : "1px solid #e9ecef",
    boxShadow: "none",
  };

  const tenantPickerSecondaryButtonStyle = {
    backgroundColor: isDark ? "#374151" : undefined,
    borderColor: isDark ? "#4b5563" : undefined,
    color: isDark ? "#f9fafb" : undefined,
  };

  const tenantPickerSecondaryButtonDisabledStyle = {
    ...tenantPickerSecondaryButtonStyle,
    opacity: isDark ? 0.55 : undefined,
    cursor: "not-allowed",
  };

  const removeTenantButtonStyle = {
    backgroundColor: isDark ? "#7f1d1d" : undefined,
    borderColor: isDark ? "#991b1b" : undefined,
    color: isDark ? "#ffffff" : undefined,
    fontWeight: 600,
    padding: "0.25rem 0.6rem",
    lineHeight: 1.2,
  };

  const removeTenantButtonDisabledStyle = {
    ...removeTenantButtonStyle,
    opacity: isDark ? 0.55 : undefined,
    cursor: "not-allowed",
  };

  const renderCollapseIcon = (open) => (
    <div
      style={{
        color: isDark ? "#d1d5db" : "#495057",
        fontSize: "1.2rem",
        fontWeight: 700,
        userSelect: "none",
      }}
    >
      {open ? "−" : "+"}
    </div>
  );

  const renderAkuvoxToggle = ({
    id,
    label,
    description,
    checked,
    onChange,
    disabled = false,
    enabledText = "Enabled",
    disabledText = "Disabled",
  }) => {
    const isDisabled = Boolean(disabled);
    const statusText = checked ? enabledText : disabledText;

    return (
      <FormGroup className="mb-3">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "1rem",
            minHeight: "68px",
            padding: "0.15rem 0",
            opacity: isDisabled ? 0.6 : 1,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <label
              className="form-control-label"
              htmlFor={id}
              style={{
                ...labelStyle,
                display: "block",
                marginBottom: description ? "0.3rem" : 0,
                cursor: isDisabled ? "not-allowed" : "pointer",
              }}
            >
              {label}
            </label>

            {description && (
              <small
                style={{
                  display: "block",
                  color: isDark ? "#9ca3af" : "#6c757d",
                  lineHeight: 1.35,
                }}
              >
                {description}
              </small>
            )}
          </div>

          <label
            htmlFor={id}
            title={`${label}: ${statusText}`}
            style={{
              position: "relative",
              display: "inline-block",
              width: "44px",
              height: "24px",
              margin: 0,
              flex: "0 0 auto",
              cursor: isDisabled ? "not-allowed" : "pointer",
            }}
          >
            <input
              id={id}
              type="checkbox"
              checked={Boolean(checked)}
              onChange={(event) => onChange(event.target.checked)}
              disabled={isDisabled}
              aria-label={`${label}: ${statusText}`}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                margin: 0,
                opacity: 0,
                cursor: isDisabled ? "not-allowed" : "pointer",
                zIndex: 2,
              }}
            />

            <span
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "999px",
                backgroundColor: checked
                  ? "#5e72e4"
                  : isDark
                    ? "#4b5563"
                    : "#cbd5e1",
                border: isDark
                  ? "1px solid #6b7280"
                  : "1px solid #b8c2cc",
                transition: "background-color 160ms ease",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: "2px",
                  left: checked ? "21px" : "2px",
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.25)",
                  transition: "left 160ms ease",
                }}
              />
            </span>
          </label>
        </div>
      </FormGroup>
    );
  };

  const emptyQuickSetupStatus = {
    show: false,
    success: false,
    message: "",
  };

  const isRootFolderUnsupportedMessage = (message) =>
    String(message || "").includes("Root folder display is only supported for 2N IP Style devices");

  const siteUuid = getSite()?.site_uuid;

  const latestEventsRequestRef = useRef(0);
  const latestCallLogsRequestRef = useRef(0);

  const supportsDeviceButton = ["2N IP Verso", "2N IP Solo"].includes(deviceData.device_model);
  const supportsMultipleQuickDialButtons = deviceData.device_model === "2N IP Verso";
  const isAkuvoxS539 = deviceData.device_model === AKUVOX_S539_MODEL;
  const isAkuvoxX916 = deviceData.device_model === AKUVOX_X916_MODEL;
  const supportsCurrentDeviceApi = ACTIVITY_DEVICE_MODELS.has(deviceData.device_model);

  const normalizeDeviceButtonValue = (value) => {
    if (Array.isArray(value)) {
      return value.flatMap(normalizeDeviceButtonValue);
    }

    if (value === null || value === undefined || value === "") {
      return [];
    }

    const text = String(value).trim();

    if (!text) {
      return [];
    }

    // Handles Postgres array text coming back from the API: "{uuid1,uuid2}"
    if (text.startsWith("{") && text.endsWith("}")) {
      const inner = text.slice(1, -1).trim();

      if (!inner) {
        return [];
      }

      return inner
        .split(",")
        .map(v => v.trim().replace(/^"|"$/g, ""))
        .filter(Boolean);
    }

    return [text];
  };

  const clampQuickDialButtonCount = (value) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed)) {
      return 1;
    }

    return Math.min(MAX_QUICK_DIAL_BUTTON_COUNT, Math.max(1, parsed));
  };

  const getEffectiveQuickDialButtonCount = (
    model = deviceData.device_model,
    rawCount = deviceData.quick_dial_button_count
  ) => {
    return model === "2N IP Verso"
      ? clampQuickDialButtonCount(rawCount)
      : 1;
  };

  const normalizeDeviceButtonAssignments = (assignments, fallbackButtonOne = []) => {
    const normalized = {};

    if (assignments && typeof assignments === "object" && !Array.isArray(assignments)) {
      Object.entries(assignments).forEach(([buttonPosition, tenantUuids]) => {
        const buttonNumber = Number.parseInt(buttonPosition, 10);

        if (!buttonNumber || buttonNumber < 1) {
          return;
        }

        const tenantIds = normalizeDeviceButtonValue(tenantUuids);

        if (tenantIds.length > 0) {
          normalized[String(buttonNumber)] = tenantIds;
        }
      });

      return normalized;
    }

    const fallbackTenantIds = normalizeDeviceButtonValue(fallbackButtonOne);

    if (fallbackTenantIds.length > 0) {
      normalized["1"] = fallbackTenantIds;
    }

    return normalized;
  };

  const buildButtonAssignmentState = (count, existing = {}) => {
    const safeCount = clampQuickDialButtonCount(count);
    const next = {};

    for (let buttonNumber = 1; buttonNumber <= safeCount; buttonNumber += 1) {
      next[String(buttonNumber)] =
        normalizeDeviceButtonValue(existing[String(buttonNumber)] || existing[buttonNumber] || []);
    }

    return next;
  };

  const getTenantOptionByUuid = (tenantUuid) => {
    const tenant = directoryTenants.find(
      (t) => String(t.tenant_uuid) === String(tenantUuid)
    );

    return tenant
      ? {
        value: tenant.tenant_uuid,
        label: tenant.user_name,
      }
      : null;
  };

  const getButtonTenantSelectValue = (buttonNumber) => {
    const buttonKey = String(buttonNumber);
    const tenantUuids = deviceData.device_button_assignments?.[buttonKey] || [];

    return tenantUuids
      .map(getTenantOptionByUuid)
      .filter(Boolean);
  };

  const handleQuickDialButtonCountChange = (e) => {
    const rawValue = e.target.value;

    // Solo is fixed to one button, so this input should only affect Verso.
    if (!supportsMultipleQuickDialButtons) {
      setDeviceData((prev) => ({
        ...prev,
        quick_dial_button_count: 1,
        device_button_assignments: buildButtonAssignmentState(
          1,
          prev.device_button_assignments || {}
        ),
      }));
      return;
    }

    // Allow the user to erase the field before typing a new value.
    if (rawValue === "") {
      setDeviceData((prev) => ({
        ...prev,
        quick_dial_button_count: "",
      }));
      return;
    }

    // Keep only positive whole numbers.
    if (!/^\d+$/.test(rawValue)) {
      return;
    }

    const nextCount = clampQuickDialButtonCount(rawValue);

    setDeviceData((prev) => ({
      ...prev,
      quick_dial_button_count: nextCount,
      device_button_assignments: buildButtonAssignmentState(
        nextCount,
        prev.device_button_assignments || {}
      ),
    }));
  };

  const handleQuickDialButtonCountBlur = () => {
    if (!supportsMultipleQuickDialButtons) {
      setDeviceData((prev) => ({
        ...prev,
        quick_dial_button_count: 1,
        device_button_assignments: buildButtonAssignmentState(
          1,
          prev.device_button_assignments || {}
        ),
      }));
      return;
    }

    const nextCount = clampQuickDialButtonCount(deviceData.quick_dial_button_count);

    setDeviceData((prev) => ({
      ...prev,
      quick_dial_button_count: nextCount,
      device_button_assignments: buildButtonAssignmentState(
        nextCount,
        prev.device_button_assignments || {}
      ),
    }));
  };

  const handleButtonTenantChange = (buttonNumber, selectedOptions) => {
    const selected = selectedOptions || [];

    if (selected.length > MAX_TENANTS_PER_DEVICE_BUTTON) {
      setSaveStatus({
        show: true,
        success: false,
        message: `Button ${buttonNumber} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
      });
      return;
    }

    const buttonKey = String(buttonNumber);

    setDeviceData((prev) => ({
      ...prev,
      device_button_assignments: {
        ...(prev.device_button_assignments || {}),
        [buttonKey]: selected.map((opt) => opt.value),
      },
    }));
  };

  const getTenantByUuid = (tenantUuid) => {
    return directoryTenants.find(
      (tenant) => String(tenant.tenant_uuid) === String(tenantUuid)
    ) || null;
  };

  const getTenantName = (tenant) => {
    return tenant?.user_name || tenant?.name || "—";
  };

  const getTenantVirtualNumber = (tenant) => {
    return (
      tenant?.v_number ||
      tenant?.virtNumber ||
      tenant?.virtual_number ||
      tenant?.virtualNumber ||
      ""
    );
  };

  const getTenantPhone1 = (tenant) => {
    return (
      tenant?.phone1 ||
      tenant?.phone_1 ||
      tenant?.phoneNumber1 ||
      tenant?.phone_number_1 ||
      ""
    );
  };

  const getSelectedTenantsForButton = (buttonNumber) => {
    const buttonKey = String(buttonNumber);
    const tenantUuids = normalizeDeviceButtonValue(
      deviceData.device_button_assignments?.[buttonKey] || []
    );

    return tenantUuids.map((tenantUuid) => {
      return getTenantByUuid(tenantUuid) || {
        tenant_uuid: tenantUuid,
        user_name: "Unknown tenant",
        v_number: "",
        phone1: "",
      };
    });
  };

  const removeTenantFromButton = (buttonNumber, tenantUuid) => {
    const buttonKey = String(buttonNumber);

    setDeviceData((prev) => {
      const current = normalizeDeviceButtonValue(
        prev.device_button_assignments?.[buttonKey] || []
      );

      return {
        ...prev,
        device_button_assignments: {
          ...(prev.device_button_assignments || {}),
          [buttonKey]: current.filter(
            (id) => String(id) !== String(tenantUuid)
          ),
        },
      };
    });
  };

  const openTenantPicker = (buttonNumber) => {
    const buttonKey = String(buttonNumber);
    const current = normalizeDeviceButtonValue(
      deviceData.device_button_assignments?.[buttonKey] || []
    );

    setTenantPickerButtonNumber(buttonNumber);
    setTenantPickerSelected(current);
    setTenantPickerSearch("");
    setTenantPickerPage(1);
    setTenantPickerOpen(true);
  };

  const closeTenantPicker = () => {
    setTenantPickerOpen(false);
    setTenantPickerButtonNumber(null);
    setTenantPickerSelected([]);
    setTenantPickerSearch("");
    setTenantPickerPage(1);
  };

  const isTenantPickerSelected = (tenantUuid) => {
    return tenantPickerSelected.some(
      (id) => String(id) === String(tenantUuid)
    );
  };

  const toggleTenantPickerTenant = (tenantUuid) => {
    const tenantId = String(tenantUuid);

    setTenantPickerSelected((prev) => {
      const exists = prev.some((id) => String(id) === tenantId);

      if (exists) {
        return prev.filter((id) => String(id) !== tenantId);
      }

      if (prev.length >= MAX_TENANTS_PER_DEVICE_BUTTON) {
        setSaveStatus({
          show: true,
          success: false,
          message: `Button ${tenantPickerButtonNumber} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
        });
        return prev;
      }

      return [...prev, tenantId];
    });
  };

  const saveTenantPickerSelection = () => {
    if (!tenantPickerButtonNumber) return;

    if (tenantPickerSelected.length > MAX_TENANTS_PER_DEVICE_BUTTON) {
      setSaveStatus({
        show: true,
        success: false,
        message: `Button ${tenantPickerButtonNumber} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
      });
      return;
    }

    const buttonKey = String(tenantPickerButtonNumber);

    setDeviceData((prev) => ({
      ...prev,
      device_button_assignments: {
        ...(prev.device_button_assignments || {}),
        [buttonKey]: tenantPickerSelected,
      },
    }));

    closeTenantPicker();
  };

  const getAlreadyAssignedTenantIdsForPicker = () => {
    if (!tenantPickerButtonNumber) {
      return new Set();
    }

    const buttonKey = String(tenantPickerButtonNumber);
    const assignedTenantIds = normalizeDeviceButtonValue(
      deviceData.device_button_assignments?.[buttonKey] || []
    );

    return new Set(assignedTenantIds.map((id) => String(id)));
  };

  const tenantPickerAlreadyAssignedIds = getAlreadyAssignedTenantIdsForPicker();

  const tenantPickerFilteredTenants = directoryTenants.filter((tenant) => {
    const tenantId = String(tenant.tenant_uuid);

    // Do not show tenants already assigned to this button.
    if (tenantPickerAlreadyAssignedIds.has(tenantId)) {
      return false;
    }

    const search = tenantPickerSearch.trim().toLowerCase();

    if (!search) return true;

    const searchableText = [
      getTenantName(tenant),
      getTenantVirtualNumber(tenant),
      getTenantPhone1(tenant),
    ]
      .join(" ")
      .toLowerCase();

    return searchableText.includes(search);
  });

  const tenantPickerTotalPages = Math.max(
    1,
    Math.ceil(tenantPickerFilteredTenants.length / TENANT_PICKER_PAGE_SIZE)
  );

  const tenantPickerSafePage = Math.min(
    tenantPickerPage,
    tenantPickerTotalPages
  );

  const tenantPickerPageTenants = tenantPickerFilteredTenants.slice(
    (tenantPickerSafePage - 1) * TENANT_PICKER_PAGE_SIZE,
    tenantPickerSafePage * TENANT_PICKER_PAGE_SIZE
  );

  const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Failed to read image file"));

      reader.readAsDataURL(file);
    });
  };

  const dataUrlToBytes = (dataUrl) => {
    return Math.ceil((dataUrl || "").length * 0.75);
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", reject);
      image.setAttribute("crossOrigin", "anonymous");
      image.src = url;
    });

  const getCroppedImage = async (imageSrc) => {
    const image = await createImage(imageSrc);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const size = 432;
    canvas.width = size;
    canvas.height = size;

    const isPng = imageSrc.startsWith("data:image/png");
    const outputType = isPng ? "image/png" : "image/jpeg";

    if (!isPng) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
    }

    const baseScale = Math.min(size / image.width, size / image.height);
    const finalScale = baseScale * zoom;

    const drawWidth = image.width * finalScale;
    const drawHeight = image.height * finalScale;

    const drawX = (size - drawWidth) / 2 + panX;
    const drawY = (size - drawHeight) / 2 + panY;

    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);

    let dataUrl;

    if (outputType === "image/jpeg") {
      let quality = 0.9;

      do {
        dataUrl = canvas.toDataURL("image/jpeg", quality);
        quality -= 0.05;
      } while (
        dataUrlToBytes(dataUrl) > MAX_DIRECTORY_IMAGE_BYTES &&
        quality > 0.4
      );
    } else {
      dataUrl = canvas.toDataURL("image/png");

      if (dataUrlToBytes(dataUrl) > MAX_DIRECTORY_IMAGE_BYTES) {
        throw new Error("PNG image is too large. Try zooming out or using a simpler image.");
      }
    }

    return dataUrl;
  };

  const openDirectoryImageEditor = async (dirUuid, file = null, existingImage = null) => {
    const imageDataUrl = file ? await fileToDataUrl(file) : existingImage;

    if (!imageDataUrl) return;

    setImageEditorDirUuid(dirUuid);
    setImageEditorSrc(imageDataUrl);
    setImageEditorFileName(file?.name || "");
    setCrop({ x: 0, y: 0 });
    setZoom(1.0);
    setPanX(0);
    setPanY(0);
    setCroppedAreaPixels(null);
    setImageEditorError("");
    setImageEditorResultTooLarge(false);
    setImageEditorResultBytes(0);
    setImageEditorOpen(true);
  };

  const closeDirectoryImageEditor = () => {
    setImageEditorOpen(false);
    setImageEditorDirUuid(null);
    setImageEditorSrc(null);
    setImageEditorFileName("");
    setImageEditorError("");
    setImageEditorResultTooLarge(false);
    setImageEditorResultBytes(0);
  };

  const saveEditedDirectoryImage = async () => {
    if (!imageEditorDirUuid || !imageEditorSrc) return;

    try {
      const croppedImage = await getCroppedImage(imageEditorSrc);
      const croppedBytes = dataUrlToBytes(croppedImage);

      if (croppedBytes > MAX_DIRECTORY_IMAGE_BYTES) {
        setImageEditorResultTooLarge(true);
        setImageEditorResultBytes(croppedBytes);
        setImageEditorError("The resultant image is too large. Try another format.");
        return;
      }

      if (dataUrlToBytes(croppedImage) > MAX_DIRECTORY_IMAGE_BYTES) {
        setImageEditorError("The resultant image is too large. Try another format.");
        return;
      }

      setDirectoryDisplaySettings((prev) => ({
        ...prev,
        [imageEditorDirUuid]: {
          ...(prev[imageEditorDirUuid] || {}),
          image: croppedImage,
          imageName: imageEditorFileName || "Image selected",
          selectedColor: "",
          removeImage: false,
        },
      }));

      closeDirectoryImageEditor();
    } catch (err) {
      console.error("Failed to save edited image:", err);
      setImageEditorError(err.message || "Failed to crop image. Please try another image.");
    }
  };

  const handleDirectoryColorSelect = async (dirUuid, option) => {
    if (!dirUuid || !option || isBusy) return;

    setQuickSetupStatus({ show: false, success: false, message: "" });

    try {
      const dataUrl = await createSolidColorDirectoryImageDataUrl(option.hex);
      const bytes = dataUrlToBytes(dataUrl);

      if (bytes > MAX_DIRECTORY_IMAGE_BYTES) {
        setQuickSetupStatus({
          show: true,
          success: false,
          message: "The selected color image is too large. Please try another color.",
        });
        return;
      }

      setDirectoryDisplaySettings((prev) => ({
        ...prev,
        [dirUuid]: {
          ...(prev[dirUuid] || {}),
          image: dataUrl,
          imageName: `${option.label} color`,
          selectedColor: option.hex,
          removeImage: false,
        },
      }));
    } catch (err) {
      console.error("Failed to create directory color image:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to create directory color image.",
      });
    }
  };

  useEffect(() => {
    if (!siteUuid) return;

    const loadDirectories = async () => {
      try {
        const res = await fetch(
          `${serverHost}/hrms/get_directories?site_uuid=${siteUuid}`
        );
        if (!res.ok) throw new Error("Failed to fetch directories");
        const data = await res.json();
        setDirectories(data);
      } catch (err) {
        console.error("Failed to load directories:", err);
      }
    };

    loadDirectories();
  }, [siteUuid]);

  useEffect(() => {
    if (!siteUuid) return;

    const loadExtensions = async () => {
      try {
        const res = await fetch(
          `${serverHost}/hrms/hucs/extensions/by-site/${siteUuid}`
        );
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data?.error || "Failed to fetch HUCS extensions");
        }

        setExtensionOptions(data || []);
      } catch (err) {
        console.error("Failed to load HUCS extensions:", err);
        setExtensionOptions([]);
      }
    };

    loadExtensions();
  }, [siteUuid]);

  useEffect(() => {
    if (!deviceData.dir_uuids || deviceData.dir_uuids.length === 0) {
      setDirectoryTenants([]);
      return;
    }

    const loadDirectoryTenants = async () => {
      try {
        // Fetch tenants for each selected directory
        const tenantsArrays = await Promise.all(
          deviceData.dir_uuids.map(async (dir_uuid) => {
            const res = await fetch(
              `${serverHost}/hrms/get_directory_tenants?dir_uuid=${dir_uuid}&site_uuid=${siteUuid}`
            );
            if (!res.ok) throw new Error("Failed to fetch tenants");
            return await res.json();
          })
        );

        // Flatten and remove duplicates
        const allTenants = tenantsArrays.flat();
        const uniqueTenants = Array.from(
          new Map(allTenants.map(t => [t.tenant_uuid, t])).values()
        );

        setDirectoryTenants(uniqueTenants);
      } catch (err) {
        console.error("Failed to load tenants:", err);
        setDirectoryTenants([]);
      }
    };

    loadDirectoryTenants();
  }, [deviceData.dir_uuids]);

  // Load device if editing
  useEffect(() => {
    if (!editing) return;

    const site = getSite();
    if (!site?.site_uuid) return;

    const loadDevice = async () => {
      try {
        const [deviceRes, dirsRes] = await Promise.all([
          fetch(`${serverHost}/hrms/get_device/${device_uuid}`),
          fetch(`${serverHost}/hrms/get_device_dir?device_uuid=${device_uuid}&site_uuid=${site.site_uuid}`),
        ]);

        if (!deviceRes.ok) throw new Error("Failed to fetch device");

        const data = await deviceRes.json();
        const dirsData = dirsRes.ok ? await dirsRes.json() : [];

        const loadedDeviceModel = data.device_model || "";

        const loadedButtonCount = loadedDeviceModel === "2N IP Verso"
          ? clampQuickDialButtonCount(data.quick_dial_button_count || 1)
          : 1;

        const loadedAssignments = buildButtonAssignmentState(
          loadedButtonCount,
          normalizeDeviceButtonAssignments(
            data.device_button_assignments,
            data.device_button
          )
        );

        setDeviceData({
          device_name: data.device_name || "",
          device_model: loadedDeviceModel,
          device_ip: data.device_ip || "",
          device_mac: data.device_mac || "",
          device_firmware: data.device_firmware || (
            loadedDeviceModel === AKUVOX_S539_MODEL
              ? "539.30.10.523"
              : "2.46"
          ),
          device_button: normalizeDeviceButtonValue(data.device_button),
          quick_dial_button_count: loadedButtonCount,
          device_button_assignments: loadedAssignments,
          extension_uuid: data.extension_uuid || "",
          site_uuid: data.site_uuid || "",
          last_checkin: data.last_checkin || null,
          dir_uuids: dirsData.map((d) => d.dir_uuid || d.directory_uuid),
        });
        const loadedBackground = data.background_image || null;
        setBackgroundPreview(loadedBackground);
        setSavedBackgroundPreview(loadedBackground);
      } catch (err) {
        console.error("Failed to fetch device:", err);
        setSaveStatus({ show: true, success: false, message: "Failed to load device" });
      } finally {
        setLoading(false);
      }
    };

    loadDevice();
  }, [device_uuid, editing]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadRfidBacklightState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadShowcaseModeState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (!supportsCurrentDeviceApi) return;
    loadCallLogs();
  }, [editing, device_uuid, callLogsRange, supportsCurrentDeviceApi]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadRootFolderDisplayState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Style") return;

    loadStyleDisplaySettingsState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (deviceData.device_model !== "2N IP Verso") return;

    loadVersoDisplaySettingsState();
  }, [editing, device_uuid, deviceData.device_model]);

  useEffect(() => {
    if (!displaySettingsOpen || !editing || !device_uuid || !isAkuvoxS539) {
      return;
    }

    // Opening the panel must always start from the values currently held by
    // the intercom, rather than the values read when the device form mounted.
    loadAkuvoxDisplaySettingsState();
  }, [displaySettingsOpen, editing, device_uuid, isAkuvoxS539]);

  useEffect(() => {
    akuvoxX916LoadedSettings.current = null;
    setAkuvoxX916DisplayLoaded(false);
    setAkuvoxX916DisplayDirty(false);
    setAkuvoxX916DisplayLoadError("");
    setAkuvoxX916DisplaySettings(DEFAULT_AKUVOX_X916_DISPLAY_SETTINGS);
  }, [device_uuid, isAkuvoxX916]);

  useEffect(() => {
    if (!x916DisplaySettingsOpen || !editing || !device_uuid || !isAkuvoxX916 || akuvoxX916DisplayLoaded) return;
    loadAkuvoxX916DisplaySettings();
  }, [x916DisplaySettingsOpen, editing, device_uuid, isAkuvoxX916, akuvoxX916DisplayLoaded]);

  useEffect(() => {
    if (!liveEventsOpen) return;
    if (!device_uuid) return;

    loadEvents();
  }, [liveEventsOpen, device_uuid, eventsRange]);

  useEffect(() => {
    if (!deviceData.dir_uuids || deviceData.dir_uuids.length === 0) {
      setDirectoryDisplaySettings({});
      setInitialDirectoryDisplaySettings({});
      return;
    }

    setDirectoryDisplaySettings((prev) => {
      const next = {};

      deviceData.dir_uuids.forEach((dirUuid) => {
        next[dirUuid] = prev[dirUuid] || {
          priority: "",
          image: null,
          imageName: "",
          removeImage: false,
        };
      });

      return next;
    });
  }, [deviceData.dir_uuids]);

  useEffect(() => {
    if (!imageEditorOpen) return;

    const el = imageEditorRef.current;
    if (!el) return;

    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();

      const delta = e.deltaY > 0 ? -0.08 : 0.08;

      setZoom((prev) => {
        const next = prev + delta;
        return Math.min(3, Math.max(0.1, next));
      });
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, [imageEditorOpen]);

  useEffect(() => {
    if (!editing || !device_uuid) return;
    if (!deviceData.dir_uuids || deviceData.dir_uuids.length === 0) return;

    loadDirectoryDisplaySettings();
  }, [editing, device_uuid, deviceData.dir_uuids]);

  useEffect(() => {
    if (!imageEditorOpen || !imageEditorSrc) return;

    const timeout = setTimeout(async () => {
      try {
        const croppedImage = await getCroppedImage(imageEditorSrc);
        const bytes = dataUrlToBytes(croppedImage);

        setImageEditorResultBytes(bytes);
        setImageEditorResultTooLarge(bytes > MAX_DIRECTORY_IMAGE_BYTES);

        if (bytes > MAX_DIRECTORY_IMAGE_BYTES) {
          setImageEditorError("The resultant image is too large. Try zooming out or choosing a simpler image.");
        } else {
          setImageEditorError("");
        }
      } catch (err) {
        console.error("Failed to check cropped image size:", err);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [imageEditorOpen, imageEditorSrc, zoom, panX, panY]);

  useEffect(() => {
    if (!displaySettingsOpen) return;

    if (
      deviceData.device_model === "2N IP Verso" &&
      isRootFolderUnsupportedMessage(quickSetupStatus.message)
    ) {
      setQuickSetupStatus(emptyQuickSetupStatus);
    }
  }, [displaySettingsOpen, deviceData.device_model, quickSetupStatus.message]);

  const loadRfidBacklightState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setRfidBacklightLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/rfid_backlight/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load RFID backlight state");
      }

      const enabled = !!result.enabled;
      setRfidBacklightEnabled(enabled);
      setInitialRfidBacklightEnabled(enabled);
    } catch (err) {
      console.error("Load RFID backlight state error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load RFID backlight state",
      });
    } finally {
      setRfidBacklightLoading(false);
    }
  };

  const handleRfidBacklightToggle = (e) => {
    setRfidBacklightEnabled(e.target.checked);
    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const loadShowcaseModeState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setShowcaseModeLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/showcase_mode/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load showcase mode");
      }

      const mode = result.mode || "OFF";
      setShowcaseMode(mode);
      setInitialShowcaseMode(mode);
    } catch (err) {
      console.error("Load showcase mode error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load showcase mode",
      });
    } finally {
      setShowcaseModeLoading(false);
    }
  };

  const handleShowcaseModeChange = (e) => {
    setShowcaseMode(e.target.value);
    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const loadRootFolderDisplayState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setRootFolderDisplayLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/root_folder_display/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load root folder display");
      }

      const mode = result.mode || "CARDS_1_COLUMN";
      setRootFolderDisplay(mode);
      setInitialRootFolderDisplay(mode);
    } catch (err) {
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load root folder display",
      });
    } finally {
      setRootFolderDisplayLoading(false);
    }
  };

  const handleRootFolderDisplayChange = (e) => {
    setRootFolderDisplay(e.target.value);
    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const normalizeStyleDisplaySettings = (settings = {}) => {
    const rawMode = settings.entry_keypad_mode ?? settings.entryKeypadMode ?? "NORMAL";
    const normalizedMode = String(rawMode || "NORMAL").trim().toUpperCase();

    return {
      codeEntryButton: settings.code_entry_button ?? settings.codeEntryButton ?? true,
      entryKeypadMode: ["NORMAL", "SCRAMBLED"].includes(normalizedMode)
        ? normalizedMode
        : "NORMAL",
      touchSounds: settings.touch_sounds ?? settings.touchSounds ?? true,
      flexiBuilderEnabled: settings.flexi_builder_enabled ?? settings.flexiBuilderEnabled ?? false,
    };
  };

  const toStyleDisplaySettingsPayload = (settings = {}) => ({
    code_entry_button: !!settings.codeEntryButton,
    entry_keypad_mode: settings.entryKeypadMode || "NORMAL",
    touch_sounds: !!settings.touchSounds,
    flexi_builder_enabled: !!settings.flexiBuilderEnabled,
  });

  const loadStyleDisplaySettingsState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    setStyleDisplaySettingsLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/style_display_settings/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load Style display settings");
      }

      const normalized = normalizeStyleDisplaySettings(result.settings || {});
      setStyleDisplaySettings(normalized);
      setInitialStyleDisplaySettings(normalized);
    } catch (err) {
      console.error("Load Style display settings error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load Style display settings",
      });
    } finally {
      setStyleDisplaySettingsLoading(false);
    }
  };

  const handleStyleDisplayToggle = (field) => (e) => {
    setStyleDisplaySettings((prev) => ({
      ...prev,
      [field]: e.target.checked,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const handleStyleKeypadModeChange = (e) => {
    setStyleDisplaySettings((prev) => ({
      ...prev,
      entryKeypadMode: e.target.value,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const saveStyleDisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (JSON.stringify(styleDisplaySettings) === JSON.stringify(initialStyleDisplaySettings)) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/style_display_settings/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          settings: toStyleDisplaySettingsPayload(styleDisplaySettings),
        }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update Style display settings");
    }

    const normalized = normalizeStyleDisplaySettings(result.settings || styleDisplaySettings);
    setStyleDisplaySettings(normalized);
    setInitialStyleDisplaySettings(normalized);
  };

  const normalizeVersoDisplaySettings = (settings = {}) => ({
    phonebookDisplayed: settings.phonebook_displayed ?? settings.phonebookDisplayed ?? true,
    codeEntryButton: settings.code_entry_button ?? settings.codeEntryButton ?? true,
    entryKeypadMode: settings.entry_keypad_mode ?? settings.entryKeypadMode ?? "NORMAL",
    preferIconsToText: settings.prefer_icons_to_text ?? settings.preferIconsToText ?? false,
  });

  const toVersoDisplaySettingsPayload = (settings = {}) => ({
    phonebook_displayed: !!settings.phonebookDisplayed,
    code_entry_button: !!settings.codeEntryButton,
    entry_keypad_mode: settings.entryKeypadMode || "NORMAL",
    prefer_icons_to_text: !!settings.preferIconsToText,
  });

  const loadVersoDisplaySettingsState = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Verso") return;

    setVersoDisplaySettingsLoading(true);

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_quick_setup/verso_display_settings/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load Verso display settings");
      }

      const normalized = normalizeVersoDisplaySettings(result.settings || {});
      setVersoDisplaySettings(normalized);
      setInitialVersoDisplaySettings(normalized);
    } catch (err) {
      console.error("Load Verso display settings error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load Verso display settings",
      });
    } finally {
      setVersoDisplaySettingsLoading(false);
    }
  };

  const handleVersoDisplayToggle = (field) => (e) => {
    setVersoDisplaySettings((prev) => ({
      ...prev,
      [field]: e.target.checked,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const handleVersoKeypadModeChange = (e) => {
    setVersoDisplaySettings((prev) => ({
      ...prev,
      entryKeypadMode: e.target.value,
    }));

    setQuickSetupStatus({ show: false, success: false, message: "" });
  };

  const saveVersoDisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Verso") return;

    if (JSON.stringify(versoDisplaySettings) === JSON.stringify(initialVersoDisplaySettings)) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/verso_display_settings/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          settings: toVersoDisplaySettingsPayload(versoDisplaySettings),
        }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update Verso display settings");
    }

    const normalized = normalizeVersoDisplaySettings(result.settings || versoDisplaySettings);
    setVersoDisplaySettings(normalized);
    setInitialVersoDisplaySettings(normalized);
  };

  const normalizeAkuvoxDisplaySettings = (settings = {}) => {
    const theme = String(
      settings.theme ?? DEFAULT_AKUVOX_DISPLAY_SETTINGS.theme
    );

    const rawBuilding =
      settings.building && typeof settings.building === "object"
        ? settings.building
        : {};

    const rawVilla =
      settings.villa && typeof settings.villa === "object"
        ? settings.villa
        : {};

    const rawPrompts =
      settings.prompts && typeof settings.prompts === "object"
        ? settings.prompts
        : {};

    const rawSpeedDial =
      settings.speed_dial && typeof settings.speed_dial === "object"
        ? settings.speed_dial
        : {};

    const rawAppearance =
      settings.appearance && typeof settings.appearance === "object"
        ? settings.appearance
        : {};

    const rawBuildingKeys = Array.isArray(rawBuilding.keys)
      ? rawBuilding.keys
      : Array.isArray(settings.keys)
        ? settings.keys
        : [];

    const rawVillaKeys = Array.isArray(rawVilla.keys)
      ? rawVilla.keys
      : [];

    const normalizeKeys = (
      rawKeys,
      defaultKeys,
      includeVisibility = false
    ) =>
      defaultKeys.map((fallback, fallbackIndex) => {
        const item =
          rawKeys.find(
            (candidate) =>
              Number(candidate?.index) === fallback.index
          ) ||
          rawKeys[fallbackIndex] ||
          {};

        return {
          index: fallback.index,
          label: String(item.label ?? fallback.label),
          type: String(item.type ?? fallback.type),
          ...(Object.prototype.hasOwnProperty.call(
            fallback,
            "value"
          )
            ? { value: String(item.value ?? fallback.value) }
            : {}),
          ...(includeVisibility
            ? {
              visible: toAkuvoxBool(
                item.visible,
                fallback.visible
              ),
            }
            : {}),
        };
      });

    const rawLanguages = Array.isArray(settings.languages)
      ? settings.languages
      : [];

    const rawTime =
      settings.time && typeof settings.time === "object"
        ? settings.time
        : {};

    const rawTimezoneId = String(
      rawTime.timezone_id ?? rawTime.timezoneId ?? ""
    );

    const timezoneLabel = String(
      rawTime.timezone_label ??
      rawTime.timezoneLabel ??
      rawTimezoneId
    );

    const timezoneId = normalizeAkuvoxTimezoneId(
      rawTimezoneId,
      timezoneLabel
    );

    return {
      theme,
      keypadMode: String(
        settings.keypad_mode ??
        settings.keypadMode ??
        DEFAULT_AKUVOX_DISPLAY_SETTINGS.keypadMode
      ),

      prompts: {
        call: String(
          rawPrompts.call ??
          settings.call_prompt ??
          settings.callPrompt ??
          ""
        ),
        pin: String(rawPrompts.pin ?? ""),
        directory: String(rawPrompts.directory ?? ""),
      },

      building: {
        defaultPage: String(
          rawBuilding.default_page ??
          rawBuilding.defaultPage ??
          (theme === "2" ? settings.default_page : undefined) ??
          DEFAULT_AKUVOX_DISPLAY_SETTINGS.building.defaultPage
        ),
        keys: normalizeKeys(
          rawBuildingKeys,
          DEFAULT_AKUVOX_DISPLAY_SETTINGS.building.keys
        ),
      },

      villa: {
        defaultPage: String(
          rawVilla.default_page ??
          rawVilla.defaultPage ??
          (theme === "1" ? settings.default_page : undefined) ??
          DEFAULT_AKUVOX_DISPLAY_SETTINGS.villa.defaultPage
        ),
        keys: normalizeKeys(
          rawVillaKeys,
          DEFAULT_AKUVOX_DISPLAY_SETTINGS.villa.keys,
          true
        ),
      },

      speedDial: {
        groupEnabled: toAkuvoxBool(
          rawSpeedDial.group_enabled ??
          rawSpeedDial.groupEnabled,
          false
        ),
        groupNumber: String(
          rawSpeedDial.group_number ??
          rawSpeedDial.groupNumber ??
          ""
        ),
        dialOutForward: toAkuvoxBool(
          rawSpeedDial.dial_out_forward ??
          rawSpeedDial.dialOutForward,
          false
        ),
        account: String(rawSpeedDial.account ?? "0"),
        openRelay: String(
          rawSpeedDial.open_relay ??
          rawSpeedDial.openRelay ??
          "0"
        ),
        httpEnabled: toAkuvoxBool(
          rawSpeedDial.http_enabled ??
          rawSpeedDial.httpEnabled,
          false
        ),
        httpUrl: String(
          rawSpeedDial.http_url ??
          rawSpeedDial.httpUrl ??
          ""
        ),
      },

      appearance: {
        mode: String(rawAppearance.mode ?? "0"),

        residentTheme: String(
          rawAppearance.resident_theme ??
          rawAppearance.residentTheme ??
          "0"
        ),

        autoActivation: Array.isArray(
          rawAppearance.auto_activation ??
          rawAppearance.autoActivation
        )
          ? (
            rawAppearance.auto_activation ??
            rawAppearance.autoActivation
          ).map((value) => String(value))
          : [],
      },

      languageSelectorVisible: toAkuvoxBool(
        settings.language_selector_visible ??
        settings.languageSelectorVisible,
        false
      ),
      deviceLanguage: String(
        settings.device_language ??
        settings.deviceLanguage ??
        DEFAULT_AKUVOX_DISPLAY_SETTINGS.deviceLanguage
      ),
      languages:
        DEFAULT_AKUVOX_DISPLAY_SETTINGS.languages.map(
          (fallback, index) =>
            String(rawLanguages[index] ?? fallback)
        ),

      time: {
        autoTime: toAkuvoxBool(
          rawTime.auto_time ?? rawTime.autoTime,
          true
        ),
        localTime: toDateTimeLocalValue(
          rawTime.local_time ?? rawTime.localTime
        ),
        timezoneId,
        timezoneLabel,
        timezoneOptions: AKUVOX_TIMEZONE_OPTIONS,
        ntpServer1: String(
          rawTime.ntp_server1 ??
          rawTime.ntpServer1 ??
          DEFAULT_AKUVOX_DISPLAY_SETTINGS.time.ntpServer1
        ),
        dateFormat: String(
          rawTime.date_format ??
          rawTime.dateFormat ??
          DEFAULT_AKUVOX_DISPLAY_SETTINGS.time.dateFormat
        ),
        timeFormat: String(
          rawTime.time_format ??
          rawTime.timeFormat ??
          DEFAULT_AKUVOX_DISPLAY_SETTINGS.time.timeFormat
        ),
      },
    };
  };

  const toAkuvoxDisplaySettingsPayload = (settings = {}) => ({
    theme: String(settings.theme || "1"),
    keypad_mode: String(settings.keypadMode ?? "0"),

    prompts: {
      call: String(settings.prompts?.call ?? ""),
      pin: String(settings.prompts?.pin ?? ""),
      directory: String(settings.prompts?.directory ?? ""),
    },

    building: {
      default_page: String(
        settings.building?.defaultPage ?? "0"
      ),
      keys: (settings.building?.keys || []).map(
        (item, index) => ({
          index: Number(item.index || index + 1),
          label: String(item.label ?? ""),
          type: String(item.type ?? "0"),
          value: String(item.value ?? ""),
        })
      ),
    },

    villa: {
      default_page: String(settings.villa?.defaultPage ?? "5"),
      keys: (settings.villa?.keys || []).map(
        (item, index) => ({
          index: Number(item.index || index + 1),
          label: String(item.label ?? ""),
          type: String(item.type ?? "2"),
          visible: !!item.visible,
        })
      ),
    },

    speed_dial: {
      group_enabled: !!settings.speedDial?.groupEnabled,
      group_number: String(
        settings.speedDial?.groupNumber ?? ""
      ),
      dial_out_forward: !!settings.speedDial?.dialOutForward,
      account: String(settings.speedDial?.account ?? "0"),
      open_relay: String(
        settings.speedDial?.openRelay ?? "0"
      ),
      http_enabled: !!settings.speedDial?.httpEnabled,
      http_url: String(settings.speedDial?.httpUrl ?? ""),
    },

    appearance: {
      resident_theme: String(
        settings.appearance?.residentTheme ?? "0"
      ),

      auto_activation: (
        settings.appearance?.autoActivation || []
      ).map((value) => String(value)),
    },

    language_selector_visible:
      !!settings.languageSelectorVisible,
    device_language: String(settings.deviceLanguage ?? "0"),
    languages: (settings.languages || []).map((value) =>
      String(value)
    ),

    time: {
      auto_time: !!settings.time?.autoTime,
      local_time: String(settings.time?.localTime ?? ""),
      timezone_id: String(settings.time?.timezoneId ?? ""),
      ntp_server1: String(settings.time?.ntpServer1 ?? ""),
      date_format: String(settings.time?.dateFormat ?? "0"),
      time_format: String(settings.time?.timeFormat ?? "0"),
    },
  });

  const markAkuvoxDisplaySectionDirty = (section) => {
    if (section) {
      akuvoxDisplayDirtySectionsRef.current.add(section);
    }
  };

  const loadAkuvoxDisplaySettingsState = async () => {
    if (!editing || !device_uuid || !isAkuvoxS539) return;

    const requestId = akuvoxDisplayLoadRequestRef.current + 1;
    akuvoxDisplayLoadRequestRef.current = requestId;
    setAkuvoxDisplaySettingsLoading(true);
    setAkuvoxDisplaySettings((prev) => ({ ...prev, theme: "" }));

    try {
      const result = await getAkuvoxDisplaySettings(device_uuid);

      // Ignore an older response if another live read started after it.
      if (requestId !== akuvoxDisplayLoadRequestRef.current) return;

      const liveSettings =
        result?.settings && typeof result.settings === "object"
          ? result.settings
          : result &&
            typeof result === "object" &&
            Object.prototype.hasOwnProperty.call(result, "theme")
            ? result
            : null;

      if (!liveSettings) {
        throw new Error(
          "The Akuvox display response did not contain a settings object"
        );
      }

      const liveTheme = String(liveSettings.theme ?? "").trim();
      if (!AKUVOX_THEME_OPTIONS.some((option) => option.value === liveTheme)) {
        throw new Error(
          `The Akuvox device returned unsupported theme value ${liveTheme || "(missing)"
          }`
        );
      }

      const normalized = normalizeAkuvoxDisplaySettings(liveSettings);
      console.info("[AKUVOX DISPLAY UI] live settings applied", {
        source: result?.source || "unknown",
        fetchedAt: result?.fetched_at || null,
        rawTheme: liveTheme,
        normalizedTheme: normalized.theme,
      });
      setAkuvoxDisplaySettings(normalized);
      akuvoxDisplayDirtySectionsRef.current.clear();
    } catch (err) {
      if (requestId !== akuvoxDisplayLoadRequestRef.current) return;

      console.error("Load Akuvox display settings error:", err);
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load Akuvox display settings",
      });
    } finally {
      if (requestId === akuvoxDisplayLoadRequestRef.current) {
        setAkuvoxDisplaySettingsLoading(false);
      }
    }
  };

  const applyAkuvoxX916DisplaySettings = (raw) => {
    if (!raw || typeof raw !== "object") {
      throw new Error("The X916 display response did not contain a settings object");
    }
    const theme = String(raw.theme ?? "").trim();
    if (!AKUVOX_X916_THEME_OPTIONS.some((option) => option.value === theme)) {
      throw new Error(`The X916 returned unsupported theme value ${theme || "(missing)"}`);
    }
    const text = (value) => value == null ? null : String(value);
    const flag = (value) => value === true || value === "1" || value === 1
      ? true : value === false || value === "0" || value === 0 ? false : null;
    const settings = {
      ...DEFAULT_AKUVOX_X916_DISPLAY_SETTINGS,
      keypadMode: text(raw.keypad_mode),
      speedDial: {
        cloudNumber: text(raw.speed_dial?.cloud_number),
        groupEnabled: flag(raw.speed_dial?.group_enabled),
        groupNumber: text(raw.speed_dial?.group_number),
      },
      groups: Array.isArray(raw.groups) ? raw.groups.map(String) : [],
      groupsError: raw.groups_error || "",
      reception: {
        dialType: text(raw.reception?.dial_type),
        openRelay: text(raw.reception?.open_relay),
        httpEnabled: flag(raw.reception?.http_enabled),
        httpUrl: text(raw.reception?.http_url),
      },
      languageSelectorVisible: flag(raw.language_selector_visible),
      languages: Array.from({ length: 4 }, (_, index) => text(raw.languages?.[index])),
      appearance: {
        mode: text(raw.appearance?.mode),
        residentTheme: text(raw.appearance?.resident_theme),
        autoActivation: Array.isArray(raw.appearance?.auto_activation)
          ? raw.appearance.auto_activation.map(String) : null,
      },
      addressLine: text(raw.address_line),
      welcomeMessage: text(raw.welcome_message),
      visitorCallButtonLabel: text(raw.visitor_call_button_label),
      tempPinEnterButtonLabel: text(raw.temp_pin_enter_button_label),
      theme,
      displayType: raw.display_type == null ? "" : String(raw.display_type).trim(),
      numberOfKeys: raw.number_of_keys == null ? "" : String(raw.number_of_keys),
      fullScreenDirectory: raw.full_screen_directory == null
        ? null
        : raw.full_screen_directory === true || String(raw.full_screen_directory) === "1",
      // Only render rows read from the device; do not fill gaps with defaults.
      keys: (Array.isArray(raw.keys) ? raw.keys : []).map((item) => ({
        index: Number(item.index),
        type: String(item.type),
        label: String(item.label),
        value: String(item.value),
      })),
    };
    akuvoxX916LoadedSettings.current = settings;
    setAkuvoxX916DisplaySettings(settings);
    setAkuvoxX916DisplayDirty(false);
    setAkuvoxX916DisplayLoaded(true);
    setAkuvoxX916DisplayLoadError("");
  };

  const loadAkuvoxX916DisplaySettings = async () => {
    if (!editing || !device_uuid || !isAkuvoxX916) return;
    setAkuvoxX916DisplayLoading(true);
    setAkuvoxX916DisplayLoadError("");
    try {
      const result = await getAkuvoxDisplaySettings(device_uuid);
      applyAkuvoxX916DisplaySettings(result?.settings || result);
    } catch (err) {
      console.error("Load Akuvox X916 display settings error:", err);
      setAkuvoxX916DisplayLoaded(false);
      setAkuvoxX916DisplayLoadError(err.message || "Failed to load X916 display settings");
      setQuickSetupStatus({
        show: true,
        success: false,
        message: err.message || "Failed to load X916 display settings",
      });
    } finally {
      setAkuvoxX916DisplayLoading(false);
    }
  };

  const updateAkuvoxX916Field = (field, value) => {
    setAkuvoxX916DisplaySettings((prev) => ({ ...prev, [field]: value }));
    setAkuvoxX916DisplayDirty(true);
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxX916NestedField = (section, field, value) => {
    setAkuvoxX916DisplaySettings((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    setAkuvoxX916DisplayDirty(true);
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxX916Key = (keyIndex, field, value) => {
    setAkuvoxX916DisplaySettings((prev) => ({
      ...prev,
      keys: prev.keys.map((item) =>
        item.index === keyIndex
          ? {
            ...item,
            [field]: value,
          }
          : item
      ),
    }));
    setAkuvoxX916DisplayDirty(true);
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxX916Language = (index, value) => {
    setAkuvoxX916DisplaySettings((prev) => ({
      ...prev,
      languages: prev.languages.map((language, languageIndex) =>
        languageIndex === index ? value : language
      ),
    }));
    setAkuvoxX916DisplayDirty(true);
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const saveAkuvoxX916DisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid || !isAkuvoxX916 || !akuvoxX916DisplayLoaded) return;
    const current = akuvoxX916DisplaySettings;
    const forceThemeMode = current.theme === "2" && current.appearance?.mode != null && current.appearance.mode !== "0";
    if (!akuvoxX916DisplayDirty && !forceThemeMode) return;
    const previous = akuvoxX916LoadedSettings.current;
    if (!previous) throw new Error("Read the X916 settings before saving changes");
    const settings = {};
    if (current.theme !== previous.theme) settings.theme = current.theme;
    if (current.theme === "2" && current.displayType !== "" && current.displayType !== previous.displayType) {
      settings.display_type = current.displayType;
    }
    if (current.theme === "5" && current.fullScreenDirectory != null && current.fullScreenDirectory !== previous.fullScreenDirectory) {
      settings.full_screen_directory = current.fullScreenDirectory;
    }
    if (current.theme === "2" || (current.theme === "5" && current.fullScreenDirectory !== true)) {
      if (current.numberOfKeys !== "" && current.numberOfKeys !== previous.numberOfKeys) {
        settings.number_of_keys = current.numberOfKeys;
      }
      const changedKeys = current.keys
        .filter((key) => current.numberOfKeys === "" || key.index <= Number(current.numberOfKeys))
        .map((key) => {
          const old = previous.keys.find((item) => item.index === key.index);
          if (!old) return null;
          const change = { index: key.index };
          if (key.type !== old.type) change.type = key.type;
          if (key.label !== old.label) change.label = key.label;
          // Preserve device-controlled values when editing other key types.
          if (key.type === "5" && key.value !== old.value) change.value = key.value;
          return Object.keys(change).length > 1 ? change : null;
        }).filter(Boolean);
      if (changedKeys.length) settings.keys = changedKeys;
    }
    if (current.theme === "2") {
      const appearance = {};
      if (forceThemeMode) appearance.mode = "0";
      if (current.appearance.residentTheme != null && current.appearance.residentTheme !== previous.appearance.residentTheme) {
        appearance.resident_theme = current.appearance.residentTheme;
      }
      if (current.appearance.autoActivation != null &&
          JSON.stringify([...current.appearance.autoActivation].sort()) !==
          JSON.stringify([...(previous.appearance.autoActivation || [])].sort())) {
        appearance.auto_activation = current.appearance.autoActivation;
      }
      if (Object.keys(appearance).length) settings.appearance = appearance;
    }
    // These sections apply to all three themes. Send only edited fields.
    if (current.keypadMode != null && current.keypadMode !== previous.keypadMode) {
      settings.keypad_mode = current.keypadMode;
    }
    if (current.speedDial.groupEnabled != null && current.speedDial.groupNumber != null &&
        (current.speedDial.groupEnabled !== previous.speedDial.groupEnabled ||
         current.speedDial.groupNumber !== previous.speedDial.groupNumber)) {
      settings.speed_dial = {
        group_enabled: current.speedDial.groupEnabled,
        group_number: current.speedDial.groupEnabled ? current.speedDial.groupNumber : "0",
      };
    }
    const reception = {};
    for (const [field, apiField] of [
      ["dialType", "dial_type"], ["openRelay", "open_relay"],
      ["httpEnabled", "http_enabled"], ["httpUrl", "http_url"],
    ]) {
      if (current.reception[field] != null && current.reception[field] !== previous.reception[field]) {
        reception[apiField] = current.reception[field];
      }
    }
    if (Object.keys(reception).length) settings.reception = reception;
    if (current.languageSelectorVisible != null && current.languageSelectorVisible !== previous.languageSelectorVisible) {
      settings.language_selector_visible = current.languageSelectorVisible;
    }
    if (current.languages.some((language, index) => language !== previous.languages[index])) {
      settings.languages = current.languages;
    }
    for (const { field, apiField } of AKUVOX_X916_HOMEPAGE_TEXT_FIELDS) {
      if (current[field] != null && current[field] !== previous[field]) {
        settings[apiField] = current[field];
      }
    }
    if (!Object.keys(settings).length) {
      setAkuvoxX916DisplayDirty(false);
      return;
    }
    const result = await updateAkuvoxDisplaySettings(device_uuid, settings);
    if (result?.success === false) throw new Error(result.error || "Failed to save X916 display settings");
    // The backend returns a verified fresh read. Do not swallow a failed refresh
    // or clear unsaved changes when the returned settings are absent.
    applyAkuvoxX916DisplaySettings(result?.settings);
  };

  const updateAkuvoxDisplayField = (field, value) => {
    const sectionByField = {
      theme: "theme",
      keypadMode: "keypad_mode",
      languageSelectorVisible: "language_selector_visible",
      deviceLanguage: "device_language",
    };

    markAkuvoxDisplaySectionDirty(sectionByField[field]);
    setAkuvoxDisplaySettings((prev) => ({
      ...prev,
      [field]: value,
    }));
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxSectionField = (
    section,
    field,
    value
  ) => {
    const sectionByName = {
      prompts: "prompts",
      building: "building",
      villa: "villa",
      speedDial: "speed_dial",
      appearance: "appearance",
    };

    markAkuvoxDisplaySectionDirty(sectionByName[section]);
    setAkuvoxDisplaySettings((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxBuildingKey = (
    keyIndex,
    field,
    value
  ) => {
    markAkuvoxDisplaySectionDirty("building");
    setAkuvoxDisplaySettings((prev) => ({
      ...prev,
      building: {
        ...prev.building,
        keys: prev.building.keys.map((item) =>
          item.index === keyIndex
            ? { ...item, [field]: value }
            : item
        ),
      },
    }));
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxVillaKey = (
    keyIndex,
    field,
    value
  ) => {
    markAkuvoxDisplaySectionDirty("villa");
    setAkuvoxDisplaySettings((prev) => {
      const currentKey = prev.villa.keys.find(
        (item) => item.index === keyIndex
      );

      if (!currentKey) return prev;

      let keys;

      if (field === "type" && currentKey.type !== value) {
        const duplicateKey = prev.villa.keys.find(
          (item) =>
            item.index !== keyIndex && item.type === value
        );

        keys = prev.villa.keys.map((item) => {
          if (item.index === keyIndex) {
            return { ...item, type: value };
          }

          if (
            duplicateKey &&
            item.index === duplicateKey.index
          ) {
            return { ...item, type: currentKey.type };
          }

          return item;
        });
      } else {
        keys = prev.villa.keys.map((item) =>
          item.index === keyIndex
            ? { ...item, [field]: value }
            : item
        );
      }

      return {
        ...prev,
        villa: {
          ...prev.villa,
          keys,
        },
      };
    });

    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxPrompt = (field, value) => {
    updateAkuvoxSectionField("prompts", field, value);
  };

  const updateAkuvoxSpeedDialField = (field, value) => {
    updateAkuvoxSectionField("speedDial", field, value);
  };

  const updateAkuvoxAppearanceField = (field, value) => {
    updateAkuvoxSectionField("appearance", field, value);
  };

  const isAkuvoxVillaDefaultKey = (item) => {
    const defaultType =
      AKUVOX_VILLA_DEFAULT_PAGE_TO_KEY_TYPE[
      akuvoxDisplaySettings.villa.defaultPage
      ];

    return !!defaultType && item.type === defaultType;
  };

  const updateAkuvoxDisplayLanguage = (languageIndex, value) => {
    markAkuvoxDisplaySectionDirty("languages");
    setAkuvoxDisplaySettings((prev) => ({
      ...prev,
      languages: prev.languages.map((item, index) =>
        index === languageIndex ? value : item
      ),
    }));
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const updateAkuvoxTimeField = (field, value) => {
    markAkuvoxDisplaySectionDirty("time");
    setAkuvoxDisplaySettings((prev) => ({
      ...prev,
      time: { ...prev.time, [field]: value },
    }));
    setQuickSetupStatus(emptyQuickSetupStatus);
  };

  const saveAkuvoxDisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid || !isAkuvoxS539) return;

    const dirtySections = Array.from(
      akuvoxDisplayDirtySectionsRef.current
    );

    if (dirtySections.length === 0) return;

    const current = toAkuvoxDisplaySettingsPayload(akuvoxDisplaySettings);
    const changedSettings = {};

    dirtySections.forEach((section) => {
      if (Object.prototype.hasOwnProperty.call(current, section)) {
        changedSettings[section] = current[section];
      }
    });

    if (Object.keys(changedSettings).length === 0) return;

    console.info(
      "Saving Akuvox display sections:",
      Object.keys(changedSettings)
    );

    const result = await updateAkuvoxDisplaySettings(device_uuid, changedSettings);
    const normalized = normalizeAkuvoxDisplaySettings(
      result.settings || akuvoxDisplaySettings
    );
    setAkuvoxDisplaySettings(normalized);
    akuvoxDisplayDirtySectionsRef.current.clear();
  };

  const saveRootFolderDisplayIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (rootFolderDisplay === initialRootFolderDisplay) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/root_folder_display/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: rootFolderDisplay }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update root folder display");
    }

    setInitialRootFolderDisplay(rootFolderDisplay);
  };

  const saveDirectoryDisplaySettingsIfChanged = async () => {
    if (!editing || !device_uuid) return;

    const initial = JSON.stringify(initialDirectoryDisplaySettings);
    const current = JSON.stringify(directoryDisplaySettings);

    if (initial === current) {
      return;
    }

    const updates = Object.entries(directoryDisplaySettings).map(([dir_uuid, settings]) => ({
      dir_uuid,
      priority: settings.priority === "" ? null : Number(settings.priority),
      image: settings.image || null,
      remove_image: !!settings.removeImage,
    }));

    const res = await fetch(
      `${serverHost}/hrms/device_directory_display_settings/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ directories: updates }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update directory display settings");
    }

    setInitialDirectoryDisplaySettings({ ...directoryDisplaySettings });
  };

  const loadDirectoryDisplaySettings = async () => {
    if (!editing || !device_uuid) return;

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_directory_display_settings/${device_uuid}`
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to load directory display settings");
      }

      const settings = {};

      (result.directories || []).forEach((dir) => {
        settings[dir.dir_uuid] = {
          priority: dir.priority === null || dir.priority === undefined ? "" : String(dir.priority),
          image: dir.image || null,
          removeImage: false,
        };
      });

      setDirectoryDisplaySettings(settings);
      setInitialDirectoryDisplaySettings(settings);
    } catch (err) {
      console.error("Failed to load directory display settings:", err);
    }
  };

  const clearBackgroundInputElement = () => {
    const input = document.getElementById("input-device-background");
    if (input) input.value = "";
  };

  const clearPendingBackgroundSelection = () => {
    setBackgroundFile(null);
    setBackgroundPreview(savedBackgroundPreview);
    setBackgroundMeta({
      width: null,
      height: null,
      validDimensions: false,
    });
    setBackgroundStatus({ show: false, success: false, message: "" });
    clearBackgroundInputElement();
  };

  const handleBackgroundFileChange = async (e) => {
    const file = e.target.files?.[0] || null;
    setBackgroundPreview(null);
    setBackgroundFile(null);
    setBackgroundMeta({
      width: null,
      height: null,
      validDimensions: false,
    });
    setBackgroundStatus({ show: false, success: false, message: "" });

    if (!file) {
      setBackgroundPreview(savedBackgroundPreview);
      return;
    }

    try {
      const meta = await validateBackgroundFile(file);
      const previewUrl = URL.createObjectURL(file);

      setBackgroundFile(file);
      setBackgroundPreview(previewUrl);
      setBackgroundMeta(meta);

      if (!meta.validDimensions) {
        setBackgroundStatus({
          show: true,
          success: false,
          message: `Image must be at least 800 x 1280. Selected image is ${meta.width} x ${meta.height}.`,
        });
      }
    } catch (err) {
      setBackgroundStatus({
        show: true,
        success: false,
        message: err.message || "Invalid image file",
      });
    }
  };

  const handleAkuvoxX916ImageSelect = async (asset, event) => {
    if (isBusy || !isAkuvoxX916 || !editing || !device_uuid) return;
    const input = event.target;
    const file = input.files?.[0];
    const readId = ++akuvoxX916ImageReads.current[asset];
    setAkuvoxX916Images((prev) => ({ ...prev, [asset]: null }));
    setAkuvoxX916ImageStatus(null);
    if (!file) return;
    if (!/\.jpe?g$/i.test(file.name) || (file.type && file.type !== "image/jpeg")
        || file.size === 0 || file.size > 2 * 1024 * 1024) {
      input.value = "";
      setAkuvoxX916ImageStatus({ color: "danger", message: "Select a JPG/JPEG image of at most 2 MiB." });
      return;
    }
    try {
      const preview = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Could not read this image. Select it again."));
        reader.onabort = () => reject(new Error("Image reading was cancelled."));
        reader.readAsDataURL(file);
      });
      if (readId !== akuvoxX916ImageReads.current[asset]) return;
      setAkuvoxX916Images((prev) => ({ ...prev, [asset]: { file, preview } }));
    } catch (err) {
      if (readId !== akuvoxX916ImageReads.current[asset]) return;
      input.value = "";
      setAkuvoxX916ImageStatus({ color: "danger", message: err.message });
    }
  };

  const submitAkuvoxX916Image = async (asset, reset = false) => {
    if (isBusy || akuvoxX916ImageRequestActive.current || !isAkuvoxX916
        || !editing || !device_uuid || !akuvoxX916DisplayLoaded) return;
    const selected = akuvoxX916Images[asset];
    if (!reset && !selected?.file) return;
    const generation = akuvoxX916ImageGeneration.current;
    akuvoxX916ImageRequestActive.current = true;
    setAkuvoxX916ImageBusy(`${asset}:${reset ? "reset" : "upload"}`);
    setAkuvoxX916ImageStatus(null);
    try {
      const options = { method: reset ? "DELETE" : "POST" };
      if (!reset) {
        const form = new FormData();
        form.append("image", selected.file);
        options.body = form;
      }
      const response = await fetch(
        `${serverHost}/hrms/device_quick_setup/akuvox_x916_image/${device_uuid}/${asset}`, options,
      );
      const result = await response.json().catch(() => {
        throw new Error(`Server returned HTTP ${response.status}. Confirm the backend is updated.`);
      });
      if (generation !== akuvoxX916ImageGeneration.current) return;
      if (!response.ok || !result.success || result.submitted !== true) {
        throw new Error(result.error || "Device did not acknowledge the image request.");
      }
      if (reset) {
        ++akuvoxX916ImageReads.current[asset];
        setAkuvoxX916Images((prev) => ({ ...prev, [asset]: null }));
        if (akuvoxX916ImageInputs.current[asset]) akuvoxX916ImageInputs.current[asset].value = "";
      }
      setAkuvoxX916ImageStatus({ color: result.verified ? "success" : "warning", message: result.message });
    } catch (err) {
      if (generation === akuvoxX916ImageGeneration.current) {
        setAkuvoxX916ImageStatus({ color: "danger", message: err.message || "Image request failed." });
      }
    } finally {
      akuvoxX916ImageRequestActive.current = false;
      setAkuvoxX916ImageBusy("");
    }
  };

  const handleBackgroundUpload = async () => {
    if (!editing || !device_uuid || isBusy) return;

    if (!backgroundFile) {
      setBackgroundStatus({
        show: true,
        success: false,
        message: "Please choose an image first",
      });
      return;
    }

    if (!backgroundMeta.validDimensions) {
      setBackgroundStatus({
        show: true,
        success: false,
        message: `Image must be at least 800 x 1280. Current image is ${backgroundMeta.width} x ${backgroundMeta.height}.`,
      });
      return;
    }

    setBackgroundUploading(true);
    setBackgroundStatus({ show: false, success: false, message: "" });

    try {
      const formData = new FormData();
      formData.append("image", backgroundFile);
      formData.append("displayLayer", "default");

      const res = await fetch(`${serverHost}/hrms/device_background_config/${device_uuid}`, {
        method: "POST",
        body: formData,
      });

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to upload background image");
      }

      setBackgroundStatus({
        show: true,
        success: true,
        message: "Background image uploaded successfully",
      });

      const uploadedBackground = result.background_image || backgroundPreview;

      setBackgroundPreview(uploadedBackground);
      setSavedBackgroundPreview(uploadedBackground);
      setBackgroundFile(null);
      setBackgroundMeta({
        width: null,
        height: null,
        validDimensions: false,
      });

      clearBackgroundInputElement();
    } catch (err) {
      console.error("Background upload error:", err);
      setBackgroundStatus({
        show: true,
        success: false,
        message: err.message || "Failed to upload background image",
      });
    } finally {
      setBackgroundUploading(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!editing || !device_uuid || isBusy) return;

    setBackgroundRemoving(true);
    setBackgroundStatus({ show: false, success: false, message: "" });

    try {
      const res = await fetch(
        `${serverHost}/hrms/device_background_config/${device_uuid}`,
        { method: "DELETE" }
      );

      const result = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(result.error || "Failed to remove background");
      }

      setBackgroundStatus({
        show: true,
        success: true,
        message: "Background removed successfully",
      });

      setBackgroundPreview(null);
      setSavedBackgroundPreview(null);
      setBackgroundFile(null);
      setBackgroundMeta({
        width: null,
        height: null,
        validDimensions: false,
      });

      clearBackgroundInputElement();
    } catch (err) {
      console.error("Background remove error:", err);
      setBackgroundStatus({
        show: true,
        success: false,
        message: err.message || "Failed to remove background",
      });
    } finally {
      setBackgroundRemoving(false);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    const field = id.replace("input-", "");

    if (field === "device_model") {
      const nextSupportsDeviceButton = ["2N IP Verso", "2N IP Solo"].includes(value);
      const nextButtonCount = value === "2N IP Verso"
        ? clampQuickDialButtonCount(deviceData.quick_dial_button_count)
        : 1;

      setDeviceData((prev) => {
        const nextAssignments = nextSupportsDeviceButton
          ? buildButtonAssignmentState(
            nextButtonCount,
            prev.device_button_assignments || {}
          )
          : {};

        return {
          ...prev,
          device_model: value,
          device_firmware: value === AKUVOX_S539_MODEL
            ? "539.30.10.523"
            : (
              prev.device_model === AKUVOX_S539_MODEL
                ? "2.46"
                : prev.device_firmware || "2.46"
            ),
          quick_dial_button_count: nextButtonCount,
          device_button_assignments: nextAssignments,
          device_button: nextAssignments["1"] || [],
        };
      });

      return;
    }

    setDeviceData((prev) => ({ ...prev, [field]: value }));
  };

  const saveRfidBacklightIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (rfidBacklightEnabled === initialRfidBacklightEnabled) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/rfid_backlight/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: rfidBacklightEnabled }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update RFID backlight");
    }

    setInitialRfidBacklightEnabled(rfidBacklightEnabled);
  };

  const saveShowcaseModeIfChanged = async () => {
    if (!editing || !device_uuid || deviceData.device_model !== "2N IP Style") return;

    if (showcaseMode === initialShowcaseMode) {
      return;
    }

    const res = await fetch(
      `${serverHost}/hrms/device_quick_setup/showcase_mode/${device_uuid}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: showcaseMode }),
      }
    );

    const result = await res.json().catch(() => ({}));

    if (!res.ok) {
      throw new Error(result.error || "Failed to update showcase mode");
    }

    setInitialShowcaseMode(showcaseMode);
  };

  const isValidMacAddress = (value) => {
    if (!value || String(value).trim() === "") return true;

    const cleaned = String(value).trim();

    return /^([0-9a-fA-F]{2}[:-]){5}[0-9a-fA-F]{2}$/.test(cleaned)
      || /^[0-9a-fA-F]{12}$/.test(cleaned);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isBusy) return;

    const deviceName = String(deviceData.device_name || "").trim();
    const deviceModel = String(deviceData.device_model || "").trim();
    const deviceFirmware = String(deviceData.device_firmware || "").trim();
    const deviceIp = String(deviceData.device_ip || "").trim();
    const deviceMac = String(deviceData.device_mac || "").trim();

    if (!deviceName || !deviceModel || !deviceFirmware) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Please fill in all required fields.",
      });
      return;
    }

    if (!deviceIp && !deviceMac) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Please enter either an IP address or a MAC address.",
      });
      return;
    }

    if (deviceMac && !isValidMacAddress(deviceMac)) {
      setSaveStatus({
        show: true,
        success: false,
        message: "Please enter a valid MAC address, for example 7C:1E:B3:07:10:4A.",
      });
      return;
    }

    setSaving(true);
    setSaveStatus({ show: false, success: false, message: "" });
    setQuickSetupStatus({ show: false, success: false, message: "" });

    try {
      const site = getSite();
      const user = getUser();

      const payloadSupportsDeviceButton = ["2N IP Verso", "2N IP Solo"].includes(deviceModel);
      const payloadSupportsMultipleQuickDialButtons = deviceModel === "2N IP Verso";

      const quickDialButtonCount = payloadSupportsMultipleQuickDialButtons
        ? clampQuickDialButtonCount(deviceData.quick_dial_button_count)
        : 1;

      const deviceButtonAssignments = payloadSupportsDeviceButton
        ? buildButtonAssignmentState(
          quickDialButtonCount,
          deviceData.device_button_assignments || {}
        )
        : {};

      for (const [buttonPosition, tenantUuids] of Object.entries(deviceButtonAssignments)) {
        if ((tenantUuids || []).length > MAX_TENANTS_PER_DEVICE_BUTTON) {
          setSaveStatus({
            show: true,
            success: false,
            message: `Button ${buttonPosition} can have at most ${MAX_TENANTS_PER_DEVICE_BUTTON} tenants.`,
          });
          return;
        }
      }

      const payload = {
        ...deviceData,
        device_name: deviceName,
        device_model: deviceModel,
        device_firmware: deviceFirmware,
        site_uuid: deviceData.site_uuid || site.site_uuid,
        device_ip: deviceIp || null,
        device_mac: deviceMac || null,
        quick_dial_button_count: quickDialButtonCount,
        device_button_assignments: deviceButtonAssignments,

        // button 1 assignments still sent as device_button.
        device_button: payloadSupportsDeviceButton
          ? (deviceButtonAssignments["1"] || [])
          : [],

        extension_uuid: deviceData.extension_uuid || null,
      };

      const endpoint = editing
        ? `/hrms/update_device/${device_uuid}`
        : "/hrms/create_device";
      const method = editing ? "PUT" : "POST";

      const res = await fetch(`${serverHost}${endpoint}`, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ device: payload }),
      });

      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result.error || "Failed to save device");

      if (editing) {
        await saveDirectoryDisplaySettingsIfChanged();
      }

      // Apply display setting changes only when editing an existing supported device
      if (editing && deviceData.device_model === "2N IP Style") {
        await saveRfidBacklightIfChanged();
        await saveStyleDisplaySettingsIfChanged();
        await saveShowcaseModeIfChanged();
        await saveRootFolderDisplayIfChanged();
      }

      if (editing && deviceData.device_model === "2N IP Verso") {
        await saveVersoDisplaySettingsIfChanged();
      }

      if (editing && deviceModel === AKUVOX_S539_MODEL) {
        await saveAkuvoxDisplaySettingsIfChanged();
      }

      if (editing && deviceModel === AKUVOX_X916_MODEL) {
        await saveAkuvoxX916DisplaySettingsIfChanged();
      }
      setTimeout(() => {
        navigate("/admin/hrms/devices", { replace: true });
      }, 400);
    } catch (err) {
      console.error("Save device error:", err);

      setSaveStatus({
        show: true,
        success: false,
        message: err.message || "Error saving device",
      });

      setSaving(false);
    }
  };

  const loadEvents = async () => {
    if (!device_uuid) return;

    const requestId = Date.now();
    latestEventsRequestRef.current = requestId;

    try {
      setEventsLoading(true);
      setEventsError("");

      const subscribeResult = await subscribeToDeviceEvents(device_uuid, {
        filter: "CallStateChanged,CallSessionStateChanged,RegistrationStateChanged,UserAuthenticated,UserRejected,CodeEntered",
        include: "all",
        duration: 300,
      });

      const subscriptionId = subscribeResult.subscription_id;
      if (!subscriptionId) {
        throw new Error("No subscription id returned");
      }

      const pullResult = await pullDeviceEvents(device_uuid, {
        subscriptionId,
        timeout: 0,
        range: eventsRange,
        max_batches: 100,
      });

      if (latestEventsRequestRef.current !== requestId) {
        return;
      }

      const newEvents = pullResult.events || [];
      const seen = new Set();
      const deduped = [];

      for (const item of newEvents) {
        const key =
          item?.id ??
          `${item?.event}-${item?.device_local_datetime || item?.utc_time}-${JSON.stringify(item?.params || {})}`;

        if (seen.has(key)) continue;
        seen.add(key);
        deduped.push(item);
      }

      deduped.sort((a, b) => getEventSortTime(b) - getEventSortTime(a));
      setEvents(deduped);
    } catch (error) {
      if (latestEventsRequestRef.current !== requestId) {
        return;
      }

      setEventsError(error.message || "Failed to load events");
      setEvents([]);
    } finally {
      if (latestEventsRequestRef.current === requestId) {
        setEventsLoading(false);
      }
    }
  };

  const loadCallLogs = async () => {
    if (!device_uuid) return;

    const requestId = Date.now();
    latestCallLogsRequestRef.current = requestId;

    try {
      setCallLogsLoading(true);
      setCallLogsError("");

      const result = await getDeviceCallLogs(device_uuid, {
        limit: 300,
        range: callLogsRange,
      });

      if (latestCallLogsRequestRef.current !== requestId) return;
      setCallLogs(result.call_logs || []);
    } catch (error) {
      if (latestCallLogsRequestRef.current !== requestId) return;
      setCallLogsError(error.message || "Failed to load call logs");
      setCallLogs([]);
    } finally {
      if (latestCallLogsRequestRef.current === requestId) {
        setCallLogsLoading(false);
      }
    }
  };

  const parseDeviceLocalDateTime = (value) => {
    if (!value) return null;

    const match = String(value).trim().match(
      /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/
    );

    if (!match) return null;

    const [, year, month, day, hour, minute, second = "00"] = match;
    const sortValue = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second)
    ).getTime();

    return { year, month, day, hour, minute, second, sortValue };
  };

  const formatCallLogDate = (log) => {
    const deviceLocal = parseDeviceLocalDateTime(log?.device_local_datetime);
    if (deviceLocal) {
      return {
        date: `${deviceLocal.year}-${deviceLocal.month}-${deviceLocal.day}`,
        time: `${deviceLocal.hour}:${deviceLocal.minute}:${deviceLocal.second}`,
      };
    }

    const value = log?.date;
    if (!value) {
      return { date: "—", time: "" };
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return { date: value, time: "" };
    }

    const pad = (n) => String(n).padStart(2, "0");

    const date = `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())}`;
    const time = `${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;

    return { date, time };
  };

  const formatCallLogDuration = (value) => {
    if (value === null || value === undefined || value === "") return "—";

    const seconds = Number(value);
    if (Number.isNaN(seconds)) return String(value);

    if (seconds < 60) return `${seconds}s`;

    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;

    if (mins < 60) {
      return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
    }

    const hours = Math.floor(mins / 60);
    const remMins = mins % 60;

    if (remMins > 0) return `${hours}h ${remMins}m`;
    return `${hours}h`;
  };

  const formatCallLogName = (log) => {
    const candidates = [
      log?.name,
      log?.display_name,
      log?.remote_party,
      log?.sip_uri,
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      let text = String(candidate).trim();
      if (!text) continue;

      if (text.toLowerCase().startsWith("sip:")) {
        text = text.slice(4);
      }

      return text;
    }

    return "—";
  };

  const getCallTypeDisplay = (type) => {
    if (!type) return "Call";

    const normalized = String(type).toLowerCase();

    if (normalized === "incoming") return "Incoming";
    if (normalized === "outgoing") return "Outgoing";
    if (normalized === "missed") return "Missed";

    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const formatEventTime = (evt) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const deviceLocal = parseDeviceLocalDateTime(evt?.device_local_datetime);
    if (deviceLocal) {
      return `${deviceLocal.year}-${deviceLocal.month}-${deviceLocal.day} ${deviceLocal.hour}:${deviceLocal.minute}:${deviceLocal.second}`;
    }

    let dateObj = null;

    if (typeof evt?.utc_time === "number") {
      dateObj = new Date(evt.utc_time * 1000);
    } else if (typeof evt?.local_time_unix === "number") {
      dateObj = new Date(evt.local_time_unix * 1000);
    }

    if (!dateObj || Number.isNaN(dateObj.getTime())) {
      return "—";
    }

    const pad = (n) => String(n).padStart(2, "0");

    return `${pad(dateObj.getDate())} ${months[dateObj.getMonth()]} ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}:${pad(dateObj.getSeconds())}`;
  };

  const formatEventType = (evt) => {
    if (evt?.source === "akuvox_doorlog") {
      if (evt?.event === "UserAuthenticated") return "Access Granted";
      if (evt?.event === "UserRejected") return "Access Rejected";
    }

    return evt?.event || "—";
  };

  const formatEventDescription = (evt) => {
    const params = evt?.params || {};

    const parts = [];

    const orderedKeys = [
      "sipAccountType",
      "sipAccount",
      "account",
      "direction",
      "state",
      "reason",
      "peer",
      "session",
      "call",
      "originator",
      "info",
      "name",
      "uuid",
      "code",
      "valid",
    ];

    orderedKeys.forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
        parts.push(`${key}=${params[key]}`);
      }
    });

    Object.keys(params).forEach((key) => {
      if (orderedKeys.includes(key)) return;
      const value = params[key];
      if (value !== undefined && value !== null && value !== "") {
        parts.push(`${key}=${value}`);
      }
    });

    return parts.length ? parts.join(", ") : "—";
  };

  // The backend owns the date-range filter. Applying it again in the browser
  // caused device-local Akuvox times to be compared as if they were UTC.
  const filteredCallLogs = callLogs;

  const getEventSortTime = (evt) => {
    const deviceLocal = parseDeviceLocalDateTime(evt?.device_local_datetime);
    if (deviceLocal && !Number.isNaN(deviceLocal.sortValue)) {
      return deviceLocal.sortValue;
    }

    if (typeof evt?.utc_time === "number") return evt.utc_time * 1000;
    if (typeof evt?.local_time_unix === "number") return evt.local_time_unix * 1000;

    const fallback = evt?.id;
    if (typeof fallback === "number") return fallback;

    const parsed = Number(fallback);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const filteredEvents = [...events]
    .filter((evt) => {
      return eventFilter === "ALL" ? true : evt?.event === eventFilter;
    })
    .sort((a, b) => getEventSortTime(b) - getEventSortTime(a));

  const selectedAkuvoxResidentTheme =
    AKUVOX_RESIDENT_THEME_OPTIONS.find(
      (option) =>
        option.value ===
        String(
          akuvoxDisplaySettings.appearance?.residentTheme ?? "0"
        )
    ) || AKUVOX_RESIDENT_THEME_OPTIONS[0];

  const selectedAutoActivationValues =
    akuvoxDisplaySettings.appearance?.autoActivation || [];

  const everyFestivalSelected =
    AKUVOX_AUTO_ACTIVATION_THEME_VALUES.every(
      (value) => selectedAutoActivationValues.includes(value)
    );

  const selectedAutoActivationOptions =
    everyFestivalSelected
      ? [AKUVOX_AUTO_ACTIVATION_OPTIONS[0]]
      : AKUVOX_AUTO_ACTIVATION_OPTIONS.filter(
        (option) =>
          option.value !== "all" &&
          selectedAutoActivationValues.includes(option.value)
      );

  const selectedAkuvoxX916ResidentTheme = AKUVOX_X916_RESIDENT_THEME_OPTIONS.find(
    (option) => option.value === akuvoxX916DisplaySettings.appearance.residentTheme,
  ) || null;
  const selectedX916AutoActivationValues = akuvoxX916DisplaySettings.appearance.autoActivation || [];
  const selectedX916AutoActivationOptions = AKUVOX_AUTO_ACTIVATION_THEME_VALUES.every(
    (value) => selectedX916AutoActivationValues.includes(value),
  ) ? [AKUVOX_AUTO_ACTIVATION_OPTIONS[0]] : AKUVOX_AUTO_ACTIVATION_OPTIONS.filter(
    (option) => option.value !== "all" && selectedX916AutoActivationValues.includes(option.value),
  );

  const availableEventTypes = Array.from(
    new Set((events || []).map((evt) => evt?.event).filter(Boolean))
  );

  const isNewDevice = !editing;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <div className="text-center">
          <div className="spinner-border mb-3" role="status" />
          <div>Loading device…</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SimpleHeader name={isNewDevice ? "New Device" : "Edit Device"} parentName="HRMS" />
      <Container className="mt--6" fluid>
        {saveStatus.show && (
          <Alert
            color={saveStatus.success ? "success" : "danger"}
            isOpen={saveStatus.show}
            toggle={() => setSaveStatus({ ...saveStatus, show: false })}
          >
            {saveStatus.message}
          </Alert>
        )}
        <Row>
          <Col className="order-xl-1" xl="12">
            <Card>
              <CardHeader
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
              >
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3
                      className="mb-0"
                      style={{ color: isDark ? "#e2e8f0" : "#111" }}
                    >
                      {isNewDevice ? "Add New Device" : "Edit Device"}
                    </h3>
                  </Col>
                  <Col xs="4" className="d-flex justify-content-end">
                    <Button color="danger" onClick={() => navigate('/admin/hrms/devices')} size="sm" disabled={isBusy}>
                      Back to Enterphones
                    </Button>
                    <Button color="success" onClick={handleSave} size="sm" disabled={isBusy}>
                      Save
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Form>
                  {/* Device Information */}
                  <div className="pl-lg-4">
                    <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                      Device Information
                    </h6>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_name"
                            style={labelStyle}
                          >
                            Device Name <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-device_name"
                            value={deviceData.device_name}
                            disabled={isBusy}
                            onChange={handleInputChange}
                            placeholder="Enter device name"
                            type="text"
                            required
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_model"
                            style={labelStyle}
                          >
                            Device Model <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-device_model"
                            type="select"
                            disabled={isBusy}
                            value={deviceData.device_model}
                            onChange={handleInputChange}
                            required
                          >
                            <option value="">Select Model</option>
                            <option value="2N IP Style">2N IP Style</option>
                            <option value="2N IP Verso">2N IP Verso</option>
                            <option value="2N IP Solo">2N IP Solo</option>
                            <option value={AKUVOX_S539_MODEL}>{AKUVOX_S539_MODEL}</option>
                            <option value={AKUVOX_X916_MODEL}>{AKUVOX_X916_MODEL}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_ip"
                            style={labelStyle}
                          >
                            IP Address
                          </label>
                          <Input
                            id="input-device_ip"
                            value={deviceData.device_ip}
                            disabled={isBusy}
                            onChange={handleInputChange}
                            placeholder="Enter IP address"
                            type="text"
                          />
                          <small className="form-text" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                            Required if MAC address is blank. Last known IP can be updated by discovery.
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_mac"
                            style={labelStyle}
                          >
                            MAC Address
                          </label>
                          <Input
                            id="input-device_mac"
                            value={deviceData.device_mac}
                            disabled={isBusy}
                            onChange={handleInputChange}
                            placeholder="AA:BB:CC:DD:EE:FF"
                            type="text"
                          />
                          <small className="form-text" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                            Required if IP address is blank. Used to rediscover the device if its IP changes.
                          </small>
                        </FormGroup>
                      </Col>

                      <Col lg="4">
                        <FormGroup>
                          <label
                            className="form-control-label"
                            htmlFor="input-device_firmware"
                            style={labelStyle}
                          >
                            Firmware Version <span className="text-danger">*</span>
                          </label>
                          <Input
                            id="input-device_firmware"
                            type="select"
                            disabled={isBusy}
                            value={deviceData.device_firmware}
                            onChange={handleInputChange}
                            required
                          >
                            {isAkuvoxS539 ? (
                              <option value="539.30.10.523">539.30.10.523</option>
                            ) : (
                              <option value="2.46">2.46</option>
                            )}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <label className="form-control-label" style={labelStyle}>
                            Directories
                          </label>

                          <HusariaSelect
                            styles={selectStyles}
                            isDisabled={isBusy}
                            isMulti
                            options={directories.map(d => ({
                              value: d.dir_uuid,
                              label: d.dir_name
                            }))}
                            value={(deviceData.dir_uuids || [])
                              .map(id => {
                                const dir = directories.find(d => d.dir_uuid === id);
                                return dir
                                  ? {
                                    value: dir.dir_uuid,
                                    label: dir.dir_name
                                  }
                                  : null;
                              })
                              .filter(Boolean)}
                            onChange={(selectedOptions) => {
                              const selectedIds = (selectedOptions || []).map(opt => opt.value);
                              setDeviceData(prev => ({
                                ...prev,
                                dir_uuids: selectedIds
                              }));
                            }}
                            placeholder="Select directories..."
                          />
                        </FormGroup>
                      </Col>

                      {supportsDeviceButton && (
                        <Col lg="12">
                          <div
                            style={{
                              marginTop: "1rem",
                              padding: "1rem",
                              border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                              borderRadius: "0.5rem",
                              backgroundColor: isDark ? "#111827" : "#f8f9fa",
                            }}
                          >
                            <Row>
                              {supportsMultipleQuickDialButtons ? (
                                <>
                                  <Col lg="4">
                                    <FormGroup>
                                      <label
                                        className="form-control-label"
                                        htmlFor="input-quick_dial_button_count"
                                        style={labelStyle}
                                      >
                                        Number of Quick Dial Buttons
                                      </label>

                                      <Input
                                        id="input-quick_dial_button_count"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        disabled={isBusy}
                                        value={deviceData.quick_dial_button_count ?? ""}
                                        onChange={handleQuickDialButtonCountChange}
                                        onBlur={handleQuickDialButtonCountBlur}
                                        placeholder="Enter button count"
                                      />

                                      <small
                                        className="form-text"
                                        style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                                      >
                                        Assign tenants to each button position.
                                      </small>
                                    </FormGroup>
                                  </Col>

                                </>
                              ) : (
                                <Col lg="12">
                                  <div
                                    style={{
                                      color: isDark ? "#d1d5db" : "#495057",
                                      fontSize: "0.875rem",
                                      marginBottom: "1rem",
                                    }}
                                  >
                                    Assign tenants to Button 1 below.
                                  </div>
                                </Col>
                              )}
                            </Row>

                            {deviceData.dir_uuids.length === 0 ? (
                              <Alert color="info" className="mb-0">
                                Select at least one directory before assigning tenants to quick dial buttons.
                              </Alert>
                            ) : (
                              <Row>
                                {Array.from({
                                  length: getEffectiveQuickDialButtonCount(),
                                }).map((_, index) => {
                                  const buttonNumber = index + 1;

                                  return (
                                    <Col lg="6" key={`quick-dial-button-${buttonNumber}`}>
                                      <FormGroup>
                                        <label
                                          className="form-control-label"
                                          style={labelStyle}
                                        >
                                          Button {buttonNumber}
                                        </label>

                                        {(() => {
                                          const selectedTenants = getSelectedTenantsForButton(buttonNumber);

                                          return (
                                            <div
                                              style={{
                                                border: isDark ? "1px solid #374151" : "1px solid #dee2e6",
                                                borderRadius: "0.5rem",
                                                backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                                padding: "0.75rem",
                                              }}
                                            >
                                              <div className="d-flex justify-content-between align-items-center mb-2">
                                                <div
                                                  style={{
                                                    color: isDark ? "#d1d5db" : "#495057",
                                                    fontSize: "0.875rem",
                                                  }}
                                                >
                                                  {selectedTenants.length === 0
                                                    ? "No tenants assigned"
                                                    : `${selectedTenants.length} tenant${selectedTenants.length === 1 ? "" : "s"} assigned`}
                                                </div>

                                                <Button
                                                  color="primary"
                                                  size="sm"
                                                  type="button"
                                                  disabled={isBusy || directoryTenants.length === 0}
                                                  onClick={() => openTenantPicker(buttonNumber)}
                                                >
                                                  Add User
                                                </Button>
                                              </div>

                                              {selectedTenants.length > 0 && (
                                                <div style={{ overflowX: "auto" }}>
                                                  <Table
                                                    size="sm"
                                                    className="mb-0"
                                                    style={{
                                                      color: isDark ? "#f8f9fa" : "#111",
                                                      backgroundColor: "transparent",
                                                    }}
                                                  >
                                                    <thead>
                                                      <tr>
                                                        <th>Name</th>
                                                        <th>Virtual Number</th>
                                                        <th>Phone 1</th>
                                                        <th style={{ width: "80px" }} />
                                                      </tr>
                                                    </thead>
                                                    <tbody>
                                                      {selectedTenants.map((tenant) => (
                                                        <tr key={`button-${buttonNumber}-tenant-${tenant.tenant_uuid}`}>
                                                          <td>{getTenantName(tenant)}</td>
                                                          <td>{getTenantVirtualNumber(tenant) || "—"}</td>
                                                          <td>{getTenantPhone1(tenant) || "—"}</td>
                                                          <td className="text-right">
                                                            <Button
                                                              color="danger"
                                                              size="sm"
                                                              type="button"
                                                              disabled={isBusy}
                                                              onClick={() =>
                                                                removeTenantFromButton(buttonNumber, tenant.tenant_uuid)
                                                              }
                                                              style={
                                                                isBusy
                                                                  ? removeTenantButtonDisabledStyle
                                                                  : removeTenantButtonStyle
                                                              }
                                                            >
                                                              Remove
                                                            </Button>
                                                          </td>
                                                        </tr>
                                                      ))}
                                                    </tbody>
                                                  </Table>
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </FormGroup>
                                    </Col>
                                  );
                                })}
                              </Row>
                            )}
                          </div>
                        </Col>
                      )}
                    </Row>
                  </div>

                  {/* SIP Configuration */}
                  <div className="pl-lg-4 mt-4">
                    <Row>
                      <Col lg="12">
                        <h6 className="heading-small mb-4" style={sectionHeadingStyle}>
                          SIP Configuration
                        </h6>

                        <FormGroup className="mb-0">
                          <label
                            className="form-control-label"
                            htmlFor="input-extension_uuid"
                            style={labelStyle}
                          >
                            Extension
                          </label>
                          <HusariaSelect
                            inputId="input-extension_uuid"
                            styles={selectStyles}
                            options={extensionOptions.map((opt) => ({
                              value: opt.extension_uuid,
                              label: opt.label,
                            }))}
                            value={
                              extensionOptions
                                .map((opt) => ({
                                  value: opt.extension_uuid,
                                  label: opt.label,
                                }))
                                .find((opt) => opt.value === deviceData.extension_uuid) || null
                            }
                            onChange={(selected) =>
                              setDeviceData((prev) => ({
                                ...prev,
                                extension_uuid: selected?.value || "",
                              }))
                            }
                            isDisabled={isBusy}
                            isClearable
                            placeholder="Select extension..."
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  </div>

                  <hr className="my-4" />

                  {/* Display Settings */}
                  {editing && deviceData.device_model === "2N IP Style" && (
                    <div className="pl-lg-4 mt-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setDisplaySettingsOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: displaySettingsOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4
                                className="mb-1"
                                style={{
                                  color: isDark ? "#f8f9fa" : "#111",
                                  fontWeight: 600,
                                  fontSize: "1.35rem",
                                }}
                              >
                                Display Settings
                              </h4>
                              <div
                                style={{
                                  color: isDark ? "#9ca3af" : "#6c757d",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Screen display, code entry, touch sounds, background image, directory images, & visual options
                              </div>
                            </div>

                            {renderCollapseIcon(displaySettingsOpen)}
                          </div>
                        </CardHeader>

                        {displaySettingsOpen && (
                          <CardBody
                            style={{
                              backgroundColor: isDark ? "#111827" : "#ffffff",
                              paddingTop: "1.25rem",
                            }}
                          >
                            {quickSetupStatus.show &&
                              !isRootFolderUnsupportedMessage(quickSetupStatus.message) && (
                                <Alert
                                  color={quickSetupStatus.success ? "success" : "danger"}
                                  isOpen={quickSetupStatus.show}
                                  toggle={() =>
                                    setQuickSetupStatus((prev) => ({ ...prev, show: false }))
                                  }
                                  className="mb-4"
                                >
                                  {quickSetupStatus.message}
                                </Alert>
                              )}
                            {backgroundStatus.show && (
                              <Alert
                                color={backgroundStatus.success ? "success" : "danger"}
                                isOpen={backgroundStatus.show}
                                toggle={() =>
                                  setBackgroundStatus((prev) => ({ ...prev, show: false }))
                                }
                                className="mb-4"
                              >
                                {backgroundStatus.message}
                              </Alert>
                            )}

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Screen Options
                            </h6>

                            <Card
                              className="mb-4"
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                              }}
                            >
                              <CardBody>
                                {[
                                  {
                                    id: "rfid-backlight-toggle",
                                    label: "RFID Symbol Backlight",
                                    description: "Toggle the backlight for the RFID symbol.",
                                    checked: rfidBacklightEnabled,
                                    onChange: handleRfidBacklightToggle,
                                    loading: rfidBacklightLoading,
                                  },
                                  {
                                    id: "style-flexi-builder-toggle",
                                    label: "Flexi Builder / Custom GUI",
                                    description:
                                      "Switches the IP Style between the standard 2N display UI and the custom Flexi GUI layout. Only enable this if the device already has a valid Flexi layout configured.",
                                    checked: !!styleDisplaySettings.flexiBuilderEnabled,
                                    onChange: handleStyleDisplayToggle("flexiBuilderEnabled"),
                                    loading: styleDisplaySettingsLoading,
                                  },
                                  {
                                    id: "style-code-entry-button-toggle",
                                    label: "Code Entry Button",
                                    description: "Shows or hides the code entry button on the device display.",
                                    checked: !!styleDisplaySettings.codeEntryButton,
                                    onChange: handleStyleDisplayToggle("codeEntryButton"),
                                    loading: styleDisplaySettingsLoading,
                                  },
                                  {
                                    id: "style-touch-sounds-toggle",
                                    label: "Touch Sounds",
                                    description: "Enables or disables acoustic feedback when the display is touched.",
                                    checked: !!styleDisplaySettings.touchSounds,
                                    onChange: handleStyleDisplayToggle("touchSounds"),
                                    loading: styleDisplaySettingsLoading,
                                  },
                                ].map((item) => (
                                  <div
                                    key={item.id}
                                    className="d-flex justify-content-between align-items-start"
                                    style={{
                                      padding: "0.5rem 0",
                                      gap: "1rem",
                                      opacity: item.loading ? 0.7 : 1,
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div
                                        className="d-flex align-items-center"
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          fontWeight: 600,
                                          marginBottom: "0.2rem",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        <span>{item.label}</span>
                                        {item.loading && (
                                          <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                            Loading...
                                          </small>
                                        )}
                                      </div>

                                      <small className="form-text mt-0" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                        {item.description}
                                      </small>
                                    </div>

                                    <div className="custom-control custom-switch ml-3" style={{ paddingTop: "0.15rem" }}>
                                      <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id={item.id}
                                        checked={item.checked}
                                        onChange={item.onChange}
                                        disabled={isBusy || item.loading}
                                        style={{
                                          cursor: (isBusy || item.loading) ? "not-allowed" : "pointer",
                                        }}
                                      />

                                      <label
                                        className="custom-control-label"
                                        htmlFor={item.id}
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          cursor: (isBusy || item.loading) ? "not-allowed" : "pointer",
                                          minWidth: "32px",
                                        }}
                                      >
                                        {item.checked ? "On" : "Off"}
                                      </label>
                                    </div>
                                  </div>
                                ))}

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                    opacity: styleDisplaySettingsLoading ? 0.7 : 1,
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Code Entry Keypad Mode
                                    </div>

                                    {styleDisplaySettingsLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Normal keeps the keypad ordered. Scrambled randomizes the keypad layout.
                                  </small>

                                  <Input
                                    id="input-style-entry-keypad-mode"
                                    type="select"
                                    value={styleDisplaySettings.entryKeypadMode}
                                    onChange={handleStyleKeypadModeChange}
                                    disabled={isBusy || styleDisplaySettingsLoading}
                                    style={{
                                      cursor: (isBusy || styleDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="NORMAL">Normal</option>
                                    <option value="SCRAMBLED">Scrambled</option>
                                  </Input>
                                </div>

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                    opacity: rootFolderDisplayLoading ? 0.7 : 1,
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Directories Display
                                    </div>

                                    {rootFolderDisplayLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Choose how the directories are displayed on the device.
                                  </small>

                                  <Input
                                    id="input-root-folder-display"
                                    type="select"
                                    value={rootFolderDisplay}
                                    onChange={handleRootFolderDisplayChange}
                                    disabled={isBusy || rootFolderDisplayLoading}
                                    style={{
                                      cursor: (isBusy || rootFolderDisplayLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="CARDS_1_COLUMN">1 column</option>
                                    <option value="CARDS_2_COLUMNS">2 columns</option>
                                    <option value="PHONEBOOK">Phonebook</option>
                                  </Input>
                                </div>

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                    opacity: showcaseModeLoading ? 0.7 : 1,
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Showcase Mode
                                    </div>

                                    {showcaseModeLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Choose what the device shows on the screen in showcase mode.
                                  </small>

                                  <Input
                                    id="input-showcase-mode"
                                    type="select"
                                    value={showcaseMode}
                                    onChange={handleShowcaseModeChange}
                                    disabled={isBusy || showcaseModeLoading}
                                    style={{
                                      cursor: (isBusy || showcaseModeLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="OFF">Off</option>
                                    <option value="SLIDESHOW">Slideshow</option>
                                    <option value="LOGO">Logo</option>
                                    <option value="ADDRESS">Address</option>
                                    <option value="DATE_TIME">Date & Time</option>
                                  </Input>
                                </div>
                              </CardBody>
                            </Card>

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Background
                            </h6>

                            <Card
                              className="mb-4"
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                              }}
                            >
                              <CardBody>
                                <div
                                  style={{
                                    backgroundColor: isDark ? "#111827" : "#ffffff",
                                    border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                                    borderRadius: "0.75rem",
                                    padding: "1rem",
                                    boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
                                  }}
                                >
                                  <div
                                    className="d-flex justify-content-between align-items-start"
                                    style={{ gap: "1rem", marginBottom: "0.9rem" }}
                                  >
                                    <div style={{ minWidth: 0 }}>
                                      <div
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111827",
                                          fontWeight: 700,
                                          fontSize: "1rem",
                                          lineHeight: 1.2,
                                        }}
                                      >
                                        Background Image
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#9ca3af" : "#6b7280",
                                          fontSize: "0.82rem",
                                          marginTop: "0.2rem",
                                        }}
                                      >
                                        Main display background for the device
                                      </div>
                                    </div>

                                    {backgroundFile && (
                                      <div
                                        style={{
                                          color: backgroundMeta.validDimensions
                                            ? isDark ? "#86efac" : "#198754"
                                            : isDark ? "#fca5a5" : "#dc3545",
                                          backgroundColor: backgroundMeta.validDimensions
                                            ? isDark ? "rgba(22, 163, 74, 0.15)" : "#e8f5ee"
                                            : isDark ? "rgba(220, 38, 38, 0.15)" : "#fdecec",
                                          border: backgroundMeta.validDimensions
                                            ? isDark ? "1px solid rgba(134, 239, 172, 0.35)" : "1px solid #badbcc"
                                            : isDark ? "1px solid rgba(252, 165, 165, 0.35)" : "1px solid #f5c2c7",
                                          borderRadius: "999px",
                                          padding: "0.25rem 0.65rem",
                                          fontSize: "0.78rem",
                                          fontWeight: 700,
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {backgroundMeta.validDimensions ? "Ready to upload" : "Invalid size"}
                                      </div>
                                    )}
                                  </div>

                                  <div
                                    className="d-flex align-items-start"
                                    style={{ gap: "0.9rem" }}
                                  >
                                    <div
                                      style={{
                                        width: "96px",
                                        height: "154px",
                                        minWidth: "96px",
                                        borderRadius: "0.75rem",
                                        overflow: "hidden",
                                        backgroundColor: isDark ? "#1f2937" : "#f3f4f6",
                                        border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {backgroundPreview ? (
                                        <img
                                          src={backgroundPreview}
                                          alt="Background preview"
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6b7280",
                                            fontSize: "0.75rem",
                                            textAlign: "center",
                                            padding: "0.35rem",
                                            lineHeight: 1.2,
                                          }}
                                        >
                                          No image
                                        </div>
                                      )}
                                    </div>

                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <Input
                                        id="input-device-background"
                                        type="file"
                                        accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                                        disabled={isBusy || backgroundUploading}
                                        onChange={handleBackgroundFileChange}
                                        style={{ display: "none" }}
                                      />

                                      <div
                                        className="d-flex align-items-center flex-wrap"
                                        style={{ gap: "0.5rem", marginBottom: "1.25rem" }}
                                      >
                                        <Button
                                          type="button"
                                          size="sm"
                                          color={isDark ? undefined : "secondary"}
                                          disabled={isBusy || backgroundUploading}
                                          style={{
                                            backgroundColor: isDark ? "#374151" : undefined,
                                            borderColor: isDark ? "#4b5563" : undefined,
                                            color: isDark ? "#f8f9fa" : undefined,
                                            fontWeight: 600,
                                          }}
                                          onClick={() => document.getElementById("input-device-background")?.click()}
                                        >
                                          {backgroundPreview ? "Change Image" : "Choose Image"}
                                        </Button>

                                        {backgroundFile && (
                                          <Button
                                            type="button"
                                            size="sm"
                                            color={isDark ? undefined : "secondary"}
                                            disabled={isBusy || backgroundUploading}
                                            style={{
                                              backgroundColor: isDark ? "#374151" : undefined,
                                              borderColor: isDark ? "#4b5563" : undefined,
                                              color: isDark ? "#f8f9fa" : undefined,
                                              fontWeight: 600,
                                            }}
                                            onClick={clearPendingBackgroundSelection}
                                          >
                                            Cancel Selection
                                          </Button>
                                        )}

                                        {savedBackgroundPreview && !backgroundFile && (
                                          <Button
                                            type="button"
                                            size="sm"
                                            color="danger"
                                            disabled={isBusy}
                                            style={{ fontWeight: 600 }}
                                            onClick={handleRemoveBackground}
                                          >
                                            {backgroundRemoving ? "Removing..." : "Remove Image"}
                                          </Button>
                                        )}

                                        {backgroundFile && (
                                          <Button
                                            type="button"
                                            size="sm"
                                            color="primary"
                                            onClick={handleBackgroundUpload}
                                            disabled={isBusy || !backgroundMeta.validDimensions}
                                            style={{ fontWeight: 600 }}
                                          >
                                            {backgroundUploading ? "Uploading..." : "Upload Background"}
                                          </Button>
                                        )}
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#d1d5db" : "#374151",
                                          fontSize: "0.78rem",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          letterSpacing: "0.04em",
                                          marginBottom: "0.35rem",
                                        }}
                                      >
                                        Requirements
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#9ca3af" : "#6b7280",
                                          fontSize: "0.82rem",
                                          lineHeight: 1.45,
                                        }}
                                      >
                                        JPG or PNG. Minimum size: <strong>800 x 1280</strong>. Larger images will be reduced automatically.
                                      </div>

                                      {backgroundFile && (
                                        <div
                                          style={{
                                            marginTop: "0.75rem",
                                            padding: "0.75rem",
                                            borderRadius: "0.5rem",
                                            backgroundColor: isDark ? "#1f2937" : "#f9fafb",
                                            border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                                          }}
                                        >
                                          <div
                                            style={{
                                              color: isDark ? "#f8f9fa" : "#111827",
                                              fontSize: "0.86rem",
                                              fontWeight: 600,
                                              whiteSpace: "nowrap",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                            }}
                                            title={backgroundFile.name}
                                          >
                                            {backgroundFile.name}
                                          </div>

                                          {backgroundMeta.width && backgroundMeta.height && (
                                            <div
                                              style={{
                                                color: backgroundMeta.validDimensions
                                                  ? isDark ? "#86efac" : "#198754"
                                                  : isDark ? "#fca5a5" : "#dc3545",
                                                fontSize: "0.82rem",
                                                marginTop: "0.25rem",
                                              }}
                                            >
                                              {backgroundMeta.width} x {backgroundMeta.height}
                                              {backgroundMeta.validDimensions ? " ✓" : " — must be at least 800 x 1280"}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </CardBody>
                            </Card>

                            {(deviceData.dir_uuids || []).length > 0 && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Directory Display Settings
                                </h6>

                                <Card
                                  style={{
                                    backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                    border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardBody>
                                    <div
                                      style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                                        gap: "1rem",
                                      }}
                                    >
                                      {(deviceData.dir_uuids || []).map((dirUuid) => {
                                        const dir = directories.find((d) => d.dir_uuid === dirUuid);
                                        if (!dir) return null;

                                        const settings = directoryDisplaySettings[dirUuid] || {};
                                        const hasImage = !!settings.image;

                                        return (
                                          <div
                                            key={dirUuid}
                                            style={{
                                              backgroundColor: isDark ? "#111827" : "#ffffff",
                                              border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                                              borderRadius: "0.75rem",
                                              padding: "1rem",
                                              boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
                                            }}
                                          >
                                            <div
                                              className="d-flex justify-content-between align-items-start"
                                              style={{ gap: "1rem", marginBottom: "0.9rem" }}
                                            >
                                              <div style={{ minWidth: 0 }}>
                                                <div
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111827",
                                                    fontWeight: 700,
                                                    fontSize: "1rem",
                                                    lineHeight: 1.2,
                                                    wordBreak: "break-word",
                                                  }}
                                                >
                                                  {dir.dir_name}
                                                </div>

                                                <div
                                                  style={{
                                                    color: isDark ? "#9ca3af" : "#6b7280",
                                                    fontSize: "0.82rem",
                                                    marginTop: "0.2rem",
                                                  }}
                                                >
                                                  Directory image and display order
                                                </div>
                                              </div>

                                              <Input
                                                type="select"
                                                title="Priority controls the directory order on the device. Priority 1 appears at the top of the list. No Priority appears at the bottom."
                                                value={settings.priority ?? ""}
                                                disabled={isBusy}
                                                onChange={(e) => {
                                                  const value = e.target.value;

                                                  setDirectoryDisplaySettings((prev) => ({
                                                    ...prev,
                                                    [dirUuid]: {
                                                      ...(prev[dirUuid] || {}),
                                                      priority: value,
                                                    },
                                                  }));
                                                }}
                                                style={{
                                                  width: "165px",
                                                  minWidth: "165px",
                                                  minHeight: "42px",
                                                  height: "42px",
                                                  cursor: isBusy ? "not-allowed" : "pointer",
                                                  backgroundColor: isDark ? "#1f2937" : "#fff",
                                                  color: isDark ? "#f8f9fa" : "#111",
                                                  borderColor: isDark ? "#374151" : "#d1d5db",
                                                  fontSize: "0.9rem",
                                                  lineHeight: "1.4",
                                                  paddingTop: "0.5rem",
                                                  paddingBottom: "0.5rem",
                                                  paddingLeft: "0.75rem",
                                                  paddingRight: "2rem",
                                                }}
                                              >
                                                <option value="">No Priority</option>
                                                <option value="1">★ 1</option>
                                                <option value="2">★ 2</option>
                                                <option value="3">★ 3</option>
                                                <option value="4">★ 4</option>
                                                <option value="5">★ 5</option>
                                                <option value="6">★ 6</option>
                                                <option value="7">★ 7</option>
                                              </Input>
                                            </div>

                                            <div
                                              className="d-flex align-items-start"
                                              style={{ gap: "0.9rem" }}
                                            >
                                              <div
                                                style={{
                                                  width: "76px",
                                                  height: "76px",
                                                  minWidth: "76px",
                                                  borderRadius: "0.75rem",
                                                  overflow: "hidden",
                                                  backgroundColor: isDark ? "#1f2937" : "#f3f4f6",
                                                  border: isDark ? "1px solid #374151" : "1px solid #d1d5db",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                }}
                                              >
                                                {hasImage ? (
                                                  <img
                                                    src={settings.image}
                                                    alt={`${dir.dir_name} preview`}
                                                    style={{
                                                      width: "100%",
                                                      height: "100%",
                                                      objectFit: "cover",
                                                      display: "block",
                                                    }}
                                                  />
                                                ) : (
                                                  <div
                                                    style={{
                                                      color: isDark ? "#9ca3af" : "#6b7280",
                                                      fontSize: "0.75rem",
                                                      textAlign: "center",
                                                      padding: "0.35rem",
                                                      lineHeight: 1.2,
                                                    }}
                                                  >
                                                    No image
                                                  </div>
                                                )}
                                              </div>

                                              <div style={{ flex: 1, minWidth: 0 }}>
                                                <div
                                                  className="d-flex align-items-center flex-wrap"
                                                  style={{ gap: "0.5rem", marginBottom: "1.25rem" }}
                                                >
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    color={isDark ? undefined : "secondary"}
                                                    disabled={isBusy}
                                                    style={{
                                                      backgroundColor: isDark ? "#374151" : undefined,
                                                      borderColor: isDark ? "#4b5563" : undefined,
                                                      color: isDark ? "#f8f9fa" : undefined,
                                                      fontWeight: 600,
                                                    }}
                                                    onClick={() => {
                                                      const currentImage = settings.image;

                                                      if (currentImage) {
                                                        openDirectoryImageEditor(dirUuid, null, currentImage);
                                                      } else {
                                                        document.getElementById(`dir-image-${dirUuid}`)?.click();
                                                      }
                                                    }}
                                                  >
                                                    {hasImage ? "Edit Image" : "Choose Image"}
                                                  </Button>

                                                  {hasImage && (
                                                    <Button
                                                      type="button"
                                                      size="sm"
                                                      color="danger"
                                                      disabled={isBusy}
                                                      style={{ fontWeight: 600 }}
                                                      onClick={() => {
                                                        setDirectoryDisplaySettings((prev) => ({
                                                          ...prev,
                                                          [dirUuid]: {
                                                            ...(prev[dirUuid] || {}),
                                                            image: null,
                                                            imageName: "",
                                                            selectedColor: "",
                                                            removeImage: true,
                                                          },
                                                        }));
                                                      }}
                                                    >
                                                      Remove Image
                                                    </Button>
                                                  )}
                                                </div>

                                                <Input
                                                  id={`dir-image-${dirUuid}`}
                                                  type="file"
                                                  accept="image/png,image/jpeg"
                                                  disabled={isBusy}
                                                  onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    if (!file.type.startsWith("image/")) {
                                                      setQuickSetupStatus({
                                                        show: true,
                                                        success: false,
                                                        message: "Please select a valid image file.",
                                                      });
                                                      e.target.value = "";
                                                      return;
                                                    }

                                                    await openDirectoryImageEditor(dirUuid, file);
                                                    e.target.value = "";
                                                  }}
                                                  style={{ display: "none" }}
                                                />

                                                <div
                                                  style={{
                                                    color: isDark ? "#d1d5db" : "#374151",
                                                    fontSize: "0.78rem",
                                                    fontWeight: 700,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                    marginBottom: "0.45rem",
                                                  }}
                                                >
                                                  Use solid color
                                                </div>

                                                <div
                                                  className="d-flex align-items-center flex-wrap"
                                                  style={{ gap: "0.45rem" }}
                                                >
                                                  {DIRECTORY_IMAGE_COLOR_OPTIONS.map((option) => {
                                                    const isSelected = settings.selectedColor === option.hex;

                                                    return (
                                                      <button
                                                        key={`${dirUuid}-${option.hex}`}
                                                        type="button"
                                                        disabled={isBusy}
                                                        onClick={() => handleDirectoryColorSelect(dirUuid, option)}
                                                        title={option.label}
                                                        aria-label={`Use ${option.label} as directory image`}
                                                        style={{
                                                          width: "26px",
                                                          height: "26px",
                                                          borderRadius: "999px",
                                                          padding: 0,
                                                          backgroundColor: option.hex,
                                                          border:
                                                            option.hex === "#ffffff"
                                                              ? "1px solid #d1d5db"
                                                              : "1px solid rgba(0,0,0,0.25)",
                                                          outline: isSelected
                                                            ? `3px solid ${isDark ? "#93c5fd" : "#5e72e4"}`
                                                            : "none",
                                                          outlineOffset: "2px",
                                                          boxShadow: isSelected
                                                            ? "0 0 0 1px rgba(0,0,0,0.08)"
                                                            : "none",
                                                          cursor: isBusy ? "not-allowed" : "pointer",
                                                          opacity: isBusy ? 0.55 : 1,
                                                        }}
                                                      />
                                                    );
                                                  })}
                                                </div>

                                                {settings.imageName && (
                                                  <div
                                                    style={{
                                                      color: isDark ? "#9ca3af" : "#6b7280",
                                                      fontSize: "0.78rem",
                                                      marginTop: "0.55rem",
                                                      whiteSpace: "nowrap",
                                                      overflow: "hidden",
                                                      textOverflow: "ellipsis",
                                                    }}
                                                    title={settings.imageName}
                                                  >
                                                    {settings.imageName}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>

                                    {/*                                     <small
                                      className="form-text mt-3"
                                      style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                                    >
                                      Directory images and priorities are applied during the next enterphone sync.
                                    </small> */}
                                  </CardBody>
                                </Card>
                              </>
                            )}
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}

                  {/* Akuvox S539 Display Settings */}
                  {editing && isAkuvoxS539 && (
                    <div className="pl-lg-4 mt-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setDisplaySettingsOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: displaySettingsOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4
                                className="mb-1"
                                style={{
                                  color: isDark ? "#f8f9fa" : "#111",
                                  fontWeight: 600,
                                  fontSize: "1.35rem",
                                }}
                              >
                                Display Settings
                              </h4>
                              <div
                                style={{
                                  color: isDark ? "#9ca3af" : "#6c757d",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Homepage keys, PIN keypad, language, text, and device time
                              </div>
                            </div>

                            {renderCollapseIcon(displaySettingsOpen)}
                          </div>
                        </CardHeader>

                        {displaySettingsOpen && (
                          <CardBody
                            style={{
                              backgroundColor: isDark ? "#111827" : "#ffffff",
                              paddingTop: "1.25rem",
                              opacity: akuvoxDisplaySettingsLoading ? 0.72 : 1,
                            }}
                          >
                            {quickSetupStatus.show && (
                              <Alert
                                color={quickSetupStatus.success ? "success" : "danger"}
                                isOpen={quickSetupStatus.show}
                                toggle={() => setQuickSetupStatus(emptyQuickSetupStatus)}
                                className="mb-4"
                              >
                                {quickSetupStatus.message}
                              </Alert>
                            )}

                            {akuvoxDisplaySettingsLoading && (
                              <div className="d-flex align-items-center mb-3" style={{ gap: "0.5rem" }}>
                                <Spinner size="sm" />
                                <span style={{ color: isDark ? "#d1d5db" : "#6c757d" }}>
                                  Reading settings from the S539…
                                </span>
                              </div>
                            )}

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Theme &amp; PIN
                            </h6>

                            <Card className="mb-4" style={akuvoxSettingsCardStyle}>
                              <CardBody>
                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <label className="form-control-label" style={labelStyle}>
                                        Theme
                                      </label>
                                      <Input
                                        type="select"
                                        value={
                                          AKUVOX_THEME_OPTIONS.some(
                                            (option) =>
                                              option.value ===
                                              akuvoxDisplaySettings.theme
                                          )
                                            ? akuvoxDisplaySettings.theme
                                            : ""
                                        }
                                        onChange={(e) =>
                                          updateAkuvoxDisplayField("theme", e.target.value)
                                        }
                                        disabled={isBusy || akuvoxDisplaySettingsLoading}
                                      >
                                        <option value="" disabled>
                                          {akuvoxDisplaySettingsLoading
                                            ? "Reading from device…"
                                            : "No live theme loaded"}
                                        </option>
                                        {AKUVOX_THEME_OPTIONS.map((option) => (
                                          <option key={option.value} value={option.value}>
                                            {option.label}
                                          </option>
                                        ))}
                                      </Input>
                                    </FormGroup>
                                  </Col>

                                  <Col md="6">
                                    <FormGroup>
                                      <label className="form-control-label" style={labelStyle}>
                                        PIN Keypad Mode
                                      </label>
                                      <Input
                                        type="select"
                                        value={akuvoxDisplaySettings.keypadMode}
                                        onChange={(e) =>
                                          updateAkuvoxDisplayField(
                                            "keypadMode",
                                            e.target.value
                                          )
                                        }
                                        disabled={isBusy || akuvoxDisplaySettingsLoading}
                                      >
                                        <option value="0">Normal</option>
                                        <option value="1">Scrambled</option>
                                      </Input>
                                    </FormGroup>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>

                            {akuvoxDisplaySettings.theme === "2" && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Building Theme Homepage
                                </h6>

                                <Card className="mb-4" style={akuvoxSettingsCardStyle}>
                                  <CardBody>
                                    <Row>
                                      <Col md="6">
                                        <FormGroup>
                                          <label className="form-control-label" style={labelStyle}>
                                            Default Page
                                          </label>
                                          <Input
                                            type="select"
                                            value={akuvoxDisplaySettings.building.defaultPage}
                                            onChange={(e) =>
                                              updateAkuvoxSectionField(
                                                "building",
                                                "defaultPage",
                                                e.target.value
                                              )
                                            }
                                            disabled={isBusy || akuvoxDisplaySettingsLoading}
                                          >
                                            {!AKUVOX_BUILDING_DEFAULT_PAGE_OPTIONS.some(
                                              (option) =>
                                                option.value ===
                                                akuvoxDisplaySettings.building.defaultPage
                                            ) && (
                                                <option
                                                  value={
                                                    akuvoxDisplaySettings.building.defaultPage
                                                  }
                                                >
                                                  Device value (
                                                  {akuvoxDisplaySettings.building.defaultPage})
                                                </option>
                                              )}
                                            {AKUVOX_BUILDING_DEFAULT_PAGE_OPTIONS.map(
                                              (option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              )
                                            )}
                                          </Input>
                                        </FormGroup>
                                      </Col>
                                    </Row>

                                    <div className="table-responsive">
                                      <Table bordered className="align-items-center mb-0">
                                        <thead>
                                          <tr>
                                            <th style={{ width: "70px" }}>Key</th>
                                            <th>Label</th>
                                            <th style={{ width: "220px" }}>Type</th>
                                            <th>Value</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {akuvoxDisplaySettings.building.keys.map(
                                            (item) => (
                                              <tr key={`akuvox-building-key-${item.index}`}>
                                                <td className="text-center">{item.index}</td>
                                                <td>
                                                  <Input
                                                    value={item.label}
                                                    maxLength={63}
                                                    placeholder="Use device default"
                                                    onChange={(e) =>
                                                      updateAkuvoxBuildingKey(
                                                        item.index,
                                                        "label",
                                                        e.target.value
                                                      )
                                                    }
                                                    disabled={
                                                      isBusy ||
                                                      akuvoxDisplaySettingsLoading
                                                    }
                                                  />
                                                </td>
                                                <td>
                                                  <Input
                                                    type="select"
                                                    value={item.type}
                                                    onChange={(e) =>
                                                      updateAkuvoxBuildingKey(
                                                        item.index,
                                                        "type",
                                                        e.target.value
                                                      )
                                                    }
                                                    disabled={
                                                      isBusy ||
                                                      akuvoxDisplaySettingsLoading
                                                    }
                                                  >
                                                    {!AKUVOX_BUILDING_KEY_TYPE_OPTIONS.some(
                                                      (option) =>
                                                        option.value === item.type
                                                    ) && (
                                                        <option value={item.type}>
                                                          Device value ({item.type})
                                                        </option>
                                                      )}
                                                    {AKUVOX_BUILDING_KEY_TYPE_OPTIONS.map(
                                                      (option) => (
                                                        <option
                                                          key={option.value}
                                                          value={option.value}
                                                        >
                                                          {option.label}
                                                        </option>
                                                      )
                                                    )}
                                                  </Input>
                                                </td>
                                                <td>
                                                  <Input
                                                    value={item.value}
                                                    maxLength={255}
                                                    placeholder={
                                                      item.type === "3"
                                                        ? "SIP number or extension"
                                                        : "Not used for this type"
                                                    }
                                                    onChange={(e) =>
                                                      updateAkuvoxBuildingKey(
                                                        item.index,
                                                        "value",
                                                        e.target.value
                                                      )
                                                    }
                                                    disabled={
                                                      isBusy ||
                                                      akuvoxDisplaySettingsLoading ||
                                                      item.type !== "3"
                                                    }
                                                  />
                                                </td>
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </Table>
                                    </div>
                                  </CardBody>
                                </Card>
                              </>
                            )}

                            {akuvoxDisplaySettings.theme === "1" && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Villa Theme
                                </h6>

                                <Card className="mb-4" style={akuvoxSettingsCardStyle}>
                                  <CardBody>
                                    <Row>
                                      <Col md="6">
                                        <FormGroup>
                                          <label className="form-control-label" style={labelStyle}>
                                            Default Page
                                          </label>
                                          <Input
                                            type="select"
                                            value={akuvoxDisplaySettings.villa.defaultPage}
                                            onChange={(e) =>
                                              updateAkuvoxSectionField(
                                                "villa",
                                                "defaultPage",
                                                e.target.value
                                              )
                                            }
                                            disabled={isBusy || akuvoxDisplaySettingsLoading}
                                          >
                                            {AKUVOX_VILLA_DEFAULT_PAGE_OPTIONS.map(
                                              (option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              )
                                            )}
                                          </Input>
                                        </FormGroup>
                                      </Col>
                                    </Row>

                                    <div className="table-responsive">
                                      <Table bordered className="align-items-center mb-2">
                                        <thead>
                                          <tr>
                                            <th style={{ width: "70px" }}>Key</th>
                                            <th style={{ width: "230px" }}>Type</th>
                                            <th>Label</th>
                                            <th style={{ width: "170px" }}>Visibility</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {akuvoxDisplaySettings.villa.keys.map(
                                            (item) => {
                                              const isDefault =
                                                isAkuvoxVillaDefaultKey(item);

                                              return (
                                                <tr key={`akuvox-villa-key-${item.index}`}>
                                                  <td className="text-center">
                                                    {item.index}
                                                  </td>
                                                  <td>
                                                    <Input
                                                      type="select"
                                                      value={item.type}
                                                      onChange={(e) =>
                                                        updateAkuvoxVillaKey(
                                                          item.index,
                                                          "type",
                                                          e.target.value
                                                        )
                                                      }
                                                      disabled={
                                                        isBusy ||
                                                        akuvoxDisplaySettingsLoading
                                                      }
                                                    >
                                                      {AKUVOX_VILLA_KEY_TYPE_OPTIONS.map(
                                                        (option) => (
                                                          <option
                                                            key={option.value}
                                                            value={option.value}
                                                          >
                                                            {option.label}
                                                          </option>
                                                        )
                                                      )}
                                                    </Input>
                                                  </td>
                                                  <td>
                                                    <Input
                                                      value={item.label}
                                                      maxLength={63}
                                                      placeholder="Use device default"
                                                      onChange={(e) =>
                                                        updateAkuvoxVillaKey(
                                                          item.index,
                                                          "label",
                                                          e.target.value
                                                        )
                                                      }
                                                      disabled={
                                                        isBusy ||
                                                        akuvoxDisplaySettingsLoading
                                                      }
                                                    />
                                                  </td>
                                                  <td>
                                                    <Input
                                                      type="select"
                                                      value={item.visible ? "1" : "0"}
                                                      onChange={(e) =>
                                                        updateAkuvoxVillaKey(
                                                          item.index,
                                                          "visible",
                                                          e.target.value === "1"
                                                        )
                                                      }
                                                      disabled={
                                                        isBusy ||
                                                        akuvoxDisplaySettingsLoading
                                                      }
                                                    >
                                                      <option value="1">Show</option>
                                                      <option value="0" disabled={isDefault}>
                                                        Hide
                                                      </option>
                                                    </Input>
                                                  </td>
                                                </tr>
                                              );
                                            }
                                          )}
                                        </tbody>
                                      </Table>
                                    </div>

                                    <small
                                      className="form-text"
                                      style={{
                                        color: isDark ? "#9ca3af" : "#6c757d",
                                      }}
                                    >
                                      A page cannot be hidden while it is selected as
                                      the Villa default page.
                                    </small>
                                  </CardBody>
                                </Card>
                              </>
                            )}

                            {["3", "4"].includes(akuvoxDisplaySettings.theme) && (
                              <Alert color="info">
                                HRMS can select this theme, but its specialized
                                controls remain managed through the Akuvox interface.
                              </Alert>
                            )}

                            {["1", "2"].includes(akuvoxDisplaySettings.theme) && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Text Prompts
                                </h6>

                                <Card className="mb-4" style={akuvoxSettingsCardStyle}>
                                  <CardBody>
                                    <Row>
                                      {[
                                        {
                                          field: "call",
                                          label: "Call Interface",
                                          placeholder:
                                            "Please enter the apartment number (e.g.101)",
                                        },
                                        {
                                          field: "pin",
                                          label: "PIN Interface",
                                          placeholder: "Please enter your PIN",
                                        },
                                        {
                                          field: "directory",
                                          label: "Directory Interface",
                                          placeholder: "Tap here to search",
                                        },
                                      ].map((prompt) => (
                                        <Col md="4" key={prompt.field}>
                                          <FormGroup className="mb-0">
                                            <label
                                              className="form-control-label"
                                              style={labelStyle}
                                            >
                                              {prompt.label}
                                            </label>
                                            <Input
                                              value={
                                                akuvoxDisplaySettings.prompts[
                                                prompt.field
                                                ]
                                              }
                                              maxLength={63}
                                              placeholder={prompt.placeholder}
                                              onChange={(e) =>
                                                updateAkuvoxPrompt(
                                                  prompt.field,
                                                  e.target.value
                                                )
                                              }
                                              disabled={
                                                isBusy ||
                                                akuvoxDisplaySettingsLoading
                                              }
                                            />
                                          </FormGroup>
                                        </Col>
                                      ))}
                                    </Row>
                                  </CardBody>
                                </Card>
                              </>
                            )}

                            {akuvoxDisplaySettings.theme === "2" && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Speed Dial
                                </h6>

                                <Card className="mb-4" style={akuvoxSettingsCardStyle}>
                                  <CardBody>
                                    <Row>
                                      <Col md="4">
                                        {renderAkuvoxToggle({
                                          id: "akuvox-speed-dial-group-toggle",
                                          label: "Speed Dial Group",
                                          description: "Use a configured speed-dial group.",
                                          checked:
                                            akuvoxDisplaySettings.speedDial
                                              .groupEnabled,
                                          onChange: (checked) =>
                                            updateAkuvoxSpeedDialField(
                                              "groupEnabled",
                                              checked
                                            ),
                                          disabled:
                                            isBusy ||
                                            akuvoxDisplaySettingsLoading,
                                        })}
                                      </Col>

                                      <Col md="4">
                                        <FormGroup>
                                          <label className="form-control-label" style={labelStyle}>
                                            Group Number
                                          </label>
                                          <Input
                                            value={
                                              akuvoxDisplaySettings.speedDial.groupNumber
                                            }
                                            onChange={(e) =>
                                              updateAkuvoxSpeedDialField(
                                                "groupNumber",
                                                e.target.value
                                              )
                                            }
                                            disabled={
                                              isBusy ||
                                              akuvoxDisplaySettingsLoading ||
                                              !akuvoxDisplaySettings.speedDial.groupEnabled
                                            }
                                          />
                                        </FormGroup>
                                      </Col>

                                      <Col md="4">
                                        {renderAkuvoxToggle({
                                          id: "akuvox-dial-out-forward-toggle",
                                          label: "Dial Out Forward",
                                          description: "Forward calls using the selected account.",
                                          checked:
                                            akuvoxDisplaySettings.speedDial
                                              .dialOutForward,
                                          onChange: (checked) =>
                                            updateAkuvoxSpeedDialField(
                                              "dialOutForward",
                                              checked
                                            ),
                                          disabled:
                                            isBusy ||
                                            akuvoxDisplaySettingsLoading,
                                        })}
                                      </Col>
                                    </Row>

                                    <Row>
                                      <Col md="4">
                                        <FormGroup>
                                          <label className="form-control-label" style={labelStyle}>
                                            Account
                                          </label>
                                          <Input
                                            type="select"
                                            value={akuvoxDisplaySettings.speedDial.account}
                                            onChange={(e) =>
                                              updateAkuvoxSpeedDialField(
                                                "account",
                                                e.target.value
                                              )
                                            }
                                            disabled={isBusy || akuvoxDisplaySettingsLoading}
                                          >
                                            {AKUVOX_SPEED_DIAL_ACCOUNT_OPTIONS.map(
                                              (option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              )
                                            )}
                                          </Input>
                                        </FormGroup>
                                      </Col>

                                      <Col md="4">
                                        <FormGroup>
                                          <label className="form-control-label" style={labelStyle}>
                                            Open Relay
                                          </label>
                                          <Input
                                            type="select"
                                            value={akuvoxDisplaySettings.speedDial.openRelay}
                                            onChange={(e) =>
                                              updateAkuvoxSpeedDialField(
                                                "openRelay",
                                                e.target.value
                                              )
                                            }
                                            disabled={isBusy || akuvoxDisplaySettingsLoading}
                                          >
                                            {AKUVOX_SPEED_DIAL_RELAY_OPTIONS.map(
                                              (option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              )
                                            )}
                                          </Input>
                                        </FormGroup>
                                      </Col>

                                      <Col md="4">
                                        {renderAkuvoxToggle({
                                          id: "akuvox-http-action-toggle",
                                          label: "HTTP Action",
                                          description: "Call the configured URL for this action.",
                                          checked:
                                            akuvoxDisplaySettings.speedDial
                                              .httpEnabled,
                                          onChange: (checked) =>
                                            updateAkuvoxSpeedDialField(
                                              "httpEnabled",
                                              checked
                                            ),
                                          disabled:
                                            isBusy ||
                                            akuvoxDisplaySettingsLoading,
                                        })}
                                      </Col>
                                    </Row>

                                    <FormGroup className="mb-0">
                                      <label className="form-control-label" style={labelStyle}>
                                        HTTP URL
                                      </label>
                                      <Input
                                        value={akuvoxDisplaySettings.speedDial.httpUrl}
                                        maxLength={512}
                                        onChange={(e) =>
                                          updateAkuvoxSpeedDialField(
                                            "httpUrl",
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          isBusy ||
                                          akuvoxDisplaySettingsLoading ||
                                          !akuvoxDisplaySettings.speedDial.httpEnabled
                                        }
                                      />
                                    </FormGroup>
                                  </CardBody>
                                </Card>

                                <h6
                                  className="heading-small mb-3"
                                  style={sectionHeadingStyle}
                                >
                                  Appearance
                                </h6>

                                <Card className="mb-4" style={akuvoxSettingsCardStyle}>
                                  <CardBody>
                                    <Row>
                                      <Col lg="5">
                                        <FormGroup>
                                          <label
                                            className="form-control-label"
                                            style={labelStyle}
                                          >
                                            Resident Theme
                                          </label>

                                          <Input
                                            type="select"
                                            value={
                                              akuvoxDisplaySettings.appearance
                                                ?.residentTheme ?? "0"
                                            }
                                            onChange={(event) =>
                                              updateAkuvoxAppearanceField(
                                                "residentTheme",
                                                event.target.value
                                              )
                                            }
                                            disabled={
                                              isBusy || akuvoxDisplaySettingsLoading
                                            }
                                          >
                                            {AKUVOX_RESIDENT_THEME_OPTIONS.map(
                                              (option) => (
                                                <option
                                                  key={option.value}
                                                  value={option.value}
                                                >
                                                  {option.label}
                                                </option>
                                              )
                                            )}
                                          </Input>
                                        </FormGroup>

                                        <FormGroup className="mb-0">
                                          <label
                                            className="form-control-label"
                                            style={labelStyle}
                                          >
                                            Auto Activation
                                          </label>

                                          <HusariaSelect
                                            styles={selectStyles}
                                            isMulti
                                            isClearable
                                            closeMenuOnSelect={false}
                                            options={AKUVOX_AUTO_ACTIVATION_OPTIONS}
                                            value={selectedAutoActivationOptions}
                                            onChange={(selectedOptions) => {
                                              const selectedValues = (
                                                selectedOptions || []
                                              ).map((option) => option.value);

                                              updateAkuvoxAppearanceField(
                                                "autoActivation",
                                                selectedValues.includes("all")
                                                  ? [
                                                    ...AKUVOX_AUTO_ACTIVATION_THEME_VALUES,
                                                  ]
                                                  : selectedValues
                                              );
                                            }}
                                            isDisabled={
                                              isBusy || akuvoxDisplaySettingsLoading
                                            }
                                            placeholder="No automatic festival themes"
                                          />

                                          <small
                                            className="form-text mt-2"
                                            style={{
                                              color: isDark ? "#9ca3af" : "#6c757d",
                                            }}
                                          >
                                            Theme will automatically activate on the specified festival days.
                                            <br></br>
                                            <br></br>
                                            If no days are selected, the selected resident theme will continue to be used.
                                          </small>
                                        </FormGroup>
                                      </Col>

                                      <Col
                                        lg="7"
                                        className="mt-4 mt-lg-0 d-flex justify-content-center align-items-start"
                                      >
                                        <div className="text-center">
                                          <img
                                            key={selectedAkuvoxResidentTheme.value}
                                            src={selectedAkuvoxResidentTheme.previewSrc}
                                            alt={`${selectedAkuvoxResidentTheme.label} Akuvox theme preview`}
                                            loading="lazy"
                                            style={{
                                              display: "block",
                                              width: "100%",
                                              maxWidth: "225px",
                                              height: "auto",
                                              borderRadius: "4px",
                                              boxShadow: isDark
                                                ? "0 6px 18px rgba(0, 0, 0, 0.3)"
                                                : "0 6px 18px rgba(15, 23, 42, 0.14)",
                                            }}
                                          />

                                          <small
                                            className="d-block mt-2"
                                            style={{
                                              color: isDark ? "#9ca3af" : "#6c757d",
                                            }}
                                          >
                                            {selectedAkuvoxResidentTheme.label} preview
                                          </small>
                                        </div>
                                      </Col>
                                    </Row>
                                  </CardBody>
                                </Card>
                              </>
                            )}

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Language
                            </h6>

                            <Card
                              className="mb-4"
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                              }}
                            >
                              <CardBody>
                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <label className="form-control-label" htmlFor="input-akuvox-device-language" style={labelStyle}>
                                        LCD Language
                                      </label>
                                      <Input
                                        id="input-akuvox-device-language"
                                        type="select"
                                        value={akuvoxDisplaySettings.deviceLanguage}
                                        onChange={(e) => updateAkuvoxDisplayField("deviceLanguage", e.target.value)}
                                        disabled={isBusy || akuvoxDisplaySettingsLoading}
                                      >
                                        {!AKUVOX_LANGUAGE_OPTIONS.some((option) => option.value === akuvoxDisplaySettings.deviceLanguage) && (
                                          <option value={akuvoxDisplaySettings.deviceLanguage}>
                                            Device value ({akuvoxDisplaySettings.deviceLanguage})
                                          </option>
                                        )}
                                        {AKUVOX_LANGUAGE_OPTIONS.map((option) => (
                                          <option key={option.value} value={option.value}>{option.label}</option>
                                        ))}
                                      </Input>
                                    </FormGroup>
                                  </Col>

                                  {akuvoxDisplaySettings.theme === "2" && (
                                    <Col md="6">
                                      {renderAkuvoxToggle({
                                        id: "akuvox-language-selector-toggle",
                                        label: "Homepage Language Selector",
                                        description:
                                          "Allow visitors to choose from the four homepage languages.",
                                        checked:
                                          akuvoxDisplaySettings
                                            .languageSelectorVisible,
                                        onChange: (checked) =>
                                          updateAkuvoxDisplayField(
                                            "languageSelectorVisible",
                                            checked
                                          ),
                                        disabled:
                                          isBusy ||
                                          akuvoxDisplaySettingsLoading,
                                        enabledText: "Shown",
                                        disabledText: "Hidden",
                                      })}
                                    </Col>
                                  )}
                                </Row>

                                {akuvoxDisplaySettings.theme === "2" && (
                                  <Row className="mt-2">
                                    {akuvoxDisplaySettings.languages.map((language, index) => (
                                      <Col md="3" sm="6" key={`akuvox-language-${index + 1}`}>
                                        <FormGroup className="mb-0">
                                          <label className="form-control-label" style={labelStyle}>
                                            {index + 1}{["st", "nd", "rd", "th"][index]} Language
                                          </label>
                                          <Input
                                            type="select"
                                            value={language}
                                            onChange={(e) => updateAkuvoxDisplayLanguage(index, e.target.value)}
                                            disabled={isBusy || akuvoxDisplaySettingsLoading}
                                          >
                                            {!AKUVOX_LANGUAGE_OPTIONS.some((option) => option.value === language) && (
                                              <option value={language}>Device value ({language})</option>
                                            )}
                                            {AKUVOX_LANGUAGE_OPTIONS.map((option) => (
                                              <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                          </Input>
                                        </FormGroup>
                                      </Col>
                                    ))}
                                  </Row>
                                )}
                              </CardBody>
                            </Card>

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Date &amp; Time
                            </h6>

                            <Card
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                              }}
                            >
                              <CardBody>
                                <Row>
                                  <Col md="12">
                                    {renderAkuvoxToggle({
                                      id: "akuvox-auto-time-toggle",
                                      label: "Automatic Date and Time",
                                      description:
                                        "Synchronize the device clock with the configured NTP server.",
                                      checked: akuvoxDisplaySettings.time.autoTime,
                                      onChange: (checked) =>
                                        updateAkuvoxTimeField("autoTime", checked),
                                      disabled:
                                        isBusy || akuvoxDisplaySettingsLoading,
                                      enabledText: "Automatic",
                                      disabledText: "Manual",
                                    })}
                                  </Col>
                                </Row>

                                {!akuvoxDisplaySettings.time.autoTime && (
                                  <Row>
                                    <Col md="6">
                                      <FormGroup>
                                        <label
                                          className="form-control-label"
                                          htmlFor="input-akuvox-manual-date"
                                          style={labelStyle}
                                        >
                                          Date
                                        </label>

                                        <Input
                                          id="input-akuvox-manual-date"
                                          type="date"
                                          value={toDateTimeLocalValue(
                                            akuvoxDisplaySettings.time.localTime
                                          ).slice(0, 10)}
                                          onChange={(e) =>
                                            updateAkuvoxTimeField(
                                              "localTime",
                                              mergeAkuvoxLocalDateTime(
                                                akuvoxDisplaySettings.time.localTime,
                                                { date: e.target.value }
                                              )
                                            )
                                          }
                                          disabled={
                                            isBusy || akuvoxDisplaySettingsLoading
                                          }
                                          required
                                        />
                                      </FormGroup>
                                    </Col>

                                    <Col md="6">
                                      <FormGroup>
                                        <label
                                          className="form-control-label"
                                          htmlFor="input-akuvox-manual-time"
                                          style={labelStyle}
                                        >
                                          Time
                                        </label>

                                        <Input
                                          id="input-akuvox-manual-time"
                                          type="time"
                                          step="60"
                                          value={toDateTimeLocalValue(
                                            akuvoxDisplaySettings.time.localTime
                                          ).slice(11, 16)}
                                          onChange={(e) =>
                                            updateAkuvoxTimeField(
                                              "localTime",
                                              mergeAkuvoxLocalDateTime(
                                                akuvoxDisplaySettings.time.localTime,
                                                { time: e.target.value }
                                              )
                                            )
                                          }
                                          disabled={
                                            isBusy || akuvoxDisplaySettingsLoading
                                          }
                                          required
                                        />
                                      </FormGroup>
                                    </Col>
                                  </Row>
                                )}

                                <Row>
                                  <Col md="6">
                                    <FormGroup>
                                      <label
                                        className="form-control-label"
                                        htmlFor="input-akuvox-timezone"
                                        style={labelStyle}
                                      >
                                        Time Zone
                                      </label>

                                      {akuvoxDisplaySettings.time
                                        .timezoneOptions.length > 0 ? (
                                        <Input
                                          id="input-akuvox-timezone"
                                          type="select"
                                          value={akuvoxDisplaySettings.time.timezoneId}
                                          onChange={(e) =>
                                            updateAkuvoxTimeField(
                                              "timezoneId",
                                              e.target.value
                                            )
                                          }
                                          disabled={
                                            isBusy || akuvoxDisplaySettingsLoading
                                          }
                                        >
                                          <option value="" disabled>
                                            Select Pacific or Eastern Time
                                          </option>

                                          {akuvoxDisplaySettings.time.timezoneOptions.map(
                                            (option) => (
                                              <option
                                                key={option.value}
                                                value={option.value}
                                              >
                                                {option.label}
                                              </option>
                                            )
                                          )}
                                        </Input>
                                      ) : (
                                        <Input
                                          id="input-akuvox-timezone"
                                          value={
                                            akuvoxDisplaySettings.time
                                              .timezoneLabel ||
                                            akuvoxDisplaySettings.time.timezoneId
                                          }
                                          readOnly
                                          disabled
                                        />
                                      )}
                                    </FormGroup>
                                  </Col>

                                  <Col md="6">
                                    <FormGroup>
                                      <label
                                        className="form-control-label"
                                        htmlFor="input-akuvox-ntp"
                                        style={labelStyle}
                                      >
                                        NTP Server
                                      </label>

                                      <Input
                                        id="input-akuvox-ntp"
                                        value={
                                          akuvoxDisplaySettings.time.ntpServer1
                                        }
                                        maxLength={255}
                                        onChange={(e) =>
                                          updateAkuvoxTimeField(
                                            "ntpServer1",
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          isBusy || akuvoxDisplaySettingsLoading
                                        }
                                      />
                                    </FormGroup>
                                  </Col>
                                </Row>

                                <Row>
                                  <Col md="6">
                                    <FormGroup className="mb-0">
                                      <label
                                        className="form-control-label"
                                        htmlFor="input-akuvox-date-format"
                                        style={labelStyle}
                                      >
                                        Date Format
                                      </label>

                                      <Input
                                        id="input-akuvox-date-format"
                                        type="select"
                                        value={
                                          akuvoxDisplaySettings.time.dateFormat
                                        }
                                        onChange={(e) =>
                                          updateAkuvoxTimeField(
                                            "dateFormat",
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          isBusy || akuvoxDisplaySettingsLoading
                                        }
                                      >
                                        <option value="0">YYYY-MM-DD</option>
                                        <option value="2">DD-MM-YYYY</option>
                                        <option value="6">MM-DD-YYYY</option>
                                      </Input>
                                    </FormGroup>
                                  </Col>

                                  <Col md="6">
                                    <FormGroup className="mb-0">
                                      <label
                                        className="form-control-label"
                                        htmlFor="input-akuvox-time-format"
                                        style={labelStyle}
                                      >
                                        Time Format
                                      </label>

                                      <Input
                                        id="input-akuvox-time-format"
                                        type="select"
                                        value={
                                          akuvoxDisplaySettings.time.timeFormat
                                        }
                                        onChange={(e) =>
                                          updateAkuvoxTimeField(
                                            "timeFormat",
                                            e.target.value
                                          )
                                        }
                                        disabled={
                                          isBusy || akuvoxDisplaySettingsLoading
                                        }
                                      >
                                        <option value="0">24 hour</option>
                                        <option value="1">12 hour</option>
                                      </Input>
                                    </FormGroup>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}

                  {/* 2N IP Verso Display Settings */}
                  {editing && deviceData.device_model === "2N IP Verso" && (
                    <div className="pl-lg-4 mt-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setDisplaySettingsOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: displaySettingsOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4
                                className="mb-1"
                                style={{
                                  color: isDark ? "#f8f9fa" : "#111",
                                  fontWeight: 600,
                                  fontSize: "1.35rem",
                                }}
                              >
                                Display Settings
                              </h4>
                              <div
                                style={{
                                  color: isDark ? "#9ca3af" : "#6c757d",
                                  fontSize: "0.95rem",
                                }}
                              >
                                Phonebook display, code entry, keypad mode, icon labels, & directory priorities
                              </div>
                            </div>

                            {renderCollapseIcon(displaySettingsOpen)}
                          </div>
                        </CardHeader>

                        {displaySettingsOpen && (
                          <CardBody
                            style={{
                              backgroundColor: isDark ? "#111827" : "#ffffff",
                              paddingTop: "1.25rem",
                            }}
                          >
                            {quickSetupStatus.show && (
                              <Alert
                                color={quickSetupStatus.success ? "success" : "danger"}
                                isOpen={quickSetupStatus.show}
                                toggle={() =>
                                  setQuickSetupStatus((prev) => ({ ...prev, show: false }))
                                }
                                className="mb-4"
                              >
                                {quickSetupStatus.message}
                              </Alert>
                            )}

                            <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                              Screen Options
                            </h6>

                            <Card
                              className="mb-4"
                              style={{
                                backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                boxShadow: "none",
                                opacity: versoDisplaySettingsLoading ? 0.7 : 1,
                              }}
                            >
                              <CardBody>
                                {[
                                  {
                                    id: "verso-phonebook-displayed-toggle",
                                    label: "Phonebook Displayed",
                                    description: "Controls whether the phonebook is shown on the device display.",
                                    field: "phonebookDisplayed",
                                  },
                                  {
                                    id: "verso-code-entry-button-toggle",
                                    label: "Code Entry Button",
                                    description: "Shows or hides the code entry button on the device display.",
                                    field: "codeEntryButton",
                                  },
                                  {
                                    id: "verso-prefer-icons-to-text-toggle",
                                    label: "Prefer Icons to Text",
                                    description: "Uses icons instead of text labels where the Verso display supports it.",
                                    field: "preferIconsToText",
                                  },
                                ].map((item) => (
                                  <div
                                    key={item.id}
                                    className="d-flex justify-content-between align-items-start"
                                    style={{
                                      padding: "0.5rem 0",
                                      gap: "1rem",
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <div
                                        className="d-flex align-items-center"
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          fontWeight: 600,
                                          marginBottom: "0.2rem",
                                          gap: "0.5rem",
                                        }}
                                      >
                                        <span>{item.label}</span>
                                        {versoDisplaySettingsLoading && (
                                          <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                            Loading...
                                          </small>
                                        )}
                                      </div>

                                      <small className="form-text mt-0" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                        {item.description}
                                      </small>
                                    </div>

                                    <div className="custom-control custom-switch ml-3" style={{ paddingTop: "0.15rem" }}>
                                      <input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id={item.id}
                                        checked={!!versoDisplaySettings[item.field]}
                                        onChange={handleVersoDisplayToggle(item.field)}
                                        disabled={isBusy || versoDisplaySettingsLoading}
                                        style={{
                                          cursor: (isBusy || versoDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                        }}
                                      />

                                      <label
                                        className="custom-control-label"
                                        htmlFor={item.id}
                                        style={{
                                          color: isDark ? "#f8f9fa" : "#111",
                                          cursor: (isBusy || versoDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                          minWidth: "32px",
                                        }}
                                      >
                                        {versoDisplaySettings[item.field] ? "On" : "Off"}
                                      </label>
                                    </div>
                                  </div>
                                ))}

                                <hr style={{ borderColor: isDark ? "#374151" : "#e9ecef", margin: "0.75rem 0" }} />

                                <div
                                  style={{
                                    padding: "0.25rem 0 0.5rem 0",
                                  }}
                                >
                                  <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.35rem" }}>
                                    <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                      Code Entry Keypad Mode
                                    </div>

                                    {versoDisplaySettingsLoading && (
                                      <small style={{ color: isDark ? "#9ca3af" : "#6c757d", fontWeight: 500 }}>
                                        Loading...
                                      </small>
                                    )}
                                  </div>

                                  <small className="form-text mt-0 mb-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                    Normal keeps the keypad ordered. Scrambled randomizes the keypad layout.
                                  </small>

                                  <Input
                                    id="input-verso-entry-keypad-mode"
                                    type="select"
                                    value={versoDisplaySettings.entryKeypadMode}
                                    onChange={handleVersoKeypadModeChange}
                                    disabled={isBusy || versoDisplaySettingsLoading}
                                    style={{
                                      cursor: (isBusy || versoDisplaySettingsLoading) ? "not-allowed" : "pointer",
                                      backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                      color: isDark ? "#f8f9fa" : "#111",
                                      borderColor: isDark ? "#3a3a3a" : undefined,
                                    }}
                                  >
                                    <option value="NORMAL">Normal</option>
                                    <option value="SCRAMBLED">Scrambled</option>
                                  </Input>
                                </div>
                              </CardBody>
                            </Card>

                            {(deviceData.dir_uuids || []).length > 0 && (
                              <>
                                <h6 className="heading-small mb-3" style={sectionHeadingStyle}>
                                  Directory Display Priorities
                                </h6>

                                <Card
                                  style={{
                                    backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
                                    border: isDark ? "1px solid #374151" : "1px solid #e9ecef",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardBody>
                                    {(deviceData.dir_uuids || []).map((dirUuid) => {
                                      const dir = directories.find((d) => d.dir_uuid === dirUuid);
                                      if (!dir) return null;

                                      return (
                                        <Row key={dirUuid} className="align-items-center mb-3" style={{ gap: "1rem" }}>
                                          <Col lg="auto" style={{ flex: "0 0 auto", minWidth: "220px", maxWidth: "320px" }}>
                                            <div style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600 }}>
                                              {dir.dir_name}
                                            </div>
                                          </Col>

                                          <Col lg="auto">
                                            <Input
                                              type="select"
                                              title="Priority controls the directory order on the device. Priority 1 appears at the top of the list. No Priority appears at the bottom."
                                              value={directoryDisplaySettings[dirUuid]?.priority ?? ""}
                                              disabled={isBusy}
                                              onChange={(e) => {
                                                const value = e.target.value;

                                                setDirectoryDisplaySettings((prev) => ({
                                                  ...prev,
                                                  [dirUuid]: {
                                                    ...(prev[dirUuid] || {}),
                                                    priority: value,
                                                  },
                                                }));
                                              }}
                                              style={{
                                                cursor: isBusy ? "not-allowed" : "pointer",
                                                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                                color: isDark ? "#f8f9fa" : "#111",
                                                borderColor: isDark ? "#3a3a3a" : undefined,
                                                width: "165px",
                                                minWidth: "165px",
                                                minHeight: "42px",
                                                height: "42px",
                                                fontSize: "0.9rem",
                                                lineHeight: "1.4",
                                                paddingTop: "0.5rem",
                                                paddingBottom: "0.5rem",
                                                paddingLeft: "0.75rem",
                                                paddingRight: "2rem",
                                              }}
                                            >
                                              <option value="">No Priority</option>
                                              <option value="1">★ 1</option>
                                              <option value="2">★ 2</option>
                                              <option value="3">★ 3</option>
                                              <option value="4">★ 4</option>
                                              <option value="5">★ 5</option>
                                              <option value="6">★ 6</option>
                                              <option value="7">★ 7</option>
                                            </Input>
                                          </Col>
                                        </Row>
                                      );
                                    })}

                                    <small className="form-text" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                      Directory priorities are applied during the next enterphone sync.
                                    </small>
                                  </CardBody>
                                </Card>
                              </>
                            )}
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}

                  {editing && isAkuvoxX916 && (
                    <div className="pl-lg-4 mt-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setX916DisplaySettingsOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: x916DisplaySettingsOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4 className="mb-1" style={{ color: isDark ? "#f8f9fa" : "#111", fontWeight: 600, fontSize: "1.35rem" }}>
                                Display Settings
                              </h4>
                              <div style={{ color: isDark ? "#9ca3af" : "#6c757d", fontSize: "0.95rem" }}>
                                Theme and homepage keys
                                {akuvoxX916DisplayDirty ? " · Unsaved changes" : ""}
                              </div>
                            </div>
                            {renderCollapseIcon(x916DisplaySettingsOpen)}
                          </div>
                        </CardHeader>
                        {x916DisplaySettingsOpen && (
                          <CardBody style={{ backgroundColor: isDark ? "#111827" : "#fff", opacity: akuvoxX916DisplayLoading ? 0.72 : 1 }}>
                            {akuvoxX916DisplayLoading && <div className="mb-3"><Spinner size="sm" /> Reading current settings from the X916…</div>}
                            {akuvoxX916DisplayLoadError && (
                              <Alert color="danger">Could not load live X916 settings: {akuvoxX916DisplayLoadError}</Alert>
                            )}
                            <Button type="button" size="sm" color="secondary" className="mb-3" disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplayDirty} onClick={loadAkuvoxX916DisplaySettings}>Refresh from device</Button>
                            {!akuvoxX916DisplayLoaded && !akuvoxX916DisplayLoading && !akuvoxX916DisplayLoadError && (
                              <div className="text-muted">Live settings will appear after they are read from the X916.</div>
                            )}
                            {akuvoxX916DisplayLoaded && <>
                              <Row>
                                <Col lg="4">
                                  <FormGroup>
                                    <label className="form-control-label" style={labelStyle}>Theme</label>
                                    <Input type="select" disabled={isBusy || akuvoxX916DisplayLoading} value={akuvoxX916DisplaySettings.theme} onChange={(e) => updateAkuvoxX916Field("theme", e.target.value)}>
                                      {AKUVOX_X916_THEME_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                    </Input>
                                  </FormGroup>
                                </Col>
                              </Row>
                              {["2", "5"].includes(akuvoxX916DisplaySettings.theme) && <>
                                <h5 className="mt-3 mb-2">Homepage Keys</h5>
                                <Row>
                                  {akuvoxX916DisplaySettings.theme === "2" && <Col lg="6">
                                    <FormGroup>
                                      <label className="form-control-label" style={labelStyle}>Display Type</label>
                                      <Input type="select" disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.displayType === ""} value={akuvoxX916DisplaySettings.displayType} onChange={(e) => updateAkuvoxX916Field("displayType", e.target.value)}>
                                        {akuvoxX916DisplaySettings.displayType === "" && <option value="">Not returned by device</option>}
                                        {akuvoxX916DisplaySettings.displayType !== "" && !AKUVOX_X916_DISPLAY_TYPE_OPTIONS.some((option) => option.value === akuvoxX916DisplaySettings.displayType) && <option value={akuvoxX916DisplaySettings.displayType}>Device value {akuvoxX916DisplaySettings.displayType}</option>}
                                        {AKUVOX_X916_DISPLAY_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                      </Input>
                                    </FormGroup>
                                  </Col>}
                                  {akuvoxX916DisplaySettings.theme === "5" && <Col lg="6">
                                    <FormGroup>
                                      <label className="form-control-label" style={labelStyle}>Full Screen Display of Directory</label>
                                      <div style={{ display: "flex", alignItems: "center", gap: "12px", minHeight: "46px" }}>
                                        <button type="button" role="switch" id="x916-directory-full-screen"
                                          aria-label="Full Screen Display of Directory"
                                          aria-checked={akuvoxX916DisplaySettings.fullScreenDirectory === true}
                                          disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.fullScreenDirectory == null}
                                          onClick={() => updateAkuvoxX916Field("fullScreenDirectory", !akuvoxX916DisplaySettings.fullScreenDirectory)}
                                          style={{ width: "42px", height: "24px", flexShrink: 0, padding: "3px", border: 0, borderRadius: "24px",
                                            background: akuvoxX916DisplaySettings.fullScreenDirectory ? "#5e72e4" : "#adb5bd",
                                            cursor: "pointer", opacity: (isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.fullScreenDirectory == null) ? 0.5 : 1 }}>
                                          <span style={{ display: "block", width: "18px", height: "18px", borderRadius: "50%", background: "#fff",
                                            transform: akuvoxX916DisplaySettings.fullScreenDirectory ? "translateX(18px)" : "translateX(0)", transition: "transform 0.15s ease" }} />
                                        </button>
                                        <span style={{ ...labelStyle, fontSize: "14px" }}>
                                          {akuvoxX916DisplaySettings.fullScreenDirectory == null ? "Unavailable" : akuvoxX916DisplaySettings.fullScreenDirectory ? "Enabled" : "Disabled"}
                                        </span>
                                      </div>
                                    </FormGroup>
                                  </Col>}
                                  {(akuvoxX916DisplaySettings.theme !== "5" || akuvoxX916DisplaySettings.fullScreenDirectory !== true) && <Col lg="6">
                                    <FormGroup>
                                      <label className="form-control-label" style={labelStyle}>Number of Keys</label>
                                      <Input type="select" disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.numberOfKeys === ""} value={akuvoxX916DisplaySettings.numberOfKeys} onChange={(e) => updateAkuvoxX916Field("numberOfKeys", e.target.value)}>
                                        {akuvoxX916DisplaySettings.numberOfKeys === "" && <option value="">Not returned by device</option>}
                                        {Array.from({ length: 17 }, (_, count) => <option key={count} value={String(count)}>{count}</option>)}
                                      </Input>
                                    </FormGroup>
                                  </Col>}
                                </Row>
                                {(akuvoxX916DisplaySettings.theme !== "5" || akuvoxX916DisplaySettings.fullScreenDirectory !== true) && <>
                                <div className="table-responsive">
                                  <Table size="sm" bordered>
                                    <thead><tr><th>ID</th><th>Name</th><th>Type</th><th>Value</th></tr></thead>
                                    <tbody>
                                      {akuvoxX916DisplaySettings.keys
                                        .filter((key) => akuvoxX916DisplaySettings.numberOfKeys === "" || key.index <= Number(akuvoxX916DisplaySettings.numberOfKeys))
                                        .map((key) => {
                                          const valueEditable = key.type === "5";
                                          return (
                                            <tr key={key.index}>
                                              <td>{key.index}</td>
                                              <td><Input type="text" maxLength="64" disabled={isBusy || akuvoxX916DisplayLoading} value={key.label} onChange={(e) => updateAkuvoxX916Key(key.index, "label", e.target.value)} /></td>
                                              <td><Input type="select" disabled={isBusy || akuvoxX916DisplayLoading} value={key.type} onChange={(e) => updateAkuvoxX916Key(key.index, "type", e.target.value)}>
                                                {!AKUVOX_X916_KEY_TYPE_OPTIONS.some((option) => option.value === key.type) && <option value={key.type}>Device value {key.type}</option>}
                                                {AKUVOX_X916_KEY_TYPE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                              </Input></td>
                                              <td><Input type="text" maxLength="128" disabled={isBusy || akuvoxX916DisplayLoading || !valueEditable} value={key.value} onChange={(e) => updateAkuvoxX916Key(key.index, "value", e.target.value)} /></td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </Table>
                                </div>
                                {akuvoxX916DisplaySettings.numberOfKeys === "0" && <small className="form-text text-muted">No homepage keys are displayed.</small>}
                                {akuvoxX916DisplaySettings.numberOfKeys === "" && <small className="form-text text-muted">The device did not return a key count. The rows shown are the editable fields returned by its API.</small>}
                                {akuvoxX916DisplaySettings.numberOfKeys !== "" && Number(akuvoxX916DisplaySettings.numberOfKeys) > akuvoxX916DisplaySettings.keys.filter((key) => key.index <= Number(akuvoxX916DisplaySettings.numberOfKeys)).length && <small className="form-text text-muted">The device has not returned editable fields for every selected key. Only returned rows can be edited here.</small>}
                                <small className="form-text text-muted">Value is editable for Speed Dial keys.</small>
                                </>}
                              </>}
                              {akuvoxX916DisplaySettings.theme === "2" && <>
                                <h5 className="mt-4 mb-3">Appearance</h5>
                                <Card className="mb-4" style={akuvoxSettingsCardStyle}>
                                  <CardBody><Row>
                                    <Col lg="5">
                                      <FormGroup>
                                        <label className="form-control-label" htmlFor="x916-resident-theme" style={labelStyle}>Resident Theme</label>
                                        <Input type="select" id="x916-resident-theme"
                                          value={akuvoxX916DisplaySettings.appearance.residentTheme ?? ""}
                                          disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.appearance.mode == null || akuvoxX916DisplaySettings.appearance.residentTheme == null}
                                          onChange={(event) => updateAkuvoxX916NestedField("appearance", "residentTheme", event.target.value)}>
                                          {akuvoxX916DisplaySettings.appearance.residentTheme == null && <option value="">Not returned by device</option>}
                                          {akuvoxX916DisplaySettings.appearance.residentTheme != null && !selectedAkuvoxX916ResidentTheme && <option value={akuvoxX916DisplaySettings.appearance.residentTheme}>Device value {akuvoxX916DisplaySettings.appearance.residentTheme}</option>}
                                          {AKUVOX_X916_RESIDENT_THEME_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                        </Input>
                                      </FormGroup>
                                      <FormGroup className="mb-0">
                                        <label className="form-control-label" htmlFor="x916-auto-activation" style={labelStyle}>Auto Activation</label>
                                        <HusariaSelect inputId="x916-auto-activation" styles={selectStyles} isMulti isClearable closeMenuOnSelect={false}
                                          options={AKUVOX_AUTO_ACTIVATION_OPTIONS} value={selectedX916AutoActivationOptions}
                                          isDisabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.appearance.mode == null || akuvoxX916DisplaySettings.appearance.autoActivation == null}
                                          placeholder={akuvoxX916DisplaySettings.appearance.autoActivation == null ? "Not returned by device" : "No automatic festival themes"}
                                          onChange={(selectedOptions) => {
                                            const values = (selectedOptions || []).map((option) => option.value);
                                            updateAkuvoxX916NestedField("appearance", "autoActivation", values.includes("all") ? [...AKUVOX_AUTO_ACTIVATION_THEME_VALUES] : values);
                                          }} />
                                        <small className="form-text mt-2" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                          Theme will automatically activate on the specified festival days.
                                          <br /><br />If no days are selected, the selected resident theme will continue to be used.
                                        </small>
                                      </FormGroup>
                                    </Col>
                                    <Col lg="7" className="mt-4 mt-lg-0 d-flex justify-content-center align-items-start">
                                      {selectedAkuvoxX916ResidentTheme && <div className="text-center">
                                        <img key={selectedAkuvoxX916ResidentTheme.value} src={selectedAkuvoxX916ResidentTheme.previewSrc}
                                          alt={`${selectedAkuvoxX916ResidentTheme.label} X916 theme preview`} loading="lazy"
                                          style={{ display: "block", width: "100%", maxWidth: "420px", height: "auto", borderRadius: "4px",
                                            boxShadow: isDark ? "0 6px 18px rgba(0, 0, 0, 0.3)" : "0 6px 18px rgba(15, 23, 42, 0.14)" }} />
                                        <small className="d-block mt-2" style={labelStyle}>{selectedAkuvoxX916ResidentTheme.label}</small>
                                      </div>}
                                    </Col>
                                  </Row></CardBody>
                                </Card>
                              </>}
                              {akuvoxX916DisplaySettings.theme === "1" && <div className="text-muted">The VMS theme has no homepage key controls.</div>}
                              <h5 className="mt-4 mb-3">Speed Dial Setting</h5>
                              <Row>
                                <Col lg="6"><FormGroup>
                                  <label className="form-control-label" style={labelStyle}>Speed Dial (Cloud)</label>
                                  <Input type="text" readOnly value={akuvoxX916DisplaySettings.speedDial.cloudNumber ?? ""}
                                    placeholder={akuvoxX916DisplaySettings.speedDial.cloudNumber == null ? "Not returned by config API" : ""} />
                                  <small className="form-text text-muted">Managed by Akuvox SmartPlus Cloud.</small>
                                </FormGroup></Col>
                                <Col lg="6"><FormGroup>
                                  <label className="form-control-label" style={labelStyle}>Group</label>
                                  <Input type="select"
                                    value={akuvoxX916DisplaySettings.speedDial.groupEnabled ? akuvoxX916DisplaySettings.speedDial.groupNumber ?? "" : ""}
                                    disabled={isBusy || akuvoxX916DisplayLoading || !!akuvoxX916DisplaySettings.groupsError || akuvoxX916DisplaySettings.speedDial.groupEnabled == null || akuvoxX916DisplaySettings.speedDial.groupNumber == null}
                                    onChange={(e) => {
                                      updateAkuvoxX916NestedField("speedDial", "groupEnabled", e.target.value !== "");
                                      updateAkuvoxX916NestedField("speedDial", "groupNumber", e.target.value || "0");
                                    }}>
                                    <option value="">{akuvoxX916DisplaySettings.speedDial.groupEnabled == null || akuvoxX916DisplaySettings.speedDial.groupNumber == null ? "Not returned by device" : "Disabled"}</option>
                                    {akuvoxX916DisplaySettings.speedDial.groupEnabled && akuvoxX916DisplaySettings.speedDial.groupNumber && !akuvoxX916DisplaySettings.groups.includes(akuvoxX916DisplaySettings.speedDial.groupNumber) &&
                                      <option value={akuvoxX916DisplaySettings.speedDial.groupNumber}>{akuvoxX916DisplaySettings.speedDial.groupNumber} (current device value)</option>}
                                    {akuvoxX916DisplaySettings.groups.filter(Boolean).map((name) => <option key={name} value={name}>{name}</option>)}
                                  </Input>
                                  {akuvoxX916DisplaySettings.groupsError && <small className="form-text text-danger">{akuvoxX916DisplaySettings.groupsError}</small>}
                                </FormGroup></Col>
                              </Row>
                              <h5 className="mt-4 mb-3">Keypad Display Mode Of PIN Interface</h5>
                              <Row>
                                {[
                                  { label: "Mode", value: akuvoxX916DisplaySettings.keypadMode,
                                    options: ["Normal", "Scrambled"], onChange: (value) => updateAkuvoxX916Field("keypadMode", value) },
                                ].map((field) => <Col lg="6" key={field.label}><FormGroup>
                                  <label className="form-control-label" style={labelStyle}>{field.label}</label>
                                  <Input type="select" value={field.value ?? ""} disabled={isBusy || akuvoxX916DisplayLoading || field.value == null} onChange={(e) => field.onChange(e.target.value)}>
                                    {field.value == null && <option value="">Not returned by device</option>}
                                    {field.value != null && !field.options.some((_, index) => String(index) === field.value) && <option value={field.value}>Device value {field.value}</option>}
                                    {field.options.map((label, index) => <option key={index} value={String(index)}>{label}</option>)}
                                  </Input>
                                </FormGroup></Col>)}
                              </Row>
                              <h5 className="mt-4 mb-3">Reception Action In Intercom</h5>
                              <Row>
                                {[
                                  { label: "Dial Type", field: "dialType", options: ["Default", "Account 1", "Account 2"] },
                                  { label: "Execute Relay", field: "openRelay", options: ["None", "Relay A", "Relay B", "Relay C", "Relay D"] },
                                ].map(({ label, field, options }) => <Col lg="6" key={field}><FormGroup>
                                  <label className="form-control-label" style={labelStyle}>{label}</label>
                                  <Input type="select" value={akuvoxX916DisplaySettings.reception[field] ?? ""}
                                    disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.reception[field] == null}
                                    onChange={(e) => updateAkuvoxX916NestedField("reception", field, e.target.value)}>
                                    {akuvoxX916DisplaySettings.reception[field] == null && <option value="">Not returned by device</option>}
                                    {akuvoxX916DisplaySettings.reception[field] != null && !options.some((_, index) => String(index) === akuvoxX916DisplaySettings.reception[field]) && <option value={akuvoxX916DisplaySettings.reception[field]}>Device value {akuvoxX916DisplaySettings.reception[field]}</option>}
                                    {options.map((text, index) => <option key={index} value={String(index)}>{text}</option>)}
                                  </Input>
                                </FormGroup></Col>)}
                              </Row>
                              <Row>
                                <Col lg="6"><FormGroup>
                                  <label className="form-control-label" style={labelStyle}>Action To Execute</label>
                                  <label style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "10px", minHeight: "46px", margin: 0 }}>
                                    <input type="checkbox" checked={akuvoxX916DisplaySettings.reception.httpEnabled === true}
                                      disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.reception.httpEnabled == null}
                                      onChange={(e) => updateAkuvoxX916NestedField("reception", "httpEnabled", e.target.checked)} /> HTTP
                                  </label>
                                  {akuvoxX916DisplaySettings.reception.httpEnabled == null && <small className="form-text text-muted">Not returned by device</small>}
                                </FormGroup></Col>
                                <Col lg="6"><FormGroup>
                                  <label className="form-control-label" style={labelStyle}>HTTP URL</label>
                                  <Input type="text" maxLength="512" value={akuvoxX916DisplaySettings.reception.httpUrl ?? ""}
                                    placeholder={akuvoxX916DisplaySettings.reception.httpUrl == null ? "Not returned by device" : ""}
                                    disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.reception.httpUrl == null}
                                    onChange={(e) => updateAkuvoxX916NestedField("reception", "httpUrl", e.target.value)} />
                                </FormGroup></Col>
                              </Row>
                              <h5 className="mt-4 mb-3">Language Setting Of The Intercom Theme</h5>
                              <Row><Col lg="6"><FormGroup>
                                <label className="form-control-label" style={labelStyle}>Language</label>
                                <Input type="select" value={akuvoxX916DisplaySettings.languageSelectorVisible == null ? "" : akuvoxX916DisplaySettings.languageSelectorVisible ? "1" : "0"}
                                  disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings.languageSelectorVisible == null}
                                  onChange={(e) => updateAkuvoxX916Field("languageSelectorVisible", e.target.value === "1")}>
                                  {akuvoxX916DisplaySettings.languageSelectorVisible == null && <option value="">Not returned by device</option>}
                                  <option value="0">Invisible</option><option value="1">Visible</option>
                                </Input>
                              </FormGroup></Col></Row>
                              <Row>
                                {akuvoxX916DisplaySettings.languages.map((language, index) => <Col lg="3" md="6" key={index}><FormGroup>
                                  <label className="form-control-label" style={labelStyle}>Language{index + 1}</label>
                                  <Input type="select" value={language ?? ""} disabled={isBusy || akuvoxX916DisplayLoading || language == null}
                                    onChange={(e) => updateAkuvoxX916Language(index, e.target.value)}>
                                    {language == null && <option value="">Not returned by device</option>}
                                    {language != null && !AKUVOX_X916_LANGUAGE_OPTIONS.some((option) => option.value === language) && <option value={language}>Device value {language}</option>}
                                    {AKUVOX_X916_LANGUAGE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                  </Input>
                                </FormGroup></Col>)}
                              </Row>

                              <h5 className="mt-4 mb-3">Homepage Customization (VMS Theme)</h5>
                              <small className="d-block mb-3" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                                Upload and Reset apply immediately. Text changes use Save. JPG/JPEG only, up to 2 MiB per file (application limit).
                              </small>
                              {akuvoxX916ImageStatus && <Alert color={akuvoxX916ImageStatus.color}>
                                {akuvoxX916ImageStatus.message}
                              </Alert>}
                              <Row>
                                {[{ asset: "background", label: "Background Image" }, { asset: "logo", label: "Logo" }].map(({ asset, label }) => <Col lg="6" key={asset}>
                                  <FormGroup>
                                    <label className="form-control-label" htmlFor={`x916-image-${asset}`} style={labelStyle}>{label}</label>
                                    <Input type="file" id={`x916-image-${asset}`} accept=".jpg,.jpeg,image/jpeg"
                                      innerRef={(input) => { akuvoxX916ImageInputs.current[asset] = input; }}
                                      disabled={isBusy || akuvoxX916DisplayLoading}
                                      onChange={(event) => handleAkuvoxX916ImageSelect(asset, event)} />
                                    {akuvoxX916Images[asset]?.preview && <div className="mt-2 p-2" style={{ background: isDark ? "#1f2937" : "#f3f4f6", borderRadius: "6px", textAlign: "center" }}>
                                      <img src={akuvoxX916Images[asset].preview} alt={`Selected ${label.toLowerCase()} preview`} style={{ maxWidth: "100%", maxHeight: "160px", objectFit: "contain" }} />
                                      <small className="d-block mt-1" style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>Selected file preview</small>
                                    </div>}
                                    <div className="d-flex mt-2" style={{ gap: "8px" }}>
                                      <Button type="button" size="sm" color="primary"
                                        disabled={isBusy || akuvoxX916DisplayLoading || !akuvoxX916Images[asset]?.file}
                                        onClick={() => submitAkuvoxX916Image(asset)}>
                                        {akuvoxX916ImageBusy === `${asset}:upload` && <Spinner size="sm" className="mr-1" />}Upload
                                      </Button>
                                      <Button type="button" size="sm" color="secondary" outline
                                        disabled={isBusy || akuvoxX916DisplayLoading}
                                        onClick={() => submitAkuvoxX916Image(asset, true)}>
                                        {akuvoxX916ImageBusy === `${asset}:reset` && <Spinner size="sm" className="mr-1" />}Reset
                                      </Button>
                                    </div>
                                  </FormGroup>
                                </Col>)}
                              </Row>
                              <Row>
                                {AKUVOX_X916_HOMEPAGE_TEXT_FIELDS.map(({ field, label }) => <Col lg="6" key={field}><FormGroup>
                                  <label className="form-control-label" htmlFor={`x916-homepage-${field}`} style={labelStyle}>{label}</label>
                                  <Input id={`x916-homepage-${field}`} type="text" maxLength="128"
                                    value={akuvoxX916DisplaySettings[field] ?? ""}
                                    placeholder={akuvoxX916DisplaySettings[field] == null ? "Not returned by device" : ""}
                                    disabled={isBusy || akuvoxX916DisplayLoading || akuvoxX916DisplaySettings[field] == null}
                                    onChange={(e) => updateAkuvoxX916Field(field, e.target.value)} />
                                </FormGroup></Col>)}
                              </Row>

                            </>}
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}

                  {editing && supportsCurrentDeviceApi && (
                    <div className="pl-lg-4">
                      <Card
                        className="shadow-sm"
                        style={{
                          border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                          boxShadow: "none",
                        }}
                      >
                        <CardHeader
                          onClick={() => setActivityOpen((prev) => !prev)}
                          style={{
                            ...collapsibleHeaderStyle,
                            borderBottom: activityOpen
                              ? isDark ? "1px solid #374151" : "1px solid #dcdfe3"
                              : "none",
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <h4
                                className="mb-1"
                                style={{
                                  color: isDark ? "#f8f9fa" : "#111",
                                  fontWeight: 600,
                                  fontSize: "1.35rem",
                                }}
                              >
                                {isAkuvoxS539 ? "Call Logs & Access Events" : "Call Logs & Events"}
                              </h4>
                              <div
                                style={{
                                  color: isDark ? "#9ca3af" : "#6c757d",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {isAkuvoxS539
                                  ? "Call records and door-access history from the intercom"
                                  : "Call records and event history from the intercom"}
                              </div>
                            </div>

                            {renderCollapseIcon(activityOpen)}
                          </div>
                        </CardHeader>

                        {activityOpen && (
                          <CardBody style={{ backgroundColor: isDark ? "#111827" : "#ffffff" }}>

                            <Row>
                              <Col lg="12" className="mb-4">
                                <Card
                                  className="shadow-sm"
                                  style={{
                                    border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardHeader
                                    onClick={() => setCallLogsOpen((prev) => !prev)}
                                    style={{
                                      backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                      borderBottom: callLogsOpen
                                        ? (isDark ? "1px solid #374151" : "1px solid #dcdfe3")
                                        : "none",
                                      paddingTop: "1rem",
                                      paddingBottom: "1rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <h4
                                          className="mb-1"
                                          style={{
                                            color: isDark ? "#f8f9fa" : "#111",
                                            fontWeight: 500,
                                            fontSize: "1.35rem",
                                            letterSpacing: "0.01em",
                                          }}
                                        >
                                          Call Logs
                                        </h4>
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            fontSize: "0.95rem",
                                          }}
                                        >
                                          Call records from the intercom
                                        </div>
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#d1d5db" : "#495057",
                                          fontSize: "1.2rem",
                                          fontWeight: 700,
                                          userSelect: "none",
                                        }}
                                      >
                                        {callLogsOpen ? "−" : "+"}
                                      </div>
                                    </div>
                                  </CardHeader>

                                  {callLogsOpen && (
                                    <CardBody
                                      style={{
                                        backgroundColor: isDark ? "#111827" : "#ffffff",
                                        paddingTop: "1rem",
                                      }}
                                    >
                                      <div
                                        className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                                        style={{ gap: "0.75rem" }}
                                      >
                                        <div style={{ minWidth: 180 }}>
                                          <Input
                                            type="select"
                                            value={callLogsRange}
                                            onChange={(e) => setCallLogsRange(e.target.value)}
                                            style={{
                                              backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                              color: isDark ? "#f8f9fa" : "#111",
                                              borderColor: isDark ? "#3a3a3a" : "#ced4da",
                                              fontSize: "0.95rem",
                                              cursor: "pointer",
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <option value="1D">Last 1 Day</option>
                                            <option value="3D">Last 3 Days</option>
                                            <option value="7D">Last 7 Days</option>
                                            <option value="ALL">All</option>
                                          </Input>
                                        </div>

                                        <Button
                                          color="primary"
                                          size="sm"
                                          onClick={loadCallLogs}
                                          disabled={!device_uuid || callLogsLoading}
                                          style={{
                                            minWidth: "140px",
                                            fontWeight: 600,
                                            cursor: "pointer",
                                          }}
                                        >
                                          {callLogsLoading ? (
                                            <>
                                              <Spinner size="sm" className="mr-2" />
                                              Loading...
                                            </>
                                          ) : (
                                            <>
                                              <span className="btn-inner--icon mr-1">
                                                <i className="fas fa-sync" />
                                              </span>
                                              <span className="btn-inner--text">Refresh</span>
                                            </>
                                          )}
                                        </Button>
                                      </div>

                                      {callLogsError && (
                                        <Alert color="danger">{callLogsError}</Alert>
                                      )}

                                      {!callLogsLoading && filteredCallLogs.length === 0 && !callLogsError && (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            padding: "0.5rem 0",
                                          }}
                                        >
                                          No call logs found for the selected filters.
                                        </div>
                                      )}

                                      {filteredCallLogs.length > 0 && (
                                        <div className="table-responsive">
                                          <Table responsive className="mb-0" style={{ backgroundColor: "transparent" }}>
                                            <thead>
                                              <tr
                                                style={{
                                                  borderBottom: isDark ? "1px solid #374151" : "1px solid #bfc5cc",
                                                }}
                                              >
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "42%",
                                                  }}
                                                >
                                                  Name
                                                </th>
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "26%",
                                                  }}
                                                >
                                                  Date and Time
                                                </th>
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "18%",
                                                  }}
                                                >
                                                  Type
                                                </th>
                                                <th
                                                  style={{
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.95rem",
                                                    fontWeight: 700,
                                                    paddingLeft: "1rem",
                                                    paddingRight: "1rem",
                                                    borderTop: "none",
                                                    width: "14%",
                                                  }}
                                                >
                                                  Call Duration
                                                </th>
                                              </tr>
                                            </thead>

                                            <tbody>
                                              {filteredCallLogs.map((log, idx) => {
                                                const formatted = formatCallLogDate(log);
                                                const displayName = formatCallLogName(log);
                                                const displayType = getCallTypeDisplay(log.call_type);
                                                const displayDuration = formatCallLogDuration(log.duration);

                                                return (
                                                  <tr
                                                    key={`${log.id || "log"}-${idx}`}
                                                    style={{
                                                      borderBottom: isDark ? "1px solid #374151" : "1px solid #e6e9ed",
                                                    }}
                                                  >
                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        color: isDark ? "#f8f9fa" : "#111",
                                                        borderTop: "none",
                                                      }}
                                                    >
                                                      <div className="d-flex align-items-center" style={{ gap: "0.85rem" }}>
                                                        <div
                                                          style={{
                                                            fontSize: "1.6rem",
                                                            lineHeight: 1,
                                                            color:
                                                              String(log.call_type || "").toLowerCase() === "outgoing"
                                                                ? "#66bb33"
                                                                : String(log.call_type || "").toLowerCase() === "incoming"
                                                                  ? "#2d9cdb"
                                                                  : "#f59e0b",
                                                            minWidth: "22px",
                                                          }}
                                                        >
                                                          {String(log.call_type || "").toLowerCase() === "outgoing"
                                                            ? "↗"
                                                            : String(log.call_type || "").toLowerCase() === "incoming"
                                                              ? "↙"
                                                              : "•"}
                                                        </div>

                                                        <div>
                                                          <div
                                                            style={{
                                                              fontSize: "1.05rem",
                                                              fontWeight: 500,
                                                              color: isDark ? "#f8f9fa" : "#111",
                                                            }}
                                                          >
                                                            {displayName}
                                                          </div>

                                                          {log.remote_party && log.remote_party !== displayName && (
                                                            <div
                                                              style={{
                                                                fontSize: "0.86rem",
                                                                color: isDark ? "#9ca3af" : "#6c757d",
                                                                marginTop: "0.1rem",
                                                              }}
                                                            >
                                                              {String(log.remote_party).replace(/^sip:/i, "")}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </td>

                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        borderTop: "none",
                                                      }}
                                                    >
                                                      <div
                                                        style={{
                                                          fontSize: "1rem",
                                                          color: isDark ? "#f8f9fa" : "#111",
                                                          fontWeight: 500,
                                                          lineHeight: 1.25,
                                                        }}
                                                      >
                                                        {formatted.date}
                                                      </div>
                                                      {formatted.time && (
                                                        <div
                                                          style={{
                                                            fontSize: "0.96rem",
                                                            color: isDark ? "#d1d5db" : "#343a40",
                                                            marginTop: "0.15rem",
                                                            lineHeight: 1.2,
                                                          }}
                                                        >
                                                          {formatted.time}
                                                        </div>
                                                      )}
                                                    </td>

                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        borderTop: "none",
                                                      }}
                                                    >
                                                      <span
                                                        style={{
                                                          fontSize: "0.95rem",
                                                          color: isDark ? "#e5e7eb" : "#212529",
                                                        }}
                                                      >
                                                        {displayType}
                                                      </span>
                                                    </td>

                                                    <td
                                                      style={{
                                                        padding: "0.95rem 1rem",
                                                        verticalAlign: "middle",
                                                        borderTop: "none",
                                                        fontSize: "1rem",
                                                        color: isDark ? "#f8f9fa" : "#111",
                                                        whiteSpace: "nowrap",
                                                      }}
                                                    >
                                                      {displayDuration}
                                                    </td>
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </Table>
                                        </div>
                                      )}
                                    </CardBody>
                                  )}
                                </Card>
                              </Col>

                              <Col lg="12">
                                <Card
                                  className="shadow-sm"
                                  style={{
                                    border: isDark ? "1px solid #3a3a3a" : "1px solid #dcdfe3",
                                    boxShadow: "none",
                                  }}
                                >
                                  <CardHeader
                                    onClick={() => setLiveEventsOpen((prev) => !prev)}
                                    style={{
                                      backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                      borderBottom: liveEventsOpen
                                        ? (isDark ? "1px solid #374151" : "1px solid #dcdfe3")
                                        : "none",
                                      paddingTop: "1rem",
                                      paddingBottom: "1rem",
                                      cursor: "pointer",
                                    }}
                                  >
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <h4
                                          className="mb-1"
                                          style={{
                                            color: isDark ? "#f8f9fa" : "#111",
                                            fontWeight: 500,
                                            fontSize: "1.35rem",
                                          }}
                                        >
                                          {isAkuvoxS539 ? "Access Events" : "Events"}
                                        </h4>
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            fontSize: "0.95rem",
                                          }}
                                        >
                                          {isAkuvoxS539
                                            ? "Successful and rejected door-access attempts"
                                            : "Event history from the intercom"}
                                        </div>
                                      </div>

                                      <div
                                        style={{
                                          color: isDark ? "#d1d5db" : "#495057",
                                          fontSize: "1.2rem",
                                          fontWeight: 700,
                                          userSelect: "none",
                                        }}
                                      >
                                        {liveEventsOpen ? "−" : "+"}
                                      </div>
                                    </div>
                                  </CardHeader>

                                  {liveEventsOpen && (
                                    <CardBody
                                      style={{
                                        backgroundColor: isDark ? "#111827" : "#ffffff",
                                        paddingTop: "1rem",
                                      }}
                                    >
                                      <div
                                        className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                                        style={{ gap: "0.75rem" }}
                                      >
                                        <div
                                          className="d-flex align-items-center"
                                          style={{ gap: "0.75rem", flexWrap: "wrap" }}
                                        >
                                          <div style={{ minWidth: 240 }}>
                                            <Input
                                              type="select"
                                              value={eventFilter}
                                              onChange={(e) => setEventFilter(e.target.value)}
                                              style={{
                                                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                                color: isDark ? "#f8f9fa" : "#111",
                                                borderColor: isDark ? "#3a3a3a" : "#ced4da",
                                                fontSize: "0.95rem",
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="ALL">
                                                {isAkuvoxS539 ? "[Filter Access Events]" : "[Filter Events]"}
                                              </option>
                                              {availableEventTypes.map((type) => (
                                                <option key={type} value={type}>
                                                  {formatEventType({
                                                    event: type,
                                                    source: isAkuvoxS539 ? "akuvox_doorlog" : undefined,
                                                  })}
                                                </option>
                                              ))}
                                            </Input>
                                          </div>

                                          <div style={{ minWidth: 180 }}>
                                            <Input
                                              type="select"
                                              value={eventsRange}
                                              onChange={(e) => setEventsRange(e.target.value)}
                                              style={{
                                                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                                                color: isDark ? "#f8f9fa" : "#111",
                                                borderColor: isDark ? "#3a3a3a" : "#ced4da",
                                                fontSize: "0.95rem",
                                                cursor: "pointer",
                                              }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <option value="1D">Last 1 Day</option>
                                              <option value="3D">Last 3 Days</option>
                                              <option value="7D">Last 7 Days</option>
                                              <option value="ALL">All</option>
                                            </Input>
                                          </div>
                                        </div>

                                        <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
                                          <Button
                                            color="primary"
                                            size="sm"
                                            onClick={loadEvents}
                                            disabled={eventsLoading}
                                            style={{ minWidth: "120px" }}
                                          >
                                            {eventsLoading ? (
                                              <>
                                                <Spinner size="sm" className="mr-2" />
                                                Loading...
                                              </>
                                            ) : (
                                              <>
                                                <span className="btn-inner--icon mr-1">
                                                  <i className="fas fa-sync" />
                                                </span>
                                                <span className="btn-inner--text">Refresh</span>
                                              </>
                                            )}
                                          </Button>
                                        </div>
                                      </div>

                                      {eventsError && (
                                        <Alert color="danger">{eventsError}</Alert>
                                      )}

                                      {eventsLoading && filteredEvents.length === 0 && !eventsError && (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            padding: "0.5rem 0",
                                          }}
                                        >
                                          Loading events...
                                        </div>
                                      )}

                                      {!eventsLoading && filteredEvents.length === 0 && !eventsError && (
                                        <div
                                          style={{
                                            color: isDark ? "#9ca3af" : "#6c757d",
                                            padding: "0.5rem 0",
                                          }}
                                        >
                                          No events found for the selected filters.
                                        </div>
                                      )}

                                      {filteredEvents.length > 0 && (
                                        <div
                                          style={{
                                            border: isDark ? "1px solid #374151" : "1px solid #dcdfe3",
                                            maxHeight: "500px",
                                            overflowY: "auto",
                                            overflowX: "auto",
                                          }}
                                        >
                                          <Table
                                            responsive
                                            className="mb-0"
                                            style={{
                                              backgroundColor: isDark ? "#111827" : "#ffffff",
                                              minWidth: "900px",
                                            }}
                                          >
                                            <thead
                                              style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2,
                                                backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                              }}
                                            >
                                              <tr
                                                style={{
                                                  borderBottom: isDark ? "1px solid #374151" : "1px solid #cfd4da",
                                                }}
                                              >
                                                <th
                                                  style={{
                                                    width: "140px",
                                                    minWidth: "140px",
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 700,
                                                    borderTop: "none",
                                                    padding: "0.75rem 1rem",
                                                    backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                                  }}
                                                >
                                                  TIME
                                                </th>
                                                <th
                                                  style={{
                                                    width: "220px",
                                                    minWidth: "220px",
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 700,
                                                    borderTop: "none",
                                                    padding: "0.75rem 1rem",
                                                    backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                                  }}
                                                >
                                                  EVENT TYPE
                                                </th>
                                                <th
                                                  style={{
                                                    minWidth: "520px",
                                                    color: isDark ? "#f8f9fa" : "#111",
                                                    fontSize: "0.85rem",
                                                    fontWeight: 700,
                                                    borderTop: "none",
                                                    padding: "0.75rem 1rem",
                                                    backgroundColor: isDark ? "#1f2937" : "#f5f5f5",
                                                  }}
                                                >
                                                  DESCRIPTION
                                                </th>
                                              </tr>
                                            </thead>

                                            <tbody>
                                              {filteredEvents.map((evt, idx) => (
                                                <tr
                                                  key={`${evt.id || "evt"}-${idx}`}
                                                  style={{
                                                    borderBottom: isDark ? "1px solid #2f3b4d" : "1px solid #eceff3",
                                                  }}
                                                >
                                                  <td
                                                    style={{
                                                      borderTop: "none",
                                                      padding: "0.45rem 1rem",
                                                      verticalAlign: "top",
                                                      color: isDark ? "#e5e7eb" : "#212529",
                                                      fontSize: "0.95rem",
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {formatEventTime(evt)}
                                                  </td>

                                                  <td
                                                    style={{
                                                      borderTop: "none",
                                                      padding: "0.45rem 1rem",
                                                      verticalAlign: "top",
                                                      color: isDark ? "#f8f9fa" : "#111",
                                                      fontSize: "0.98rem",
                                                      fontWeight: 700,
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {formatEventType(evt)}
                                                  </td>

                                                  <td
                                                    style={{
                                                      borderTop: "none",
                                                      padding: "0.45rem 1rem",
                                                      verticalAlign: "top",
                                                      color: isDark ? "#f3f4f6" : "#111",
                                                      fontSize: "0.96rem",
                                                      lineHeight: 1.35,
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {formatEventDescription(evt)}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        </div>
                                      )}
                                    </CardBody>
                                  )}
                                </Card>
                              </Col>
                            </Row>
                          </CardBody>
                        )}
                      </Card>
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row >
      </Container >
      {isBusy && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: isDark ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.7)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            style={{
              padding: "1.5rem 2rem",
              borderRadius: "1rem",
              backgroundColor: isDark ? "#2a2a2a" : "#ffffff",
              color: isDark ? "#f8f9fa" : "#111827",
              border: isDark ? "1px solid #3a3a3a" : "1px solid #dee2e6",
              textAlign: "center",
              minWidth: 300,
            }}
          >
            <div className="spinner-border" role="status" />

            <div className="mt-3" style={{ fontWeight: 600 }}>
              {saving
                ? "Saving device…"
                : backgroundUploading
                  ? "Uploading background…"
                  : backgroundRemoving
                    ? "Removing background..."
                    : "Applying display settings…"}
            </div>

            {(backgroundUploading || backgroundRemoving || quickSetupBusy) ? (
              <>
                <div style={{ opacity: 0.8, fontSize: "0.9rem", marginTop: "4px" }}>
                  This may take up to <strong>15 seconds</strong>.
                </div>
                <div style={{ opacity: 0.7, fontSize: "0.85rem" }}>
                  Please do not close or refresh this page.
                </div>
              </>
            ) : (
              <div style={{ opacity: 0.7, fontSize: "0.85rem", marginTop: "4px" }}>
                Please wait...
              </div>
            )}
          </Card>
        </div>
      )
      }
      {
        imageEditorOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 3000,
              backgroundColor: "rgba(0,0,0,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "1rem",
            }}
          >
            <div
              style={{
                width: "100%",
                maxWidth: "850px",
                backgroundColor: isDark ? "#1f2937" : "#fff",
                color: isDark ? "#f8f9fa" : "#111",
                borderRadius: "0.75rem",
                overflow: "hidden",
                boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
              }}
            >
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  padding: "1rem 1.25rem",
                  borderBottom: isDark ? "1px solid #374151" : "1px solid #dee2e6",
                }}
              >
                <h4 className="mb-0" style={{ color: isDark ? "#f8f9fa" : "#111" }}>
                  Photo Editor
                </h4>

                <button
                  type="button"
                  onClick={closeDirectoryImageEditor}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: isDark ? "#f8f9fa" : "#111",
                    fontSize: "1.75rem",
                    lineHeight: 1,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: "1.25rem" }}>
                {imageEditorError && (
                  <Alert color="danger" className="mb-3">
                    {imageEditorError}
                  </Alert>
                )}

                <div
                  ref={imageEditorRef}
                  style={{
                    width: "100%",
                    height: "420px",
                    borderRadius: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundImage:
                      "linear-gradient(45deg, #bbb 25%, transparent 25%), linear-gradient(-45deg, #bbb 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #bbb 75%), linear-gradient(-45deg, transparent 75%, #bbb 75%)",
                    backgroundColor: "#eee",
                    backgroundSize: "20px 20px",
                    backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                  }}
                >
                  <div
                    ref={imageEditorRef}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setIsDraggingImage(true);
                      setDragStart({
                        x: e.clientX,
                        y: e.clientY,
                        panX,
                        panY,
                      });
                    }}
                    onMouseMove={(e) => {
                      if (!isDraggingImage) return;

                      setPanX(dragStart.panX + (e.clientX - dragStart.x));
                      setPanY(dragStart.panY + (e.clientY - dragStart.y));
                    }}
                    onMouseUp={() => setIsDraggingImage(false)}
                    onMouseLeave={() => setIsDraggingImage(false)}
                    style={{
                      width: "320px",
                      height: "320px",
                      borderRadius: "50%",
                      overflow: "hidden",
                      position: "relative",
                      backgroundColor: "transparent",
                      border: "3px solid rgba(255,255,255,0.85)",
                      cursor: isDraggingImage ? "grabbing" : "grab",
                    }}
                  >
                    {imageEditorSrc && (
                      <img
                        src={imageEditorSrc}
                        alt="Directory preview"
                        draggable={false}
                        style={{
                          position: "absolute",
                          left: "50%",
                          top: "50%",
                          maxWidth: "none",
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          transform: `translate(calc(-50% + ${panX}px), calc(-50% + ${panY}px)) scale(${zoom})`,
                          transformOrigin: "center",
                          userSelect: "none",
                          pointerEvents: "none",
                        }}
                      />
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <label style={{ fontWeight: 600 }}>Zoom</label>

                  <Input
                    type="range"
                    min="0.1"
                    max="3"
                    step="0.05"
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    style={{ cursor: "pointer" }}
                  />
                </div>

                <small style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                  Move and zoom the image so it fits inside the circle. The final image will be compressed before saving.
                </small>
              </div>

              <div
                className="d-flex justify-content-end"
                style={{
                  gap: "0.5rem",
                  padding: "1rem 1.25rem",
                  borderTop: isDark ? "1px solid #374151" : "1px solid #dee2e6",
                }}
              >
                <Button
                  color="secondary"
                  type="button"
                  onClick={() => {
                    document.getElementById(`dir-image-${imageEditorDirUuid}`)?.click();
                  }}
                >
                  Choose Different Image
                </Button>

                <Button color="light" type="button" onClick={closeDirectoryImageEditor}>
                  Cancel
                </Button>

                <Button
                  color="primary"
                  type="button"
                  onClick={saveEditedDirectoryImage}
                  disabled={imageEditorResultTooLarge || imageEditorResultBytes > MAX_DIRECTORY_IMAGE_BYTES}
                >
                  OK
                </Button>
              </div>
            </div>
          </div>
        )
      }

      <Modal
        isOpen={tenantPickerOpen}
        toggle={closeTenantPicker}
        size="xl"
        centered
      >
        <ModalHeader
          toggle={closeTenantPicker}
          style={{
            backgroundColor: isDark ? "#1f2937" : "#ffffff",
            color: isDark ? "#f8f9fa" : "#111",
            borderBottom: isDark ? "1px solid #374151" : "1px solid #dee2e6",
          }}
        >
          Add User
          {tenantPickerButtonNumber ? ` - Button ${tenantPickerButtonNumber}` : ""}
        </ModalHeader>

        <ModalBody
          style={{
            backgroundColor: isDark ? "#111827" : "#ffffff",
            color: isDark ? "#f8f9fa" : "#111",
          }}
        >
          <div className="d-flex justify-content-end align-items-center mb-3">
            <label
              className="mb-0 mr-2"
              htmlFor="tenant-picker-search"
              style={{
                color: isDark ? "#d1d5db" : "#111",
                fontSize: "0.9rem",
              }}
            >
              Search
            </label>

            <Input
              id="tenant-picker-search"
              type="text"
              value={tenantPickerSearch}
              onChange={(e) => {
                setTenantPickerSearch(e.target.value);
                setTenantPickerPage(1);
              }}
              style={{
                width: "260px",
                backgroundColor: isDark ? "#2a2a2a" : "#fff",
                color: isDark ? "#f8f9fa" : "#111",
                borderColor: isDark ? "#3a3a3a" : undefined,
              }}
            />
          </div>

          <div style={{ overflowX: "auto" }}>
            <Table
              hover
              responsive
              className="mb-0"
              style={{
                color: isDark ? "#f8f9fa" : "#111",
                backgroundColor: isDark ? "#111827" : "#ffffff",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: "56px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      paddingLeft: "0.75rem",
                      paddingRight: "0.75rem",
                    }}
                  />
                  <th>Name</th>
                  <th>Virtual Number</th>
                  <th>Phone 1</th>
                </tr>
              </thead>

              <tbody>
                {tenantPickerPageTenants.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center py-4">
                      No tenants found.
                    </td>
                  </tr>
                ) : (
                  tenantPickerPageTenants.map((tenant) => {
                    const checked = isTenantPickerSelected(tenant.tenant_uuid);

                    return (
                      <tr
                        key={`tenant-picker-${tenant.tenant_uuid}`}
                        onClick={() => toggleTenantPickerTenant(tenant.tenant_uuid)}
                        style={{
                          cursor: "pointer",
                          backgroundColor: checked
                            ? isDark ? "#1f2937" : "#eef4ff"
                            : "transparent",
                        }}
                      >
                        <td
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "56px",
                            textAlign: "center",
                            verticalAlign: "middle",
                            paddingLeft: "0.75rem",
                            paddingRight: "0.75rem",
                          }}
                        >
                          <Input
                            type="checkbox"
                            checked={checked}
                            disabled={isBusy}
                            onChange={() => toggleTenantPickerTenant(tenant.tenant_uuid)}
                            style={{
                              position: "static",
                              margin: 0,
                            }}
                          />
                        </td>
                        <td>{getTenantName(tenant)}</td>
                        <td>{getTenantVirtualNumber(tenant) || "—"}</td>
                        <td>{getTenantPhone1(tenant) || "—"}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <div style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
              {tenantPickerFilteredTenants.length === 0
                ? "0 tenants"
                : `${(tenantPickerSafePage - 1) * TENANT_PICKER_PAGE_SIZE + 1} - ${Math.min(
                  tenantPickerSafePage * TENANT_PICKER_PAGE_SIZE,
                  tenantPickerFilteredTenants.length
                )} of ${tenantPickerFilteredTenants.length}`}
            </div>

            <div className="d-flex align-items-center" style={{ gap: "0.5rem" }}>
              <Button
                size="sm"
                color="secondary"
                type="button"
                disabled={tenantPickerSafePage <= 1}
                style={
                  tenantPickerSafePage <= 1
                    ? tenantPickerSecondaryButtonDisabledStyle
                    : tenantPickerSecondaryButtonStyle
                }
                onClick={() =>
                  setTenantPickerPage((prev) => Math.max(1, prev - 1))
                }
              >
                Prev
              </Button>

              <span style={{ color: isDark ? "#d1d5db" : "#495057" }}>
                Page {tenantPickerSafePage} of {tenantPickerTotalPages}
              </span>

              <Button
                size="sm"
                color="secondary"
                type="button"
                disabled={tenantPickerSafePage >= tenantPickerTotalPages}
                style={
                  tenantPickerSafePage >= tenantPickerTotalPages
                    ? tenantPickerSecondaryButtonDisabledStyle
                    : tenantPickerSecondaryButtonStyle
                }
                onClick={() =>
                  setTenantPickerPage((prev) =>
                    Math.min(tenantPickerTotalPages, prev + 1)
                  )
                }
              >
                Next
              </Button>
            </div>
          </div>
        </ModalBody>

        <ModalFooter
          style={{
            backgroundColor: isDark ? "#111827" : "#ffffff",
            borderTop: isDark ? "1px solid #374151" : "1px solid #dee2e6",
          }}
        >
          <div
            className="mr-auto"
            style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
          >
            {tenantPickerSelected.length} selected
          </div>

          <Button
            color="primary"
            type="button"
            disabled={isBusy}
            onClick={saveTenantPickerSelection}
          >
            Add
          </Button>

          <Button
            color="secondary"
            type="button"
            style={tenantPickerSecondaryButtonStyle}
            onClick={closeTenantPicker}
          >
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default DeviceForm;
